/**
 * Workshop Booking (Liquid-first + minimal JS)
 *
 * - Liquid renders the BASE POOL (product match + status active/sold_out)
 * - JS only does UI refresh: State -> City -> Variant filtering on already-rendered cards
 * - Keeps event_id deep-link in URL while preserving other query params
 *
 * Add-to-cart: JSON (items array). Cart Transform: _workshop, _resolved_price*.
 *
 * Diagnostics: window.__WB_DIAG__=true for [WB_DIAG] and [WB_PERF] logs.
 * WB_PERF format: [WB_PERF] cycle=<id> req=<id> step=<step> ms=<int> extra=<json>. Correlate with backend via extra.rid when data.meta.debug.rid is present.
 */

/**
 * window.__WB_INITIAL_EVENT_ID__ is set by a Liquid <script> snippet in the product template
 * BEFORE theme.js loads. Theme's _updateHistoryState strips event_id from the URL via
 * history.replaceState during variant init — by the time our script runs, it's already gone.
 * The Liquid snippet reads it server-side and passes it here as a global.
 * Fallback: try URL now in case Liquid snippet is missing.
 */
if (!window.__WB_INITIAL_EVENT_ID__) {
  try {
    var _wbEid = new URLSearchParams(window.location.search).get('event_id');
    if (_wbEid) {
      window.__WB_INITIAL_EVENT_ID__ = _wbEid.trim();
      if (window.__WB_DIAG__) console.log('[WB_RESTORE] event_id from URL fallback:', window.__WB_INITIAL_EVENT_ID__);
    }
  } catch (e) {}
}
if (!window.__WB_INITIAL_VARIANT__) {
  try {
    var _wbVar = new URLSearchParams(window.location.search).get('variant');
    if (_wbVar) {
      window.__WB_INITIAL_VARIANT__ = _wbVar.trim();
    }
  } catch (e) {}
}

/** Numeric id for matching; accepts gid://shopify/ProductVariant/123 or plain digits. */
function normalizeVariantIdForMatch(val) {
  if (val == null) return '';
  var s = String(val).trim();
  if (s === '') return '';
  var gid = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/i.exec(s);
  if (gid) return gid[1];
  if (/^\d+$/.test(s)) return s;
  return s;
}

/** Canonical variant param for URL bar (Shopify numeric id). */
function formatVariantIdForUrl(val) {
  var numeric = normalizeVariantIdForMatch(val);
  if (!numeric || !/^\d+$/.test(numeric)) return '';
  return numeric;
}

window.__wbNormalizeVariantId = normalizeVariantIdForMatch;
window.__wbFormatVariantIdForUrl = formatVariantIdForUrl;

/** Waitlist POST (adapter). Point to production when CORS/backend allow. */
var __WB_WAITLIST_API_URL__ =
  typeof window.__WB_WAITLIST_API_URL__ === 'string' && window.__WB_WAITLIST_API_URL__.trim()
    ? window.__WB_WAITLIST_API_URL__.trim()
    : 'https://adapter.aogen.com.au/api/public/waitlist';

window.__WB_URL_EVENT_ID_NUMERIC__ = window.__WB_URL_EVENT_ID_NUMERIC__ || '';

function wbDiag(label, payload) {
  if (!window.__WB_DIAG__) return;
  console.debug('[WB_DIAG]', label, payload);
}

/** Event status from API or DOM dataset (active | sold_out | draft | completed). */
function wbNormalizeEventStatus(status) {
  if (status == null || status === '') return '';
  return String(status).toLowerCase().replace(/\s/g, '_').replace(/-/g, '_');
}

function wbEventCapacityRemaining(evOrCard) {
  if (!evOrCard) return null;
  var raw;
  if (evOrCard.dataset && evOrCard.dataset.capacityRemaining != null && evOrCard.dataset.capacityRemaining !== '') {
    raw = evOrCard.dataset.capacityRemaining;
  } else if (evOrCard.capacity_remaining != null && evOrCard.capacity_remaining !== '') {
    raw = evOrCard.capacity_remaining;
  } else if (evOrCard._wbEv) {
    return wbEventCapacityRemaining(evOrCard._wbEv);
  } else {
    return null;
  }
  var n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function wbGetEventStatus(evOrCard) {
  if (!evOrCard) return '';
  if (evOrCard.dataset && evOrCard.dataset.status != null && evOrCard.dataset.status !== '') {
    return wbNormalizeEventStatus(evOrCard.dataset.status);
  }
  return wbNormalizeEventStatus(evOrCard.status);
}

function wbExtractEventStatusRaw(ev) {
  if (!ev || typeof ev !== 'object') return '';
  var candidates = [ev.status, ev.event_status, ev.booking_status];
  if (ev.metaobject && ev.metaobject.status != null) candidates.push(ev.metaobject.status);
  for (var i = 0; i < candidates.length; i++) {
    var c = candidates[i];
    if (c == null || c === '') continue;
    if (typeof c === 'object' && c.value != null) c = c.value;
    var n = wbNormalizeEventStatus(c);
    if (n) return n;
  }
  return '';
}

function wbEventLookupKeys(ev) {
  if (!ev || typeof ev !== 'object') return [];
  var keys = [];
  ['id', 'event_metaobject_gid', 'event_id', 'gid', 'handle', 'system_id'].forEach(function (k) {
    var v = ev[k];
    if (v == null || v === '') return;
    var s = String(v).trim();
    if (!s) return;
    keys.push(s);
    var num = s.replace(/^gid:\/\/shopify\/Metaobject\//i, '').replace(/\D/g, '');
    if (num && num !== s) keys.push(num);
  });
  return keys;
}

function wbBuildEventStatusLookup(events) {
  var map = Object.create(null);
  (Array.isArray(events) ? events : []).forEach(function (ev) {
    var st = wbExtractEventStatusRaw(ev) || wbNormalizeEventStatus(ev.status);
    if (!st) return;
    wbEventLookupKeys(ev).forEach(function (k) {
      map[k] = st;
    });
  });
  return map;
}

function wbLookupEventStatus(ev) {
  var map = typeof window !== 'undefined' ? window.__WB_EVENT_STATUS_LOOKUP__ : null;
  if (!map || !ev) return '';
  var keys = wbEventLookupKeys(ev);
  for (var i = 0; i < keys.length; i++) {
    if (map[keys[i]]) return map[keys[i]];
  }
  return '';
}

/** Resolve status: API fields, then Liquid metaobject lookup (events-v often omits status). */
function wbResolveEventStatus(ev) {
  if (!ev || typeof ev !== 'object') return 'active';
  var fromApi = wbExtractEventStatusRaw(ev);
  var fromLiquid = wbLookupEventStatus(ev);
  if (fromLiquid === 'sold_out' || fromLiquid === 'draft' || fromLiquid === 'completed') return fromLiquid;
  if (fromApi === 'sold_out' || fromApi === 'draft' || fromApi === 'completed') return fromApi;
  if (fromApi === 'active') return 'active';
  if (fromLiquid) return fromLiquid;
  return 'active';
}

/** Sold out: status sold_out OR capacity_remaining <= 0 (shown in list; click → warning, no sale). */
function wbIsEventSoldOut(evOrCard) {
  if (!evOrCard) return false;
  if (wbGetEventStatus(evOrCard) === 'sold_out') return true;
  var cap = wbEventCapacityRemaining(evOrCard);
  return cap != null && cap <= 0;
}

var WB_SOLD_OUT_CAPACITY_MSG =
  'Sorry, this event has reached capacity. Please select other available options.';

function ensureSoldOutModalStyles() {
  var style = document.head.querySelector('#wb-sold-out-modal-styles');
  if (!style) {
    style = document.createElement('style');
    style.id = 'wb-sold-out-modal-styles';
    document.head.appendChild(style);
  }
  style.textContent =
    '@keyframes wb-sold-modal-in{from{opacity:0;transform:scale(0.96)}to{opacity:1;transform:scale(1)}}' +
    '.wb-sold-out-modal{position:fixed;inset:0;z-index:2147483020;display:none;align-items:center;justify-content:center;padding:clamp(16px,4vw,28px);box-sizing:border-box;-webkit-tap-highlight-color:transparent;}' +
    '.wb-sold-out-modal.is-open{display:flex;animation:wb-sold-modal-in 0.22s ease-out;}' +
    '.wb-sold-out-modal-backdrop{position:absolute;inset:0;background:rgba(15,18,28,0.55);cursor:pointer;-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);}' +
    '.wb-sold-out-modal-dialog{position:relative;z-index:1;width:min(100%,420px);max-width:420px;padding:clamp(1.5rem,4vw,2rem);background:rgb(var(--color-background,255 255 255));color:rgb(var(--color-foreground));border-radius:20px;box-shadow:0 24px 48px rgba(15,23,42,0.18);border:1px solid rgba(var(--color-foreground),0.08);box-sizing:border-box;}' +
    '.wb-sold-out-modal-msg{margin:0 2.5rem 1.25rem;text-align:center;font-size:1rem;line-height:1.55;}' +
    '.wb-sold-out-modal-btn.btn{display:block;width:100%;min-width:0;border-radius:999px;color:var(--colorBtnPrimaryText);background:var(--colorBtnPrimary);border-color:var(--colorBtnPrimary);}' +
    '.wb-sold-out-modal-btn.btn:hover,.wb-sold-out-modal-btn.btn:focus-visible{color:var(--colorBtnPrimaryText);background:var(--colorBtnPrimary);border-color:var(--colorBtnPrimary);}' +
    '.wb-sold-out-modal-x{position:absolute;top:10px;right:10px;width:40px;height:40px;border:0;border-radius:999px;background:rgba(var(--color-foreground),0.08);color:inherit;cursor:pointer;font-size:1.25rem;line-height:1;display:flex;align-items:center;justify-content:center;}';
}

function ensureSoldOutCapacityModal() {
  if (document.body._wbSoldOutModal) return document.body._wbSoldOutModal;
  ensureSoldOutModalStyles();
  var wrap = document.createElement('div');
  wrap.className = 'wb-sold-out-modal';
  wrap.setAttribute('hidden', '');
  wrap.innerHTML =
    '<div class="wb-sold-out-modal-backdrop" data-wb-sold-out-modal-dismiss></div>' +
    '<div class="wb-sold-out-modal-dialog" role="alertdialog" aria-modal="true" aria-labelledby="wb-sold-out-modal-title">' +
    '<button type="button" class="wb-sold-out-modal-x" data-wb-sold-out-modal-dismiss aria-label="Close">\u00d7</button>' +
    '<h3 id="wb-sold-out-modal-title" class="wb-sold-out-modal-msg" data-wb-sold-out-modal-msg></h3>' +
    '<button type="button" class="btn wb-sold-out-modal-btn" data-wb-sold-out-modal-dismiss>OK</button>' +
    '</div>';
  document.body.appendChild(wrap);
  document.body._wbSoldOutModal = wrap;

  function closeSoldOutModal() {
    wrap.classList.remove('is-open');
    wrap.setAttribute('hidden', '');
  }
  wrap.querySelectorAll('[data-wb-sold-out-modal-dismiss]').forEach(function (el) {
    el.addEventListener('click', closeSoldOutModal);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && wrap.classList.contains('is-open')) closeSoldOutModal();
  });
  return wrap;
}

function showSoldOutCapacityModal() {
  var modal = ensureSoldOutCapacityModal();
  var msgEl = modal.querySelector('[data-wb-sold-out-modal-msg]');
  if (msgEl) msgEl.textContent = WB_SOLD_OUT_CAPACITY_MSG;
  modal.classList.add('is-open');
  modal.removeAttribute('hidden');
  var btn = modal.querySelector('.wb-sold-out-modal-btn');
  if (btn) {
    try {
      btn.focus();
    } catch (_) {}
  }
}

window.wbShowSoldOutCapacityModal = showSoldOutCapacityModal;

/** Bookable: active, not draft/completed/sold_out, capacity >= 1 when known. */
function wbIsEventBookable(evOrCard) {
  if (!evOrCard) return false;
  if (wbShouldHideEvent(evOrCard)) return false;
  if (wbIsEventSoldOut(evOrCard)) return false;
  var st = wbGetEventStatus(evOrCard);
  if (st && st !== 'active') return false;
  var cap = wbEventCapacityRemaining(evOrCard);
  if (cap != null && cap < 1) return false;
  return true;
}

/** Hide from public lists (draft/completed). */
function wbShouldHideEvent(ev) {
  var st = wbGetEventStatus(ev);
  return st === 'draft' || st === 'completed';
}

window.wbEventBooking = {
  normalizeStatus: wbNormalizeEventStatus,
  resolveStatus: wbResolveEventStatus,
  capacityRemaining: wbEventCapacityRemaining,
  isSoldOut: wbIsEventSoldOut,
  isBookable: wbIsEventBookable,
  shouldHide: wbShouldHideEvent
};

/** WB_PERF: only when window.__WB_DIAG__ === true. Log format: [WB_PERF] cycle=<cycleId> req=<reqId> step=<step> ms=<int> extra=<json> */
function wbPerf(cycleId, reqId, step, ms, extra) {
  if (window.__WB_DIAG__ !== true) return;
  var extraStr = (extra != null && typeof extra === 'object') ? JSON.stringify(extra) : (extra != null ? JSON.stringify(String(extra)) : '{}');
  console.log('[WB_PERF] cycle=' + (cycleId != null ? cycleId : '') + ' req=' + (reqId != null ? reqId : '') + ' step=' + step + (ms != null && Number.isFinite(ms) ? ' ms=' + Math.round(ms) : '') + ' extra=' + extraStr);
}

/** In-flight dedupe: window-global so ALL bundles (workshop-booking, events-ui, widget containers) share one map. */
window.__WB_EVENTS_IN_FLIGHT = window.__WB_EVENTS_IN_FLIGHT || Object.create(null);

/**
 * Global fetch interceptor: dedupes ANY events-v GET from third-party bundles
 * (BookThatApp EventsListWidgetContainer / UpcomingEventWidgetContainer).
 * Key strips request_id and normalizes lat/lng to 5 decimals.
 */
if (!window.__WB_FETCH_PATCHED) {
  window.__WB_FETCH_PATCHED = true;
  var _origFetch = window.fetch;
  function _wbNormalizeEventsKey(raw) {
    var k = raw.replace(/([?&])request_id=[^&]*/g, '').replace(/[?&]$/, '');
    k = k.replace(/([?&]lat=)([^&]+)/g, function (m, pre, v) { return pre + (Math.round(parseFloat(v) * 1e5) / 1e5); });
    k = k.replace(/([?&]lng=)([^&]+)/g, function (m, pre, v) { return pre + (Math.round(parseFloat(v) * 1e5) / 1e5); });
    return k;
  }
  window.fetch = function (input, init) {
    var url = (typeof input === 'string') ? input : (input && input.url ? input.url : '');
    if (url && url.indexOf('/events-v') !== -1 && (!init || !init.method || init.method === 'GET')) {
      var key = _wbNormalizeEventsKey(url);
      if (window.__WB_EVENTS_IN_FLIGHT[key]) {
        if (window.WB_DEBUG_FETCH) console.log('[WB_FETCH_INTERCEPT]', key, 'deduped');
        return window.__WB_EVENTS_IN_FLIGHT[key].then(function (r) { return r.clone(); });
      }
      var p = _origFetch.apply(this, arguments);
      window.__WB_EVENTS_IN_FLIGHT[key] = p;
      p.finally(function () { delete window.__WB_EVENTS_IN_FLIGHT[key]; });
      if (window.WB_DEBUG_FETCH) console.log('[WB_FETCH_INTERCEPT]', key, 'new');
      return p;
    }
    return _origFetch.apply(this, arguments);
  };
}

var __WB_lastHandledPlaceFp = '';

async function getLatLngFromPlace(place) {
  if (!place) return null;
  var formattedAddress = (place.formattedAddress || place.formatted_address || '').toString();
  var geo = place.geometry && place.geometry.location;
  if (geo) {
    var lat = typeof geo.lat === 'function' ? geo.lat() : geo.lat;
    var lng = typeof geo.lng === 'function' ? geo.lng() : geo.lng;
    if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)))
      return { lat: Number(lat), lng: Number(lng), formattedAddress: formattedAddress };
  }
  var loc = place.location;
  if (loc) {
    var lat = loc.lat != null ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
    var lng = loc.lng != null ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
    if (lat == null && loc.latLng && typeof loc.latLng === 'function') {
      var ll = loc.latLng();
      if (ll) { lat = typeof ll.lat === 'function' ? ll.lat() : ll.lat; lng = typeof ll.lng === 'function' ? ll.lng() : ll.lng; }
    }
    if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)))
      return { lat: Number(lat), lng: Number(lng), formattedAddress: formattedAddress };
  }
  if (place.fetchFields && typeof place.fetchFields === 'function' && !place.__wbFieldsFetched) {
    try {
      await place.fetchFields({ fields: ['location'] });
      place.__wbFieldsFetched = true;
      formattedAddress = (place.formattedAddress || place.formatted_address || '').toString();
      geo = place.geometry && place.geometry.location;
      if (geo) {
        lat = typeof geo.lat === 'function' ? geo.lat() : geo.lat;
        lng = typeof geo.lng === 'function' ? geo.lng() : geo.lng;
        if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)))
          return { lat: Number(lat), lng: Number(lng), formattedAddress: formattedAddress };
      }
      loc = place.location;
      if (loc) {
        lat = loc.lat != null ? (typeof loc.lat === 'function' ? loc.lat() : loc.lat) : null;
        lng = loc.lng != null ? (typeof loc.lng === 'function' ? loc.lng() : loc.lng) : null;
        if (lat == null && loc.latLng && typeof loc.latLng === 'function') {
          ll = loc.latLng();
          if (ll) { lat = typeof ll.lat === 'function' ? ll.lat() : ll.lat; lng = typeof ll.lng === 'function' ? ll.lng() : ll.lng; }
        }
        if (lat != null && lng != null && Number.isFinite(Number(lat)) && Number.isFinite(Number(lng)))
          return { lat: Number(lat), lng: Number(lng), formattedAddress: formattedAddress };
      }
    } catch (_) {}
  }
  return null;
}

/**
 * Extract city, state, state_code, country from Place (B-only: no geometry used for fetch).
 * Supports legacy address_components and new API addressComponents (fetchFields).
 * In workshop-booking handlePlaceSelect, this is only a label fallback when predictionText / formattedAddress
 * comma parsing fails; events are always requested via fetchEventsByLatLng(lat, lng), never via parsed city/state.
 */
async function getCityStateFromPlace(place) {
  if (!place) return null;
  var city = '';
  var state = '';
  var stateCode = '';
  var country = 'US';
  var formattedAddress = (place.formattedAddress || place.formatted_address || '').toString();
  var ac = place.address_components || place.addressComponents;
  if (ac && Array.isArray(ac)) {
    ac.forEach(function (c) {
      var types = c.types || [];
      var longName = c.long_name || c.longText || '';
      var shortName = c.short_name || c.shortText || longName;
      if (types.indexOf('locality') !== -1) city = String(longName).trim();
      else if ((types.indexOf('postal_town') !== -1 || types.indexOf('sublocality') !== -1) && !city) city = String(longName).trim();
      if (types.indexOf('administrative_area_level_1') !== -1) {
        state = String(longName).trim();
        stateCode = String(shortName).trim();
      }
      if (types.indexOf('country') !== -1) country = String(shortName).trim();
    });
  }

  if (!ac || !Array.isArray(ac) || ac.length === 0) {
    if (place.fetchFields && typeof place.fetchFields === 'function' && !place.__wbFieldsFetched) {
      try {
        await place.fetchFields({ fields: ['location'] });
        place.__wbFieldsFetched = true;
        formattedAddress = (place.formattedAddress || place.formatted_address || '').toString();
        ac = place.address_components || place.addressComponents;
        if (ac && Array.isArray(ac)) {
          ac.forEach(function (c) {
            var types = c.types || [];
            var longName = c.long_name || c.longText || '';
            var shortName = c.short_name || c.shortText || longName;
            if (types.indexOf('locality') !== -1) city = String(longName).trim();
            else if ((types.indexOf('postal_town') !== -1 || types.indexOf('sublocality') !== -1) && !city) city = String(longName).trim();
            if (types.indexOf('administrative_area_level_1') !== -1) {
              state = String(longName).trim();
              stateCode = String(shortName).trim();
            }
            if (types.indexOf('country') !== -1) country = String(shortName).trim();
          });
        }
      } catch (_) {}
    }
  }

  if (!stateCode && state) stateCode = state;
  if ((!city || !stateCode) && formattedAddress) {
    var parts = formattedAddress.split(',').map(function (p) { return p.trim(); }).filter(Boolean);
    if (parts.length >= 2 && !city) city = parts[0];
    if (parts.length >= 2 && !state) state = parts[1];
    if (!stateCode && state) stateCode = state.length <= 3 ? state.toUpperCase() : (typeof normalizeStateCode === 'function' ? normalizeStateCode(state) : state.replace(/\s+/g, '-').slice(0, 10));
  }
  if (window.__WB_DIAG__) console.log('[WB_DIAG] getCityStateFromPlace', { city: city, state: state, state_code: stateCode, country: country });
  return { city: city, state: state, state_code: stateCode, country: country, formattedAddress: formattedAddress };
}

/** Common state/region long name -> short code so city_key matches backend (e.g. us-nsw-sydney). */
var STATE_NAME_TO_CODE = {
  'new south wales': 'NSW', 'nsw': 'NSW',
  'victoria': 'VIC', 'vic': 'VIC',
  'queensland': 'QLD', 'qld': 'QLD',
  'western australia': 'WA', 'wa': 'WA',
  'south australia': 'SA', 'sa': 'SA',
  'tasmania': 'TAS', 'tas': 'TAS',
  'australian capital territory': 'ACT', 'act': 'ACT',
  'northern territory': 'NT', 'nt': 'NT'
};
function normalizeStateCode(stateName) {
  if (!stateName || stateName.length <= 3) return (stateName || '').toString().trim().toUpperCase();
  var key = stateName.toString().trim().toLowerCase();
  return STATE_NAME_TO_CODE[key] || stateName.toString().trim().toUpperCase().slice(0, 3);
}

/** Parse "City, State, Country" or "City, State" from formattedAddress when address_components unavailable. */
function parseCityStateFromFormattedAddress(formattedAddress) {
  if (!formattedAddress || typeof formattedAddress !== 'string') return null;
  var parts = formattedAddress.split(',').map(function (p) { return p.trim(); }).filter(Boolean);
  if (parts.length < 2) return null;
  var city = parts[0];
  var state = parts[1];
  var country = parts.length >= 3 ? parts[2] : 'US';
  var stateCode = state.length <= 3 ? state.toUpperCase() : normalizeStateCode(state);
  return { city: city, state: state, state_code: stateCode, country: country };
}

/**
 * Build display label for a place: prefer formatted_address; else "name, city, state" from address_components.
 * City: locality || postal_town || sublocality_level_1. State: administrative_area_level_1 short_name.
 * Compatible with classic PlaceResult (no fetchFields). Returns fallbackLabel when data missing.
 */
function getPlaceDisplayLabel(place, fallbackLabel) {
  if (!place) return fallbackLabel || '';
  var fa = (place.formatted_address || place.formattedAddress || '').toString().trim();
  if (fa) return fa;
  var ac = place.address_components || place.addressComponents;
  if (!ac || !Array.isArray(ac)) return fallbackLabel || '';
  var city = '';
  var state = '';
  ac.forEach(function (c) {
    var types = c.types || [];
    var longName = (c.long_name || c.longText || '').toString().trim();
    var shortName = (c.short_name || c.shortText || longName).toString().trim();
    if (types.indexOf('locality') !== -1) city = longName;
    else if ((types.indexOf('postal_town') !== -1 || types.indexOf('sublocality_level_1') !== -1) && !city) city = longName;
    if (types.indexOf('administrative_area_level_1') !== -1) state = shortName || longName;
  });
  var name = (place.name || place.displayName || '').toString().trim() || (fallbackLabel || '').toString().trim();
  var parts = [name, city, state].filter(Boolean);
  return parts.join(', ') || fallbackLabel || '';
}

var _lastPlaceSelectMs = 0;
function onPlaceSelect(ev) {
  var now = Date.now();
  if (now - _lastPlaceSelectMs < 500) return;
  _lastPlaceSelectMs = now;
  if (typeof window.__WB_onPlaceSelect === 'function') window.__WB_onPlaceSelect(ev);
}

function ensureDocPlaceListeners() {
  if (window.__WB_DOC_PLACE_LISTENERS__) return;
  window.__WB_DOC_PLACE_LISTENERS__ = true;

  document.addEventListener('gmp-placeselect', onPlaceSelect, true);
  document.addEventListener('gmp-select', onPlaceSelect, true);
  document.addEventListener('place_changed', onPlaceSelect, true);

  if (window.__WB_DIAG__) console.log('[WB_DIAG] doc place listeners bound');
}

function applyWizardPlaceAutocompleteChrome(placeEl) {
  if (!placeEl) return false;
  var booking = placeEl.closest && placeEl.closest('.pdp-workshop-booking.wb-wizard');
  if (!booking) return false;
  try {
    placeEl.setAttribute('no-input-icon', '');
    placeEl.noInputIcon = true;
  } catch (_) {}
  try {
    placeEl.style.setProperty('color-scheme', 'light');
    placeEl.style.setProperty('border', 'none');
    placeEl.style.setProperty('border-radius', '0');
    placeEl.style.setProperty('box-shadow', 'none');
    placeEl.style.setProperty('outline', 'none');
    placeEl.style.setProperty('background-color', 'transparent');
    placeEl.style.setProperty('background', 'transparent');
  } catch (_) {}
  return true;
}

function applyPlaceAutocompleteLightTheme(placeEl) {
  try {
    if (
      !placeEl ||
      !placeEl.matches ||
      !placeEl.matches('gmp-place-autocomplete')
    )
      return;
    var booking = placeEl.closest && placeEl.closest('.pdp-workshop-booking');
    var isWizard = !!(booking && booking.classList && booking.classList.contains('wb-wizard'));
    placeEl.style.setProperty('color-scheme', 'light');
    placeEl.style.setProperty('--gmp-mat-color-surface', isWizard ? 'transparent' : '#ffffff');
    placeEl.style.setProperty('--gmp-mat-color-on-surface', '#111827');
    placeEl.style.setProperty('--gmp-mat-color-on-surface-variant', '#6b7280');
    if (isWizard) {
      placeEl.style.setProperty('border', 'none');
      placeEl.style.setProperty('outline', 'none');
      placeEl.style.setProperty('box-shadow', 'none');
      placeEl.style.setProperty('background', 'transparent');
      applyWizardPlaceAutocompleteChrome(placeEl);
    }
  } catch (_) {}
}

function ensurePlaceListeners(placeEl) {
  if (!placeEl) return;
  applyPlaceAutocompleteLightTheme(placeEl);
  applyWizardPlaceAutocompleteChrome(placeEl);
  if (placeEl.dataset) placeEl.dataset.wbBoundV = String(window.__WB_BIND_VERSION__ || 0);

  placeEl.removeEventListener('gmp-placeselect', onPlaceSelect, true);
  placeEl.addEventListener('gmp-placeselect', onPlaceSelect, true);
  placeEl.removeEventListener('gmp-select', onPlaceSelect, true);
  placeEl.addEventListener('gmp-select', onPlaceSelect, true);
  placeEl.removeEventListener('place_changed', onPlaceSelect, true);
  placeEl.addEventListener('place_changed', onPlaceSelect, true);

  // Fallback: if place events don't fire, a debounced 'change' listener tries getPlace()
  if (!placeEl.__wbChangeFallbackBound) {
    placeEl.__wbChangeFallbackBound = true;
    var changeTimer = null;
    placeEl.addEventListener('change', function () {
      if (changeTimer) clearTimeout(changeTimer);
      changeTimer = setTimeout(function () {
        changeTimer = null;
        if (typeof placeEl.getPlace !== 'function') return;
        try {
          var p = placeEl.getPlace();
          if (p && typeof p.then === 'function') {
            p.then(function (resolved) { if (resolved) onPlaceSelect({ target: placeEl, detail: resolved }); }).catch(function () {});
          } else if (p) {
            onPlaceSelect({ target: placeEl, detail: p });
          }
        } catch (_) {}
      }, 300);
    });
  }

  if (window.__WB_DIAG__) console.log('[WB_DIAG] ensurePlaceListeners bound (capture)', { tag: placeEl.tagName, id: placeEl.id });
}

function initWorkshopBooking() {
  var wbSheetBodyEl = document.getElementById('wb-sheet-body');
  var bookingCandidates = document.querySelectorAll('.pdp-workshop-booking');
  var wizardBooking = null;
  for (var wbPick = 0; wbPick < bookingCandidates.length; wbPick++) {
    if (bookingCandidates[wbPick].classList.contains('wb-wizard')) {
      wizardBooking = bookingCandidates[wbPick];
      break;
    }
  }
  const booking =
    (wbSheetBodyEl && wbSheetBodyEl.querySelector('.pdp-workshop-booking')) ||
    wizardBooking ||
    bookingCandidates[0] ||
    document.querySelector('.pdp-workshop-booking') ||
    document.querySelector('.workshop-booking');
  if (!booking) return;

  // Idempotency: if this exact DOM node was already fully initialised, skip.
  // When Shopify replaces section HTML (section:load), the new element has no __wbBound → re-inits.
  // When Shopify fires section:select on the same DOM node → skips (prevents duplicate listeners).
  if (booking.__wbBound === true) {
    if (typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true)) {
      console.log('[WB_INIT] skipping — this booking DOM node already bound');
    }
    return;
  }
  booking.__wbBound = true;
  try {
    var isInSheetCtx = !!(booking.closest && booking.closest('#wb-sheet-body'));
    booking.classList.toggle('wb-in-sheet', isInSheetCtx);
    booking.classList.toggle('wb-standalone', !isInSheetCtx);
  } catch (_) {}

  window.__WB_BIND_VERSION__ = (window.__WB_BIND_VERSION__ || 0) + 1;
  if (window.__WB_DIAG__) console.log('[WB_DIAG] initWorkshopBooking', { v: window.__WB_BIND_VERSION__ });

  var proxyFromRoot = (booking.getAttribute && booking.getAttribute('data-wb-proxy-base'))
    || (booking.getAttribute && booking.getAttribute('data-events-proxy-url'))
    || (booking.dataset && booking.dataset.wbProxyBase)
    || (booking.dataset && booking.dataset.eventsProxyUrl) || '';
  if (proxyFromRoot) proxyFromRoot = String(proxyFromRoot).replace(/\/+$/, '');
  var PROXY_BASE = proxyFromRoot || '/apps/workshop-events';
  var PROXY_BASE_FALLBACK = proxyFromRoot || '/apps/workshop-event';
  var eventsPathAttr = (booking.getAttribute && booking.getAttribute('data-wb-events-path'))
    || (booking.dataset && booking.dataset.wbEventsPath) || '';
  var EVENTS_PATH = (eventsPathAttr && String(eventsPathAttr).trim()) ? String(eventsPathAttr).trim() : '/events-v';
  var venueListSortAttr =
    (booking.getAttribute && booking.getAttribute('data-wb-venue-list-sort')) ||
    (booking.dataset && booking.dataset.wbVenueListSort) ||
    '';
  var VENUE_LIST_SORT_MODE = String(venueListSortAttr || 'distance').trim().toLowerCase().replace(/-/g, '_');
  if (VENUE_LIST_SORT_MODE !== 'events_first') VENUE_LIST_SORT_MODE = 'distance';
  /** Safe join: trim trailing "/" from base, ensure path starts with "/", prevent segment duplication. */
  function joinProxyPath(base, path) {
    var b = String(base).replace(/\/+$/, '');
    var p = String(path);
    if (p.charAt(0) !== '/') p = '/' + p;
    var last = b.split('/').pop();
    var first = p.split('/').filter(Boolean)[0];
    if (last && first && last === first) b = b.slice(0, b.length - last.length - 1);
    return b + p;
  }
  var DEV_MODE = typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true);
  /** Fetch events from proxy; on 404 retry once with legacy path. AbortController and requestId handled by caller. */
  function fetchWorkshopEvents(queryString, fetchOpts) {
    var qs = queryString;
    if (DEV_MODE && qs.indexOf('debug=') === -1) qs = qs + (qs ? '&' : '') + 'debug=1';
    var url = joinProxyPath(PROXY_BASE, EVENTS_PATH) + '?' + qs;
    var requestId = fetchOpts && fetchOpts.requestId;
    var startMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
    if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' url=' + url + ' start');
    var opts = Object.assign({}, fetchOpts);
    delete opts.requestId;
    return fetch(url, opts).then(function (res) {
      if (res.status === 404) {
        if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' 404 fallback');
        console.warn('[Workshop] App proxy returned 404 for ' + url + '; retrying with legacy path');
        return fetch(joinProxyPath(PROXY_BASE_FALLBACK, EVENTS_PATH) + '?' + qs, opts);
      }
      var endMs = typeof performance !== 'undefined' && performance.now ? performance.now() : 0;
      if (DEV_MODE && requestId != null) console.log('[WB_EVENTS] request_id=' + requestId + ' status=' + res.status + ' end ms=' + Math.round(endMs - startMs) + ' aborted=false');
      return res;
    }).catch(function (err) {
      if (DEV_MODE && requestId != null && err && err.name === 'AbortError') console.log('[WB_EVENTS] request_id=' + requestId + ' aborted=true');
      throw err;
    });
  }
  /** Log backend rid when present in parsed data (call from response handler). */
  function logBackendRidIfPresent(requestId, data) {
    if (!DEV_MODE || requestId == null) return;
    if (data && data.meta && data.meta.debug && data.meta.debug.rid)
      console.log('[WB_EVENTS] request_id=' + requestId + ' backend_rid=' + data.meta.debug.rid);
  }

  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(() => {
      const lp = document.querySelector('[data-last-price]');
      const vs = document.getElementById('workshop-variant');
      if (lp && vs && typeof setLastPriceFromVariant === 'function') setLastPriceFromVariant();
      if (typeof updateAllVariantCardPrices === 'function') updateAllVariantCardPrices();
    });
  });

  const stateInput = document.getElementById('workshop-state-input');
  const cityInput = document.getElementById('workshop-city-input');
  /** Scope to this booking — duplicate #workshop-variant ids would otherwise bind the wrong hidden input (sheet vs PDP). */
  const variantSelect =
    booking.querySelector('#workshop-variant') ||
    booking.querySelector('[data-workshop-variant-hidden]') ||
    document.getElementById('workshop-variant');
  let variantFilterActive = false;

  const eventsRoot = booking.querySelector('[data-workshop-events]');
  const eventsPlaceholder = booking.querySelector('[data-workshop-events-placeholder]');
  function wbwDispatchVenueReset() {
    if (!booking.classList || !booking.classList.contains('wb-wizard')) return;
    try {
      document.dispatchEvent(
        new CustomEvent('wbw:venue-reset', { detail: { bookingRoot: booking } })
      );
    } catch (_) {}
  }
  /** Show user-friendly error in events area (do not assume fetch returned valid JSON). */
  function showWorkshopFetchError(msg) {
    wbwDispatchVenueReset();
    if (eventsRoot) eventsRoot.innerHTML = '';
    if (eventsPlaceholder) {
      eventsPlaceholder.classList.remove('pdp-wb-hidden');
      eventsPlaceholder.textContent = (msg && String(msg).trim()) ? String(msg).trim() : "Couldn't load sessions. Try again.";
    }
    if (booking.classList && booking.classList.contains('wb-wizard')) {
      try {
        wbRefreshBookingGuideUi();
      } catch (_) {}
    }
  }

  /** Single loader: placeholder only. No extra node in [data-workshop-events]. Always queries from booking. */
  function setLoading(bookingRoot, show) {
    var root = bookingRoot && bookingRoot.querySelector('[data-workshop-events]');
    var ph = bookingRoot && bookingRoot.querySelector('[data-workshop-events-placeholder]');
    if (show) {
      if (bookingRoot === booking) wbwDispatchVenueReset();
      if (root) root.innerHTML = '';
      if (ph) { ph.textContent = 'Loading sessions\u2026'; ph.classList.remove('pdp-wb-hidden'); }
    } else {
      var loaders = bookingRoot ? bookingRoot.querySelectorAll('[data-workshop-loading="1"]') : [];
      loaders.forEach(function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
      if (ph) ph.classList.add('pdp-wb-hidden');
    }
  }

  const variantWrap = booking.querySelector('[data-workshop-variant-wrap]');
  const eventsPayloadScript = booking.querySelector('script[data-workshop-events-payload]');
  const productHandle = (booking.dataset.productHandle || '').trim().toLowerCase();
  const productGid = (booking.dataset.productGid || '').trim();

  const useLocationFirst = Boolean(eventsRoot && eventsPayloadScript && productHandle);

  // Google key can be on a child node OR on the booking root itself.
  const googleKeyEl =
    (booking && booking.querySelector && booking.querySelector('[data-google-maps-key]')) ||
    (booking && booking.hasAttribute && booking.hasAttribute('data-google-maps-key') ? booking : null);

  const googleKey = googleKeyEl
    ? (googleKeyEl.getAttribute('data-key') || (googleKeyEl.dataset && googleKeyEl.dataset.key) || '')
    : '';
  if (useLocationFirst && !googleKey && window.__WB_DIAG__) {
    console.warn('[Workshop] Google Maps key missing. Add api_key to google_maps metaobject (or api_keys.google_maps), or set workshop_google_maps_api_key in theme settings.');
  }

  const bookingBox = booking.querySelector('[data-workshop-booking]');
  if (bookingBox) bookingBox.classList.add('wb-no-event-selected');
  const bookingHint = bookingBox ? bookingBox.querySelector('[data-booking-hint]') : null;
  var panelScopeRoot = booking.querySelector('[data-wb-panel-scope]') || booking;
  const panelTitle = panelScopeRoot.querySelector('[data-wb-panel-title]');
  const panelHintEl = panelScopeRoot.querySelector('[data-wb-panel-hint]');
  const selectedSummary = bookingBox ? bookingBox.querySelector('[data-selected-event-summary]') : null;
  const selectedEventIdInput = bookingBox ? bookingBox.querySelector('[data-selected-event-id]') : null;
  const lastPriceEl = bookingBox ? bookingBox.querySelector('[data-last-price]') : null;
  const selectedItemsWrap = bookingBox ? bookingBox.querySelector('[data-wb-selected-items]') : null;
  const selectedItemsList = bookingBox ? bookingBox.querySelector('[data-wb-selected-items-list]') : null;
  const selectedItemsTotal = bookingBox ? bookingBox.querySelector('[data-wb-selected-items-total]') : null;
  const bookNowBtn = bookingBox ? bookingBox.querySelector('[data-book-now]') : null;
  const addToCartBtn = bookingBox ? bookingBox.querySelector('[data-add-to-cart]') : null;
  var wbLockedVenueRefId = '';
  var wbLockedVenueGroupKey = '';

  const summaryVenue = bookingBox ? bookingBox.querySelector('[data-selected-event-venue]') : null;
  const summaryVenueDetails = bookingBox ? bookingBox.querySelector('[data-selected-event-venue-details]') : null;
  const summaryDatetime = bookingBox ? bookingBox.querySelector('[data-selected-event-datetime]') : null;
  const summaryCapacity = bookingBox ? bookingBox.querySelector('[data-selected-event-capacity]') : null;
  const summaryStatus = bookingBox ? bookingBox.querySelector('[data-selected-event-status]') : null;

  const normalize = (v) => (v || '').toString().trim();
  const normKey = (v) => normalize(v).toLowerCase();

  function formatMoney(cents) {
    const c = typeof cents === 'number' ? cents : parseInt(cents || '0', 10);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        return window.Shopify.formatMoney(c, window.Shopify.money_format);
      } catch (_) {}
    }
    // Fallback
    return `$${(c / 100).toFixed(2)}`;
  }

  /** Bottom price: list (base) above sale when resolved < base. Expects workshop-final-price inner spans. */
  function setLastPriceElDisplay(el, baseCents, resolvedCents) {
    if (!el) return;
    var compareEl = el.querySelector('[data-last-price-compare]');
    var currentEl = el.querySelector('[data-last-price-current]');
    var dashHtml = '<span style="color:#999;">—</span>';
    var resolved =
      typeof resolvedCents === 'number' && Number.isFinite(resolvedCents) && resolvedCents > 0 ? resolvedCents : null;
    var base =
      typeof baseCents === 'number' && Number.isFinite(baseCents) && baseCents > 0 ? baseCents : null;

    if (!currentEl) {
      if (!resolved) el.innerHTML = dashHtml;
      else el.textContent = formatMoney(resolved);
      return;
    }

    if (!resolved) {
      currentEl.innerHTML = dashHtml;
      if (compareEl) {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
      return;
    }

    currentEl.textContent = formatMoney(resolved);
    if (compareEl && base != null && base > resolved) {
      compareEl.textContent = formatMoney(base);
      compareEl.hidden = false;
    } else if (compareEl) {
      compareEl.textContent = '';
      compareEl.hidden = true;
    }
  }

  function readJsonScriptInBooking(selector) {
    const el = booking.querySelector(selector);
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || '[]');
    } catch (_) {
      return [];
    }
  }

  /** Optional: set true to log event-time override parsing and matching. Default false. */
  const WB_PRICE_DEBUG = false;

  const locationRules = readJsonScriptInBooking('script[data-workshop-location-rules]');
  const venueOverrides = readJsonScriptInBooking('script[data-workshop-venue-overrides]');
  const eventTimeOverrides = readJsonScriptInBooking('script[data-workshop-event-time-overrides]');
  if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbETOLogged) {
    window.__wbETOLogged = true;
    try {
      console.log('[WB] eventTimes parsed', { count: eventTimeOverrides.length, first: eventTimeOverrides[0] });
    } catch (_) {}
  }

  function wbDiag(label, data) {
    if (typeof window !== 'undefined' && window.__WB_DIAG__) {
      try { console.log('[WB] ' + label, data != null ? data : ''); } catch (_) {}
    }
  }

  function getSelections() {
    return {
      state: useLocationFirst ? (window.__WORKSHOP_LOCATION__ && window.__WORKSHOP_LOCATION__.state) || '' : normalize(stateInput && stateInput.value),
      city: useLocationFirst ? (window.__WORKSHOP_LOCATION__ && window.__WORKSHOP_LOCATION__.city) || '' : normalize(cityInput && cityInput.value),
      variant: normalize(variantSelect && variantSelect.value),
    };
  }

  function getSelectedVariantPriceCents() {
    if (!variantSelect) return null;
    var activeBtn = booking.querySelector('.wb-variant-btn.is-active');
    if (activeBtn) {
      var cents = activeBtn.dataset.basePrice;
      return cents ? parseInt(cents, 10) : null;
    }
    var val = variantSelect.value;
    if (!val) return null;
    var fallback = booking.querySelector('.wb-variant-btn[data-variant-id="' + CSS.escape(val) + '"]');
    return fallback && fallback.dataset.basePrice ? parseInt(fallback.dataset.basePrice, 10) : null;
  }

  function getSelectedVariantId() {
    if (!variantSelect) return '';
    return variantSelect.value || '';
  }

  // Helper — call this wherever the booking step changes
  function wstUpdate(step, values) {
    document.dispatchEvent(new CustomEvent('wst:update', {
      detail: { step: step, values: values || {} }
    }));
  }

  function wstFormatDateKey(dateKey) {
    var raw = String(dateKey || '').trim();
    if (!raw) return '';
    var d = new Date(raw + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return '';
    try {
      return new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short' }).format(d);
    } catch (_) {
      return raw;
    }
  }

  function wstValues(venueName, dateLabel, timeLabel, kitName, extrasName) {
    var values = {};
    var v1 = String(venueName || '').trim();
    var v2 = String(dateLabel || '').trim();
    var v3 = String(timeLabel || '').trim();
    var v4 = String(kitName || '').trim();
    var v6 = String(extrasName || '').trim();
    if (v1) values[1] = v1;
    if (v2) values[2] = v2;
    if (v3) values[3] = v3;
    if (v4) values[4] = v4;
    if (v6) values[6] = v6;
    return values;
  }
  /** Last two args: ticket quantity (step 5), extras label (step 6). */
  function wstValuesWithQty(venueName, dateLabel, timeLabel, kitName, ticketQtyLabel, extrasLabel) {
    var values = wstValues(venueName, dateLabel, timeLabel, kitName, extrasLabel);
    var v5 = String(ticketQtyLabel || '').trim();
    if (v5) values[5] = v5;
    return values;
  }
  function wstGetVariantNameFromButton(btn) {
    if (!btn) return '';
    var name = String(btn.getAttribute('data-variant-title') || btn.dataset.variantTitle || '').trim();
    if (!name) {
      var titleEl = btn.querySelector('.wb-variant-title');
      name = titleEl ? String(titleEl.textContent || '').trim() : '';
    }
    return name;
  }

  function wstGetActiveVariantName() {
    var activeBtn = booking ? booking.querySelector('.wb-variant-btn.is-active') : null;
    return wstGetVariantNameFromButton(activeBtn);
  }

  function wstGetCurrentBookingFacts() {
    var selectedCard = eventsRoot ? eventsRoot.querySelector('.workshop-event-card.is-selected') : null;
    if (!selectedCard) return null;
    var qtyInput = bookingBox ? bookingBox.querySelector('[data-workshop-quantity]') : null;
    var qtyNum = Number((qtyInput && qtyInput.value) || 1);
    if (!Number.isFinite(qtyNum) || qtyNum < 1) qtyNum = 1;
    return {
      venueName: String(selectedCard.getAttribute('data-venue-name') || '').trim(),
      dateLabel: wstFormatDateKey(selectedCard.getAttribute('data-date-key') || ''),
      timeLabel: String(selectedCard.getAttribute('data-time-label') || '').trim(),
      variantName: wstGetActiveVariantName(),
      extrasName: (function () {
        var addon = typeof addonSelection === 'function' ? addonSelection() : null;
        return addon && addon.selected ? String(addon.title || '').trim() : 'None';
      })(),
      qtyLabel: String(Math.round(qtyNum))
    };
  }

  function isBlank(val) {
    return val == null || normalize(val) === '';
  }

  function matchDim(ruleVal, actualVal) {
    // wildcard matching: blank = match any
    if (isBlank(ruleVal)) return true;
    return normKey(ruleVal) === normKey(actualVal);
  }

  /** Canonical event id for matching: Liquid outputs metaobject system.id (numeric); card may have ev.id (numeric) or ev.event_metaobject_gid (GID). */
  function normalizeEventIdForMatch(val) {
    if (val == null) return '';
    var s = String(val).trim();
    if (s === '') return '';
    var metaGid = /^gid:\/\/shopify\/Metaobject\/(\d+)$/i.exec(s);
    if (metaGid) return metaGid[1];
    if (/^\d+$/.test(s)) return s;
    return s;
  }

  /** Numeric id for URL param (metaobject GID or plain digits); empty string if not extractable. */
  function extractNumericEventIdForUrl(val) {
    if (val == null) return '';
    var s = String(val).trim();
    if (!s) return '';
    var metaGid = /^gid:\/\/shopify\/Metaobject\/(\d+)$/i.exec(s);
    if (metaGid) return metaGid[1];
    if (/^\d+$/.test(s)) return s;
    return '';
  }

  function getEventIdFromUrl() {
    // Prefer value captured at script-load time (before theme strips it from URL)
    if (window.__WB_INITIAL_EVENT_ID__ != null && String(window.__WB_INITIAL_EVENT_ID__).trim() !== '') {
      return String(window.__WB_INITIAL_EVENT_ID__).trim();
    }
    try {
      var params = new URLSearchParams(window.location.search);
      return (params.get('event_id') || '').trim();
    } catch (e) { return ''; }
  }

  function wbStickyVariantId() {
    if (window.__WB_URL_VARIANT_NUMERIC__) return String(window.__WB_URL_VARIANT_NUMERIC__).trim();
    if (window.__WB_URL_VARIANT_GID__) {
      return formatVariantIdForUrl(window.__WB_URL_VARIANT_GID__);
    }
    var fromInitial = formatVariantIdForUrl(window.__WB_INITIAL_VARIANT__ || '');
    if (fromInitial) return fromInitial;
    try {
      return formatVariantIdForUrl(new URLSearchParams(window.location.search).get('variant') || '');
    } catch (e) {
      return '';
    }
  }

  function wbApplyVariantIdToParams(params) {
    var vid = wbStickyVariantId();
    if (!vid) return;
    params.set('variant', vid);
    window.__WB_URL_VARIANT_NUMERIC__ = vid;
  }

  function updateUrlForEventId(eventId) {
    try {
      var params = new URLSearchParams(window.location.search);
      var numericEventId = extractNumericEventIdForUrl(eventId);
      if (numericEventId) {
        params.set('event_id', numericEventId);
      } else {
        params.delete('event_id');
      }
      wbApplyVariantIdToParams(params);
      var qs = params.toString();
      var nextUrl = window.location.pathname + (qs ? ('?' + qs) : '') + (window.location.hash || '');
      history.replaceState(history.state, '', nextUrl);
      window.__WB_URL_EVENT_ID_NUMERIC__ = numericEventId || '';
    } catch (e) {}
  }

  function wbPatchHistoryUrl(url) {
    try {
      var hasUrlArg = url != null && String(url) !== '';
      if (!hasUrlArg) return url;
      var currentParams = new URLSearchParams(window.location.search);
      var currentEventId = (currentParams.get('event_id') || '').trim();
      var stickyEventId =
        currentEventId ||
        (window.__WB_URL_EVENT_ID_NUMERIC__ || '') ||
        extractNumericEventIdForUrl(window.__WB_INITIAL_EVENT_ID__ || '');
      var stickyVariantId = wbStickyVariantId();
      if (!stickyEventId && !stickyVariantId) return url;
      var absoluteUrl = new URL(String(url), window.location.origin);
      if (stickyEventId && !(absoluteUrl.searchParams.get('event_id') || '').trim()) {
        absoluteUrl.searchParams.set('event_id', stickyEventId);
      }
      if (stickyVariantId) {
        var lockVariant = window.__WB_LOCK_VARIANT_URL__ === true;
        var urlVarNum = formatVariantIdForUrl(absoluteUrl.searchParams.get('variant') || '');
        if (lockVariant || !urlVarNum) {
          absoluteUrl.searchParams.set('variant', stickyVariantId);
        }
      }
      return absoluteUrl.pathname + absoluteUrl.search + absoluteUrl.hash;
    } catch (e) {
      return url;
    }
  }

  if (!window.__WB_HISTORY_EVENT_PATCHED__) {
    window.__WB_HISTORY_EVENT_PATCHED__ = true;
    (function patchHistory() {
      var originalReplaceState = history.replaceState;
      history.replaceState = function (state, unused, url) {
        url = wbPatchHistoryUrl(url);
        return originalReplaceState.call(this, state, unused, url);
      };
      var originalPushState = history.pushState;
      history.pushState = function (state, unused, url) {
        url = wbPatchHistoryUrl(url);
        return originalPushState.call(this, state, unused, url);
      };
    })();
  }

  function matchEventId(selectedEventId, ruleWorkshopEventId) {
    if (ruleWorkshopEventId == null || String(ruleWorkshopEventId).trim() === '') return false;
    var a = normalizeEventIdForMatch(selectedEventId);
    var b = normalizeEventIdForMatch(ruleWorkshopEventId);
    return a !== '' && b !== '' && a === b;
  }

  function matchVariant(ruleVariantId, selectedVariantId) {
    // wildcard matching: blank = match any
    if (ruleVariantId == null || String(ruleVariantId) === '') return true;
    return (
      normalizeVariantIdForMatch(ruleVariantId) === normalizeVariantIdForMatch(selectedVariantId)
    );
  }

  function matchVariantExact(ruleVariantId, selectedVariantId) {
    return (
      normalizeVariantIdForMatch(ruleVariantId) === normalizeVariantIdForMatch(selectedVariantId)
    );
  }

  /** Normalize override amount to integer cents. Values < 500 treated as dollars (e.g. 10 -> 1000); >= 10000 and divisible by 100 may be double-converted (e.g. 100000 -> 1000); else assume already cents. */
  function toCentsInt(n) {
    if (n == null) return null;
    var x = Number(n);
    if (!Number.isFinite(x)) return null;
    if (Math.abs(x) < 500) return Math.round(x * 100);
    if (Math.abs(x) >= 10000 && Math.abs(x) % 100 === 0) return Math.round(x / 100);
    return Math.round(x);
  }

  function matchLocationScope(ruleScope, ruleVariantId, selectedVariantId) {
    // EXACT per requirements:
    // - all_variants => ignore rule.variant entirely
    // - single_variant => rule.variant MUST be present AND match selected variant
    const s = normKey(ruleScope);
    if (s === 'all_variants') return true;
    if (s === 'single_variant') {
      if (ruleVariantId == null || String(ruleVariantId) === '') return false;
      return matchVariantExact(ruleVariantId, selectedVariantId);
    }
    return false;
  }

  function specificityCount(fields) {
    // counts how many rule dimensions are explicitly set (more = more specific)
    return fields.reduce((acc, v) => (isBlank(v) ? acc : acc + 1), 0);
  }

  function pickBest(candidates, scoreFn) {
    let best = null;
    let bestScore = -1;
    let bestId = null;
    for (const c of candidates) {
      const score = scoreFn(c);
      const id = c && c.id != null ? String(c.id) : '';
      if (score > bestScore) {
        best = c;
        bestScore = score;
        bestId = id;
        continue;
      }
      if (score === bestScore && best != null) {
        // deterministic tie-break: smallest id wins
        if (id && bestId && id < bestId) {
          best = c;
          bestId = id;
        }
      }
    }
    return best;
  }

  function resolvePriceCents({ basePriceCents, eventId, state, city, cityKey, venueName, venueHandle, variantId, workshopId: providedWorkshopId }, testOverrides) {
    const locs = (testOverrides && testOverrides.locationRules) || locationRules;
    const venues = (testOverrides && testOverrides.venueOverrides) || venueOverrides;
    const eventTimes = (testOverrides && testOverrides.eventTimeOverrides) || eventTimeOverrides;

    const base = basePriceCents;
    const eId = String(eventId || '');
    const vId = String(variantId || '');
    const venueHandleNorm = normKey(venueHandle || '');
    const selectedCityKey = (cityKey || '').toString().trim();
    const selectedCityKeyNorm = normKey(selectedCityKey);
    const workshopId = providedWorkshopId ? String(providedWorkshopId) : String(booking?.dataset?.productId || '');
    if (WB_PRICE_DEBUG && eId && typeof window !== 'undefined' && !window.__wbSelectedEventKeyLogged) {
      window.__wbSelectedEventKeyLogged = true;
      try {
        console.log('[WB] selected event key', { raw: eId, normalized: normalizeEventIdForMatch(eId) });
      } catch (_) {}
    }
    const ctx = { variantId: vId, basePriceCents: base, state, city, venueName, workshopEventId: eId };
    wbDiag('resolvePriceCents:context', ctx);
    if (base == null || (typeof base !== 'number') || Number.isNaN(base)) {
      wbDiag('resolvePriceCents:baseMissingOrInvalid', { base, basePriceCents });
    }

    // 1) Event time override - workshop_event_id required (match by canonical id: numeric or GID); optional geo/venue/variant with matchDim (blank = match any for that event)
    // JSON keys: workshop_event_id, variant_id, adjustment_type, pricing_status, amount_cents, percent (amount_cents from t.amount; fallback price_cents for compat)
    const etoCandidates = eventTimes.filter((o) => {
      if (!o) return false;
      if (!matchEventId(eId, o.workshop_event_id)) return false;
      if (!matchDim(o.state, state)) return false;
      if (!matchDim(o.city, city)) return false;
      if (!matchDim(o.venue_name, venueName)) return false;
      if (!matchVariant(o.variant_id, vId)) return false;
      return true;
    });
    if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbETOCandidatesLogged) {
      window.__wbETOCandidatesLogged = true;
      try {
        var first3 = etoCandidates.slice(0, 3).map(function (o) {
          return {
            workshop_event_id_raw: o && o.workshop_event_id,
            workshop_event_id_normalized: o ? normalizeEventIdForMatch(o.workshop_event_id) : null,
            variant_id: o && o.variant_id,
            adjustment_type: o && o.adjustment_type,
            amount_cents: o && o.amount_cents,
            percent: o && o.percent
          };
        });
        console.log('[WB] etoCandidates stats', {
          eId_normalized: normalizeEventIdForMatch(eId),
          etoCandidatesCount: etoCandidates.length,
          eventTimesTotal: eventTimes.length,
          first3: first3
        });
      } catch (_) {}
    }
    const eto = pickBest(etoCandidates, (o) =>
      specificityCount([o.workshop_event_id, o.state, o.city, o.venue_name, o.variant_id])
    );
    var rawAmount = eto && (eto.amount_cents != null ? eto.amount_cents : eto.price_cents != null ? eto.price_cents : null);
    var amountCents = rawAmount != null ? toCentsInt(rawAmount) : null;
    if (eto && amountCents != null) {
      const adjType = normKey(eto.adjustment_type);
      var afterCents;
      if (adjType === 'percent' && typeof eto.percent === 'number' && Number.isFinite(eto.percent)) {
        afterCents = Math.round(base * (1 + eto.percent / 100));
      } else if (adjType === 'delta') {
        afterCents = base + amountCents;
      } else if (adjType === 'fixed_price') {
        afterCents = amountCents;
      } else {
        var legacyDelta = amountCents < 0;
        afterCents = legacyDelta ? base + amountCents : amountCents;
      }
      var result = Math.max(0, Math.round(afterCents));
      if (!Number.isFinite(result) || result <= 0) {
        wbDiag('resolvePriceCents:eventTimeMatch', { eventTimeMatch: null, reason: 'invalidResult' });
      } else {
      if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbPriceUnitLogged) {
        window.__wbPriceUnitLogged = true;
        try {
          console.log('[WB] event_time override units', {
            base: base,
            raw_eto_amount_cents: eto.amount_cents,
            raw_eto_price_cents: eto.price_cents,
            adjustment_type: eto.adjustment_type,
            amountCents_normalized: amountCents,
            afterCents: afterCents,
            result: result
          });
        } catch (_) {}
      }
      if (WB_PRICE_DEBUG && !window.__wbPriceDebugLogged) {
        try {
          window.__wbPriceDebugLogged = true;
          var firstOverride = eventTimes[0];
          console.log('[WB] resolvePriceCents event_time', {
            selectedEventKeyRaw: eId,
            selectedEventKeyNormalized: normalizeEventIdForMatch(eId),
            firstOverrideWorkshopEventIdRaw: firstOverride ? firstOverride.workshop_event_id : null,
            firstOverrideWorkshopEventIdNormalized: firstOverride ? normalizeEventIdForMatch(firstOverride.workshop_event_id) : null,
            adjustment_type: eto.adjustment_type,
            amount_cents: amountCents,
            percent: eto.percent,
            baseCents: base,
            resolvedCents: result
          });
        } catch (_) {}
      }
      wbDiag('resolvePriceCents:eventTimeMatch', { eventTimeMatch: eto, amount_cents: amountCents, centsBefore: base, centsAfter: result });
      return result;
      }
    }
    wbDiag('resolvePriceCents:eventTimeMatch', { eventTimeMatch: null });

    // 2) Venue override - match by venue_handle (prefer) or venue_name; city/state when set; variant per scope. amount_cents from t.amount (integer cents).
    const venueNameNorm = normKey(venueName);
    function normVenueName(s) { return normKey((s || '').toString().replace(/^venue\s*#?\s*/i, '').trim()); }
    const voCandidates = venues.filter((o) => {
      if (!o) return false;
      var venueMatch = (venueHandleNorm && normKey(o.venue_handle) === venueHandleNorm) ||
        (venueNameNorm && normKey(o.venue_name) === venueNameNorm) ||
        (venueNameNorm && normVenueName(o.venue_name) === normVenueName(venueName));
      if (!venueMatch) return false;
      if (!isBlank(o.city) && normKey(o.city) !== normKey(city)) return false;
      if (!isBlank(o.state) && normKey(o.state) !== normKey(state)) return false;
      if (!isBlank(o.scope)) {
        const s = normKey(o.scope);
        if (s === 'single_variant') {
          if (o.variant_id == null || String(o.variant_id) === '') return false;
          if (!matchVariantExact(o.variant_id, vId)) return false;
        } else if (s !== 'all_variants') return false;
      }
      if (o.variant_id != null && String(o.variant_id) !== '' && !matchVariantExact(o.variant_id, vId)) return false;
      return true;
    });
    const vo = pickBest(voCandidates, (o) =>
      specificityCount([o.venue_handle, o.venue_name, o.state, o.city, o.scope, o.variant_id])
    );
    var voRaw = vo && (vo.amount_cents != null ? vo.amount_cents : vo.price_cents != null ? vo.price_cents : null);
    var voCents = voRaw != null ? toCentsInt(voRaw) : null;
    if (vo && voCents != null) {
      const voAdjType = normKey(vo.adjustment_type);
      var voAfterCents;
      if (voAdjType === 'percent' && typeof vo.percent === 'number' && Number.isFinite(vo.percent)) {
        voAfterCents = Math.round(base * (1 + vo.percent / 100));
      } else if (voAdjType === 'delta') {
        voAfterCents = base + voCents;
      } else if (voAdjType === 'fixed_price' || voAdjType === 'fixed') {
        voAfterCents = voCents;
      } else {
        voAfterCents = voCents < 0 ? base + voCents : voCents;
      }
      const voResult = Math.max(0, Math.round(voAfterCents));
      if (WB_PRICE_DEBUG && typeof window !== 'undefined' && !window.__wbVenueOverrideLogged) {
        window.__wbVenueOverrideLogged = true;
        try {
          console.log('[WB] venue override', { venueName, venueHandle, voRaw, voCents, adjustment_type: vo.adjustment_type, base, voResult });
        } catch (_) {}
      }
      if (!Number.isFinite(voResult) || voResult <= 0) {
        wbDiag('resolvePriceCents:venueMatch', { venueMatch: null, reason: 'invalidResult' });
      } else {
        wbDiag('resolvePriceCents:venueMatch', { venueMatch: vo, amount_cents: voCents, centsBefore: base, centsAfter: voResult });
        return voResult;
      }
    }
    wbDiag('resolvePriceCents:venueMatch', { venueMatch: null });

    // 3) Location rule - workshop + scope/variant match; location: city_key exact, or location_query contained in city_key, or both blank = global
    // Ignore rule.state. pricing_status must be active.
    function locationRuleMatches(r) {
      if (!r) return false;
      if (normKey(r.pricing_status) !== 'active') return false;
      if (r.workshop_id != null && String(r.workshop_id) !== '' && String(r.workshop_id) !== workshopId) return false;
      if (!matchLocationScope(r.scope, r.variant_id, vId)) return false;
      var rCityKey = (r.city_key || '').toString().trim();
      var rLocQuery = (r.location_query || '').toString().trim();
      if (rCityKey !== '') {
        if (normKey(rCityKey) !== selectedCityKeyNorm) return false;
      } else if (rLocQuery !== '') {
        var qNorm = normKey(rLocQuery);
        if (!qNorm || selectedCityKeyNorm.indexOf(qNorm) === -1) return false;
      }
      var amt = r.amount_cents != null ? r.amount_cents : r.price_cents;
      if (amt == null && (r.percent == null || !Number.isFinite(r.percent))) return false;
      return true;
    }
    const locCandidates = locs.filter(locationRuleMatches);
    if (locCandidates.length > 0) {
      const getTier = (r) => {
        if (normKey(r.scope) === 'single_variant' && r.variant_id != null && String(r.variant_id) !== '' && matchVariantExact(r.variant_id, vId)) return 1;
        if (!isBlank(r.city_key)) return 2;
        if (!isBlank(r.location_query)) return 3;
        return 4;
      };
      const bestTier = Math.min(...locCandidates.map(getTier));
      const tierCandidates = locCandidates.filter((r) => getTier(r) === bestTier);
      const numId = (r) => { const n = parseInt(r && r.id, 10); return Number.isFinite(n) ? n : Infinity; };
      let locWinner = null;
      let bestWorkshopScore = -1;
      let bestNumId = Infinity;
      for (const r of tierCandidates) {
        const ws = (r.workshop_id != null && String(r.workshop_id) !== '') ? 1 : 0;
        const nid = numId(r);
        if (ws > bestWorkshopScore || (ws === bestWorkshopScore && nid < bestNumId)) {
          locWinner = r;
          bestWorkshopScore = ws;
          bestNumId = nid;
        }
      }
      if (locWinner) {
        if (WB_PRICE_DEBUG && locWinner && locWinner.amount_cents != null) {
          const rawAmount = locWinner.amount_cents;

          // Suspiciously small cents (likely dollars accidentally passed as cents)
          if (Math.abs(Number(rawAmount)) < 50) {
            console.warn('[WB] Suspicious small amount_cents in location rule', {
              id: locWinner.id,
              amount_cents: rawAmount
            });
          }

          // Decimal detected in cents (should always be integer)
          if (String(rawAmount).includes('.')) {
            console.warn('[WB] Decimal detected in amount_cents (should be integer cents)', {
              id: locWinner.id,
              amount_cents: rawAmount
            });
          }
        }
        const adj = normKey(locWinner.adjustment_type || '');
        var result = null;
        if (adj === 'percent') {
          var pct = Number(locWinner.percent);
          if (Number.isFinite(pct)) result = Math.round(base * (1 + pct / 100));
        } else {
          var amount = locWinner.amount_cents != null ? locWinner.amount_cents : locWinner.price_cents;
          amount = amount != null ? toCentsInt(amount) : null;
          if (amount != null) {
            if (adj === 'fixed' || adj === 'fixed_price') result = amount;
            else if (adj === 'delta') result = base + amount;
            else result = null;
          }
        }
        if (result != null && Number.isFinite(result) && result > 0) {
          if (WB_PRICE_DEBUG && typeof window !== 'undefined') {
            try { console.log('[WB] location applied', { id: locWinner.id, adjustment_type: locWinner.adjustment_type, amount_cents: locWinner.amount_cents, percent: locWinner.percent, base: base, resolvedCents: result }); } catch (_) {}
          }
          wbDiag('resolvePriceCents:locationRuleMatch', { locationRuleMatch: locWinner, amount_cents: locWinner.amount_cents, centsBefore: base, centsAfter: result });
          return result;
        }
      }
    }
    wbDiag('resolvePriceCents:locationRuleMatch', { locationRuleMatch: null });

    wbDiag('resolvePriceCents:fallbackBase', { cents: base });
    return base;
  }

    // Expose resolver globally once (for workshop-cart.js)
    if (typeof window !== 'undefined') window.resolveWorkshopPrice = resolvePriceCents;

  function diagnosticRun() {
    if (typeof window === 'undefined' || !window.__WB_DIAG__) return;
    const base = 1000;
    const opts = { basePriceCents: base, eventId: '', state: '', city: '', venueName: '', variantId: '1', workshopId: '' };
    const venue = (name, city, state, variantId, priceCents) => ({ venue_name: name || '', city: city || '', state: state || '', scope: variantId != null ? 'single_variant' : null, variant_id: variantId != null ? variantId : null, price_cents: priceCents });
    let r;
    r = resolvePriceCents({ ...opts, venueName: 'Some Venue', city: 'LA' }, { locationRules: [], venueOverrides: [venue('', 'NYC', '', null, -5)], eventTimeOverrides: [] });
    console.assert(r === base, 'Venue override with blank venue_name must not match (no global bleed)');
    r = resolvePriceCents({ ...opts, venueName: 'Some Venue', city: 'LA' }, { locationRules: [], venueOverrides: [venue('Some Venue', 'NYC', '', null, 500)], eventTimeOverrides: [] });
    console.assert(r === base, 'Venue override with city=NYC must not match when selection city is LA');
    r = resolvePriceCents({ ...opts, venueName: 'V', variantId: '222' }, { locationRules: [], venueOverrides: [venue('V', '', '', '111', 500)], eventTimeOverrides: [] });
    console.assert(r === base, 'Variant-scoped venue override must not apply to other variant');
    r = resolvePriceCents({ ...opts, venueName: 'V' }, { locationRules: [], venueOverrides: [venue('V', '', '', null, -100)], eventTimeOverrides: [] });
    console.assert(r === 900, 'Negative venue override must apply as delta (1000 + -100 = 900)');
    wbDiag('diagnosticRun', 'all asserts passed');
  }
  if (typeof window !== 'undefined' && window.__WB_DIAG__) {
    try { diagnosticRun(); } catch (e) { console.warn('[WB] diagnosticRun failed', e); }
  }

  function updateSelectedEventPrices(selectedCard) {
    const base = getSelectedVariantPriceCents();
    if (!base || Number.isNaN(base)) return;

    const variantId = variantSelect ? variantSelect.value : '';
    const eventId = (selectedCard.dataset.eventMetaobjectGid || selectedCard.dataset.eventId || '').toString().trim();
    const state = selectedCard.dataset.state || '';
    const city = selectedCard.dataset.city || '';
    const cityKey = (selectedCard.dataset.cityKey || '').toString().trim();
    const venueName = (selectedCard.dataset.venueName || '').toString().trim();
    const venueHandle = (selectedCard.dataset.venueHandle || '').toString().trim();

    const resolved = resolvePriceCents({
      basePriceCents: base,
      eventId,
      state,
      city,
      cityKey,
      venueName,
      venueHandle,
      variantId,
    });

    if (lastPriceEl) setLastPriceElDisplay(lastPriceEl, base, resolved);

    const priceEl = selectedCard.querySelector('[data-event-final-price]');
    if (priceEl) priceEl.textContent = formatMoney(resolved);

    // Store resolved cents on the card so we can send it on add-to-cart.
    selectedCard.dataset.resolvedPriceCents = String(resolved);
  }

  /** AUDIT: Writes base price to [data-last-price] when no event selected. Reads .wb-variant-btn.is-active dataset.basePrice. */
  function setLastPriceFromVariant() {
    let lp = lastPriceEl;
    if (!document.body.contains(lp) && booking) lp = booking.querySelector('[data-last-price]');
    if (!lp) return;
    var activeBtn = booking.querySelector('.wb-variant-btn.is-active');
    var cents = activeBtn && activeBtn.dataset.basePrice ? parseInt(activeBtn.dataset.basePrice, 10) : null;
    if (!cents || Number.isNaN(cents)) {
      setLastPriceElDisplay(lp, null, null);
      return;
    }
    setLastPriceElDisplay(lp, cents, cents);
  }

  /**
   * Update the single [data-variant-price] element per variant card.
   * AUDIT: Bottom-left price is set by showPreviewPrice(card) and setLastPriceFromVariant().
   * resolvePriceCents inputs: basePriceCents, eventId, state, city, venueName, variantId, workshopId.
   * Rule sources: locationRules, venueOverrides, eventTimeOverrides (from script[data-workshop-*] in booking).
   * Resolver returns cents (integer); no match => returns base. Invalid base => can return NaN (we fallback).
   * - If NO event selected → show BASE only (do not call resolver).
   * - If event selected → show RESOLVED per variant; if resolved invalid/<=0 → fallback to base.
   */
  function updateAllVariantCardPrices() {
    var root = booking && document.body && document.body.contains(booking)
      ? booking
      : (document.querySelector('.pdp-workshop-booking') || document.querySelector('.workshop-booking'));
    if (!root) return;

    var eventsContainer = root.querySelector('[data-workshop-events]');
    var selectedCard = eventsContainer ? eventsContainer.querySelector('.workshop-event-card.is-selected') : null;
    if (!selectedCard) {
      var sheetRoot0 = document.getElementById('wb-sheet-body');
      var hasSheetBookingUi0 = sheetRoot0 && sheetRoot0.querySelector('.pdp-workshop-booking');
      var inSheetContext0 = !!(root && root.closest && root.closest('#wb-sheet-body'));
      if (hasSheetBookingUi0 && inSheetContext0) {
        selectedCard =
          document.querySelector('#wb-event-list .wb-all__event-card.is-selected') ||
          document.querySelector('.wb-all__event-card.is-selected');
      }
    }

    var variantBtns = root.querySelectorAll('.wb-variant-btn');
    var workshopId = (root.dataset && root.dataset.productId) ? String(root.dataset.productId) : '';

    if (!selectedCard) {
      for (var i = 0; i < variantBtns.length; i++) {
        var btn0 = variantBtns[i];
        var base0 = btn0.dataset.basePrice ? parseInt(btn0.dataset.basePrice, 10) : 0;
        if (base0 != null && !Number.isNaN(base0) && base0 < 0) base0 = 0;
        var priceEl0 = btn0.querySelector('[data-variant-price]');
        if (priceEl0) priceEl0.textContent = formatMoney(base0);
        var compareEl0 = btn0.querySelector('[data-variant-compare-price]');
        var row0 = btn0.querySelector('[data-variant-price-row]');
        if (compareEl0) {
          compareEl0.textContent = '';
          compareEl0.style.display = 'none';
        }
        if (row0) row0.classList.remove('wb-variant-prices--on-sale');
      }
      return;
    }

    var eventId = (selectedCard.dataset.eventMetaobjectGid || selectedCard.dataset.eventId || '').toString().trim();
    var state = selectedCard.dataset.state || '';
    var city = selectedCard.dataset.city || '';
    var cityKey = (selectedCard.dataset.cityKey || '').toString().trim();
    var venueName = (selectedCard.dataset.venueName || '').toString().trim();
    var venueHandle = (selectedCard.dataset.venueHandle || '').toString().trim();

    for (var j = 0; j < variantBtns.length; j++) {
      var btn = variantBtns[j];
      var baseCents = btn.dataset.basePrice ? parseInt(btn.dataset.basePrice, 10) : null;
      if (baseCents == null || Number.isNaN(baseCents)) baseCents = 0;
      var variantId = (btn.dataset.variantId || '').toString().trim();

      var resolved = resolvePriceCents({
        basePriceCents: baseCents,
        eventId: eventId,
        state: state,
        city: city,
        cityKey: cityKey,
        venueName: venueName,
        venueHandle: venueHandle,
        variantId: variantId,
        workshopId: workshopId
      });

      if (typeof resolved !== 'number' || !Number.isFinite(resolved) || resolved <= 0) resolved = baseCents;

      if (WB_PRICE_DEBUG && j === 0) {
        try {
          console.log('[WB] updateAllVariantCardPrices sample', {
            eventId: eventId,
            state: state,
            city: city,
            venueName: venueName,
            variantId: variantId,
            baseCents: baseCents,
            resolvedCents: resolved
          });
        } catch (_) {}
      }

      var priceEl = btn.querySelector('[data-variant-price]');
      if (priceEl) priceEl.textContent = formatMoney(resolved);
      var card = btn;
      var resolvedCents = resolved;
      var basePriceCents = baseCents;
      var compareEl = card.querySelector('[data-variant-compare-price]');
      var priceRow = card.querySelector('[data-variant-price-row]');
      if (compareEl) {
        if (resolvedCents < basePriceCents) {
          compareEl.textContent = formatMoney(basePriceCents);
          compareEl.style.display = 'block';
          if (priceRow) priceRow.classList.add('wb-variant-prices--on-sale');
        } else {
          compareEl.textContent = '';
          compareEl.style.display = 'none';
          if (priceRow) priceRow.classList.remove('wb-variant-prices--on-sale');
        }
      }
    }
  }

  /** Extract numeric variant ID from GID (gid://shopify/ProductVariant/ID) or return as-is if already numeric. */
  function parseVariantIdFromGid(val) {
    if (val == null || val === '') return null;
    const s = String(val).trim();
    const gidMatch = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/i.exec(s);
    if (gidMatch) return gidMatch[1];
    if (/^\d+$/.test(s)) return s;
    return s;
  }

  /**
   * Sync booking variant selection with the product media gallery (left side).
   * 1) Prefer theme.Variants path: set [data-variant-input] radios from variant JSON, dispatch change.
   * 2) Update master select / hidden id fields (theme uses select[name="id"]).
   * 3) Optional: click media thumbnail when data-variant-media-id is present (fallback).
   */
  function motionTriggerVariantById(variantId) {
    try {
      if (!variantId || !booking) return;
      var vid = String(parseVariantIdFromGid(variantId) || variantId).trim();
      if (!vid) return;

      var sectionId = (booking.dataset && booking.dataset.wbSectionId) ? String(booking.dataset.wbSectionId) : '';
      var sectionRoot = sectionId ? document.getElementById('shopify-section-' + sectionId) : null;
      if (sectionRoot && sectionRoot.closest && sectionRoot.closest('#wb-sheet-body')) sectionRoot = null;
      if (!sectionRoot && booking.closest) {
        var c = booking.closest('[id^="shopify-section-"]');
        if (c && (!c.closest || !c.closest('#wb-sheet-body'))) sectionRoot = c;
      }
      if (!sectionRoot) return;

      var activeBtn = booking.querySelector('.wb-variant-btn[data-variant-id="' + CSS.escape(String(vid)) + '"]')
        || booking.querySelector('.wb-variant-btn[data-variant-id="' + CSS.escape(String(variantId)) + '"]')
        || booking.querySelector('.wb-variant-btn.is-active');
      var mediaId = activeBtn ? String(activeBtn.getAttribute('data-variant-media-id') || '').trim() : '';

      var firedVariantInputs = false;
      var variantJsonEl = sectionRoot.querySelector('[data-variant-json]');
      if (variantJsonEl) {
        var variants;
        try {
          variants = JSON.parse(variantJsonEl.textContent || '[]');
        } catch (e) {
          variants = [];
        }
        var vMatch = null;
        for (var vi = 0; vi < variants.length; vi++) {
          if (String(variants[vi].id) === vid) {
            vMatch = variants[vi];
            break;
          }
        }
        if (vMatch) {
          var optKeys = ['option1', 'option2', 'option3'];
          var lastChecked = null;
          for (var oi = 0; oi < optKeys.length; oi++) {
            var ok = optKeys[oi];
            var oval = vMatch[ok];
            if (oval == null || String(oval).trim() === '') continue;
            var radios = sectionRoot.querySelectorAll('input[type="radio"][data-variant-input][data-index="' + ok + '"]');
            if (!radios || !radios.length) continue;
            radios.forEach(function (inp) {
              var isMatch = String(inp.value) === String(oval);
              inp.checked = isMatch;
              if (isMatch) lastChecked = inp;
            });
          }
          if (lastChecked) {
            lastChecked.dispatchEvent(new Event('change', { bubbles: true }));
            firedVariantInputs = true;
          }
        }
      }

      sectionRoot.querySelectorAll('form[id*="product-form-"] input[name="id"], form[id*="product-form-"] select[name="id"]').forEach(function (el) {
        if (!el) return;
        if (String(el.value) !== vid) el.value = vid;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      });

      var masterSelect = sectionRoot.querySelector('select[data-product-select], form.product-single__form select[name="id"]');
      if (masterSelect && String(masterSelect.value) !== vid) {
        masterSelect.value = vid;
        masterSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (firedVariantInputs || !mediaId) return;

      var mediaSelector = sectionId
        ? '[data-media-id="' + sectionId + '-' + mediaId + '"], #postion-image-' + sectionId + '-' + mediaId
        : '[data-media-id$="-' + mediaId + '"], [id$="-' + mediaId + '"]';
      var mediaEl = sectionRoot.querySelector(mediaSelector);
      if (!mediaEl) return;

      var clickTarget = mediaEl.querySelector('button, a, img') || mediaEl;
      if (clickTarget && clickTarget.dispatchEvent) {
        clickTarget.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
      }
      mediaEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    } catch (e) {
      if (window.__WB_DIAG__) console.warn('[WB] motionTriggerVariantById sync failed', e);
    }
  }

  // REMOVED: Webhook-based capacity checks (commented out)
  // const CAPACITY_CHECK_URL = 'https://workshop-capacity-webhook-production.up.railway.app/capacity/check';
  // async function checkWorkshopCapacity(eventId, requestedQuantity) { ... }
  // async function fetchEventCapacity(eventId, requestedQuantity = 1) { ... }
  // async function validateQuantityAgainstCapacity(quantity) { ... }

  /** Collect all keys containing "capacity" from an object (dataset or event payload). */
  function pickCapacityFields(obj) {
    if (!obj || typeof obj !== 'object') return {};
    var out = {};
    try {
      for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && /capacity/i.test(key)) {
          out[key] = obj[key];
        }
      }
    } catch (e) {}
    return out;
  }

  // Helper: Get remaining capacity from event card (metaobject-based, no webhook).
  // Desktop / venue cards (.workshop-event-card): missing or invalid capacity → 0 (unchanged legacy behavior).
  // Mobile "all workshops" list cards (.wb-all__event-card): missing/invalid → null so UI is not falsely sold-out.
  function getRemainingCapacityFromCard(card) {
    if (!card) return 0;

    var isWbAllList = card.classList && card.classList.contains('wb-all__event-card');

    const raw = card.dataset.capacityRemaining;
    if (raw == null || String(raw).trim() === '') {
      if (isWbAllList) {
        if (window.__WB_DIAG__) console.warn('[CAPACITY] Missing capacity on wb-all card — not blocking UI');
        return null;
      }
      return 0;
    }

    const capacity = Number(raw);

    if (!Number.isFinite(capacity) || capacity < 0) {
      if (window.__WB_DIAG__) console.warn('[CAPACITY] Invalid capacity:', raw);
      if (isWbAllList) return null;
      return 0;
    }

    return capacity;
  }

  function wbRenderCapacityBadge(rootEl, remaining, total) {
    try {
      var badge = rootEl && rootEl.querySelector ? rootEl.querySelector('[data-wb-capacity-badge]') : null;
      if (!badge) {
        badge = document.querySelector('#wb-sheet.wb-all__sheet--mobile-compact [data-wb-capacity-badge][data-wb-hoisted-header]');
      }
      if (!badge) return;

      var labelEl = badge.querySelector('.wb-capacity-label');
      var numEl = badge.querySelector('.wb-capacity-num');
      var pctEl = badge.querySelector('.wb-capacity-pct');
      if (numEl) {
        var remStr = '—';
        if (remaining != null && String(remaining).trim() !== '') {
          var rn = Number(remaining);
          if (Number.isFinite(rn)) remStr = String(rn);
        }
        var totStr = (total != null && total !== '' && Number(total) > 0) ? String(total) : '—';
        numEl.textContent = remStr + ' / ' + totStr;
      }

      var prog = badge.querySelector('.wb-capacity-progress');
      if (!prog) return;

      var rem = remaining == null || String(remaining).trim() === '' ? NaN : Number(remaining);
      var tot = Number(total);

      if (Number.isFinite(rem) && Number.isFinite(tot) && tot > 0) {
        var pct = (rem / tot) * 100;
        if (pct < 0) pct = 0;
        if (pct > 100) pct = 100;
        var pctRound = Math.round(pct);
        if (pctEl) pctEl.textContent = String(pctRound) + '%';

        prog.style.strokeDasharray = '100 100';
        prog.style.strokeDashoffset = String(100 - pct);
        if (pct > 66) {
          badge.setAttribute('data-capacity-state', 'high');
          if (labelEl) labelEl.textContent = 'Plenty';
          prog.style.stroke = '#1fbf6a';
        } else if (pct > 33) {
          badge.setAttribute('data-capacity-state', 'medium');
          if (labelEl) labelEl.textContent = 'Limited';
          prog.style.stroke = '#f4b400';
        } else {
          var soldOut = pct <= 0;
          badge.setAttribute('data-capacity-state', soldOut ? 'soldout' : 'low');
          if (labelEl) labelEl.textContent = soldOut ? 'Sold out' : 'Almost full';
          prog.style.stroke = '#e53935';
        }
      } else {
        badge.setAttribute('data-capacity-state', 'unknown');
        if (labelEl) labelEl.textContent = 'Capacity';
        if (pctEl) pctEl.textContent = '--%';
        prog.style.strokeDasharray = '100 100';
        prog.style.strokeDashoffset = '100';
        prog.style.stroke = 'rgba(0,0,0,0.2)';
      }
    } catch (e) {}
  }

  function wbOpenStickyPanel(bookingBox) {
    try {
      if (!bookingBox) return;
      var root =
        bookingBox.closest('[data-ai-wb-sticky]') ||
        bookingBox.closest('[data-wb-panel]') ||
        document.querySelector('[data-ai-wb-sticky]');
      if (!root) return;
      var toggle = root.querySelector('[data-ai-wb-sticky-toggle]');
      var body = root.querySelector('[data-ai-wb-sticky-body]');
      if (toggle) toggle.setAttribute('aria-expanded', 'true');
      root.classList.add('wb-sticky-open');
      root.classList.remove('wb-panel--collapsed');
      root.classList.add('wb-panel--expanded');
      if (body) {
        body.style.maxHeight = '';
        body.style.overflow = '';
        body.classList.remove('pdp-wb-hidden');
      }
    } catch (e) {}
  }

  // Validate seat selection (soft check using metaobject capacity)
  function validateSeatSelection() {
    var sheetBodyEl = document.getElementById('wb-sheet-body');
    var hasSheetBookingUi = sheetBodyEl && sheetBodyEl.querySelector('.pdp-workshop-booking');
    /**
     * "Book all" list + bottom sheet: use wb-all card when THIS init's booking lives in #wb-sheet-body.
     * Do not use sheetBookingInDom === booking (reference can differ across inits); do not require overlay class.
     */
    var inSheetContext = !!(booking && booking.closest && booking.closest('#wb-sheet-body'));
    var listSel =
      hasSheetBookingUi && inSheetContext
        ? document.querySelector('#wb-event-list .wb-all__event-card.is-selected') ||
          document.querySelector('.wb-all__event-card.is-selected')
        : null;
    const selectedCard = document.querySelector('.workshop-event-card.is-selected') || listSel;
    if (!selectedCard) {
      // No selection - disable buttons and hide warning
      const warningEl = bookingBox ? bookingBox.querySelector('[data-capacity-warning]') : null;
      const addBtn = bookingBox ? bookingBox.querySelector('[data-add-to-cart]') : null;
      const buyBtn = bookingBox ? bookingBox.querySelector('[data-book-now]') : null;
      if (warningEl) warningEl.style.display = 'none';
      if (addBtn) addBtn.disabled = true;
      if (buyBtn) buyBtn.disabled = true;
      return;
    }

    const variantId = (selectedCard.dataset.variantId || '').trim();
    if (!wbIsEventBookable(selectedCard)) {
      const warningEl = bookingBox ? bookingBox.querySelector('[data-capacity-warning]') : null;
      const addBtn = bookingBox ? bookingBox.querySelector('[data-add-to-cart]') : null;
      const buyBtn = bookingBox ? bookingBox.querySelector('[data-book-now]') : null;
      if (warningEl) {
        warningEl.textContent = 'This workshop is sold out.';
        warningEl.style.display = 'block';
      }
      if (addBtn) { addBtn.disabled = true; addBtn.style.cursor = 'not-allowed'; }
      if (buyBtn) { buyBtn.disabled = true; buyBtn.style.cursor = 'not-allowed'; }
      return;
    }
    const notBookable = selectedCard.getAttribute('data-not-bookable') === '1' || variantId === 'none';
    if (notBookable) {
      const warningEl = bookingBox ? bookingBox.querySelector('[data-capacity-warning]') : null;
      const addBtn = bookingBox ? bookingBox.querySelector('[data-add-to-cart]') : null;
      const buyBtn = bookingBox ? bookingBox.querySelector('[data-book-now]') : null;
      if (warningEl) {
        warningEl.textContent = 'Not bookable (missing variants).';
        warningEl.style.display = 'block';
      }
      if (addBtn) { addBtn.disabled = true; addBtn.style.cursor = 'not-allowed'; }
      if (buyBtn) { buyBtn.disabled = true; buyBtn.style.cursor = 'not-allowed'; }
      return;
    }

    const remainingCapacity = getRemainingCapacityFromCard(selectedCard);

    const qtyInput = bookingBox ? bookingBox.querySelector('[data-workshop-quantity]') : null;
    const requested = Math.max(1, Number((qtyInput && qtyInput.value) || 1));

    const warningEl = bookingBox ? bookingBox.querySelector('[data-capacity-warning]') : null;
    const addBtn = bookingBox ? bookingBox.querySelector('[data-add-to-cart]') : null;
    const buyBtn = bookingBox ? bookingBox.querySelector('[data-book-now]') : null;

    if (remainingCapacity != null && remainingCapacity <= 0) {
      if (warningEl) {
        warningEl.textContent = 'This workshop is sold out.';
        warningEl.style.display = 'block';
      }
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.style.backgroundColor = '#e0e0e0';
        addBtn.style.color = '#9e9e9e';
        addBtn.style.cursor = 'not-allowed';
      }
      if (buyBtn) {
        buyBtn.disabled = true;
        buyBtn.style.backgroundColor = '#e0e0e0';
        buyBtn.style.color = '#9e9e9e';
        buyBtn.style.cursor = 'not-allowed';
      }
      return;
    }

    if (remainingCapacity != null && requested > remainingCapacity) {
      if (warningEl) {
        warningEl.textContent = `This workshop has only ${remainingCapacity} seats remaining. You can't add more seats.`;
        warningEl.style.display = 'block';
      }
      if (qtyInput) qtyInput.value = String(remainingCapacity);
      if (addBtn) {
        addBtn.disabled = true;
        addBtn.style.backgroundColor = '#e0e0e0';
        addBtn.style.color = '#9e9e9e';
        addBtn.style.cursor = 'not-allowed';
      }
      if (buyBtn) {
        buyBtn.disabled = true;
        buyBtn.style.backgroundColor = '#e0e0e0';
        buyBtn.style.color = '#9e9e9e';
        buyBtn.style.cursor = 'not-allowed';
      }
      return;
    }

    if (variantSelect && !(variantSelect.value || '').toString().trim()) {
      ensureVariantUiSyncedFromSelect();
    }

    // Valid selection - enable buttons
    if (bookingBox) bookingBox.classList.remove('wb-no-event-selected');
    if (warningEl) warningEl.style.display = 'none';
    if (addBtn) {
      addBtn.disabled = false;
      addBtn.style.backgroundColor = '#1976d2';
      addBtn.style.color = '#ffffff';
      addBtn.style.cursor = 'pointer';
    }
    if (buyBtn) {
      buyBtn.disabled = false;
      buyBtn.style.backgroundColor = '#ffd700';
      buyBtn.style.color = '#000000';
      buyBtn.style.cursor = 'pointer';
    }
  }

  /** AUDIT: Writes bottom-left resolved price to [data-last-price]. Inputs: card (event), getSelectedVariantPriceCents(), getSelectedVariantId(), booking.dataset.productId. Uses resolvePriceCents(); invalid/<=0 shows "—". */
  function showPreviewPrice(card) {
    const lp =
      (booking && booking.querySelector('[data-last-price]')) ||
      (bookingBox && bookingBox.querySelector('[data-last-price]')) ||
      document.querySelector('[data-last-price]');
    if (!lp) return;

    try {
      // Get all required data for price resolution (same as Add to Cart)
      const basePriceCents = getSelectedVariantPriceCents();
      if (!basePriceCents || Number.isNaN(basePriceCents)) {
        setLastPriceElDisplay(lp, null, null);
        return;
      }

      const eventId = (card.dataset.eventMetaobjectGid || card.dataset.eventId || '').toString().trim();
      const state = card.dataset.state || '';
      const city = card.dataset.city || '';
      const cityKey = (card.dataset.cityKey || '').toString().trim();
      const venueName = (card.dataset.venueName || '').toString().trim();
      const venueHandle = (card.dataset.venueHandle || '').toString().trim();
      const variantId = getSelectedVariantId();
      const workshopId = booking?.dataset?.productId;

      // Use the SAME resolvePriceCents() logic as Add to Cart
      const resolvedCents = resolvePriceCents({
        basePriceCents: basePriceCents,
        eventId: eventId,
        state: state,
        city: city,
        cityKey: cityKey,
        venueName: venueName,
        venueHandle: venueHandle,
        variantId: variantId,
        workshopId: workshopId,
      });

      // Display the resolved price (UI-only, no state/dataset writes)
      if (resolvedCents && Number.isFinite(resolvedCents) && resolvedCents > 0) {
        setLastPriceElDisplay(lp, basePriceCents, resolvedCents);
      } else {
        setLastPriceElDisplay(lp, null, null);
      }
    } catch (err) {
      if (window.__WB_DIAG__) console.warn('Workshop: Preview price calculation failed', err);
      setLastPriceElDisplay(lp, null, null);
    }
  }

  function buildSelectionDetailFromCard(card) {
    if (!card) return null;
    var basePriceCents = getSelectedVariantPriceCents();
    if (basePriceCents == null || Number.isNaN(basePriceCents)) return null;
    var eventId = (card.dataset.eventMetaobjectGid || card.dataset.eventId || '').toString().trim();
    var state = card.dataset.state || '';
    var city = card.dataset.city || '';
    var cityKey = (card.dataset.cityKey || '').toString().trim();
    var venueName = (card.dataset.venueName || '').trim();
    var venueHandle = (card.dataset.venueHandle || '').toString().trim();
    var variantId = getSelectedVariantId();
    var workshopId = booking && booking.dataset && booking.dataset.productId;
    var resolvedCents = resolvePriceCents({
      basePriceCents: basePriceCents,
      eventId: eventId,
      state: state,
      city: city,
      cityKey: cityKey,
      venueName: venueName,
      venueHandle: venueHandle,
      variantId: variantId,
      workshopId: workshopId
    });
    var activeBtn = booking && booking.querySelector('.wb-variant-btn.is-active');
    var variantImgEl = activeBtn && activeBtn.querySelector('.wb-variant-img img');
    var productImgEl = document.querySelector('[data-product-image-main] img');
    return {
      product_handle: (booking && booking.dataset && booking.dataset.productHandle) || (card.dataset.productHandle || '').trim(),
      product_image: (booking && booking.getAttribute('data-product-image-url')) || (productImgEl && (productImgEl.currentSrc || productImgEl.src)) || null,
      variant_id: variantId,
      variant_title: (activeBtn && activeBtn.dataset.variantTitle) || '',
      variant_image: (variantImgEl && (variantImgEl.currentSrc || variantImgEl.src)) || null,
      price_cents: (resolvedCents && Number.isFinite(resolvedCents) && resolvedCents > 0) ? resolvedCents : null,
      currency: (typeof window !== 'undefined' && window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null,
      venue_name: venueName,
      city_label: city,
      date_label: (card.dataset.dateLabel || '').trim(),
      time_label: (card.dataset.timeLabel || '').trim(),
      starts_at_iso: (card.dataset.startsAt || '').trim() || null
    };
  }

  function wbDispatchSelection(detail) {
    try {
      window.__wbSelection = detail;
      document.dispatchEvent(new CustomEvent('wb:selection', { detail: detail || {} }));
    } catch (e) {}
  }

  function wbApplyEventsStepHeading(headerEl, stepNum, title, hint) {
    if (!headerEl) return;
    var titleEl = headerEl.querySelector('[data-wb-step-title]');
    var hintEl = headerEl.querySelector('[data-wb-step-hint]');
    if (!titleEl && !hintEl) {
      headerEl.textContent = title || '';
      return;
    }
    if (titleEl) titleEl.textContent = title || '';
    if (hintEl) {
      var h = hint != null && String(hint).trim() ? String(hint).trim() : '';
      hintEl.textContent = h;
      hintEl.style.display = h ? '' : 'none';
    }
  }

  function wbGetFirstBookableVenueCardInOpenGroup(root) {
    if (!root) return null;
    var groups = root.querySelectorAll('.wb-venue-group');
    for (var i = 0; i < groups.length; i++) {
      var t = groups[i].querySelector('[data-workshop-group-toggle]');
      if (!t || t.getAttribute('aria-expanded') !== 'true') continue;
      if (wbLockedVenueRefId) {
        var lockedCards = groups[i].querySelectorAll('.wb-venue-card');
        for (var lk = 0; lk < lockedCards.length; lk++) {
          if (String(lockedCards[lk].getAttribute('data-venue-ref-id') || '').trim() !== wbLockedVenueRefId) continue;
          if (lockedCards[lk].querySelector('[data-workshop-venue-date]')) return lockedCards[lk];
        }
      }
      var cards = groups[i].querySelectorAll('.wb-venue-card');
      for (var j = 0; j < cards.length; j++) {
        if (cards[j].querySelector('[data-workshop-venue-date]')) return cards[j];
      }
    }
    return null;
  }

  function wbLockVenueContextFromCard(venueCard) {
    if (!venueCard) return;
    var refId = String(venueCard.getAttribute('data-venue-ref-id') || '').trim();
    if (refId) wbLockedVenueRefId = refId;
    var group = venueCard.closest ? venueCard.closest('.wb-venue-group') : null;
    if (!group) return;
    var groupKey = String(group.getAttribute('data-workshop-venue-group') || '').trim();
    if (groupKey) wbLockedVenueGroupKey = groupKey;
  }

  function wbRestoreVenueContext() {
    if (!eventsRoot) return;
    var targetGroup = null;
    if (wbLockedVenueGroupKey) {
      try {
        if (typeof CSS !== 'undefined' && CSS.escape) {
          targetGroup = eventsRoot.querySelector('.wb-venue-group[data-workshop-venue-group="' + CSS.escape(wbLockedVenueGroupKey) + '"]');
        }
      } catch (_) {}
      if (!targetGroup) {
        var allGroups = eventsRoot.querySelectorAll('.wb-venue-group');
        for (var gi = 0; gi < allGroups.length; gi++) {
          if (String(allGroups[gi].getAttribute('data-workshop-venue-group') || '').trim() === wbLockedVenueGroupKey) {
            targetGroup = allGroups[gi];
            break;
          }
        }
      }
    }
    if (!targetGroup && wbLockedVenueRefId) {
      var cards = eventsRoot.querySelectorAll('.wb-venue-card');
      for (var ci = 0; ci < cards.length; ci++) {
        if (String(cards[ci].getAttribute('data-venue-ref-id') || '').trim() !== wbLockedVenueRefId) continue;
        targetGroup = cards[ci].closest ? cards[ci].closest('.wb-venue-group') : null;
        if (targetGroup) break;
      }
    }
    if (!targetGroup) return;
    var header = targetGroup.querySelector('[data-workshop-group-toggle]') || targetGroup.querySelector('.wb-group-toggle');
    var body = targetGroup.querySelector('.wb-group-body');
    if (header && body && header.getAttribute('aria-expanded') !== 'true') {
      wbCloseAllVenueCardsExcept(eventsRoot, targetGroup);
      wbExpandBody(body, header);
    }
  }

  function wbRefreshBookingGuideUi() {
    function wbUpdateCollapsedPanelSelectionSummary() {
      var selectedCard = eventsRoot ? eventsRoot.querySelector('.workshop-event-card.is-selected') : null;
      if (!selectedCard) return false;
      var venueText = String(selectedCard.getAttribute('data-venue-name') || '').trim();
      var dateText = String(selectedCard.getAttribute('data-date-label') || '').trim() ||
        wstFormatDateKey(selectedCard.getAttribute('data-date-key') || '');
      var timeText = String(selectedCard.getAttribute('data-time-label') || '').trim();
      var variantText = String(wstGetActiveVariantName() || '').trim();
      var priceText = '';
      if (lastPriceEl) {
        var currentPriceEl = lastPriceEl.querySelector('[data-last-price-current]');
        priceText = normalize(currentPriceEl ? currentPriceEl.textContent : lastPriceEl.textContent).replace(/\s+/g, ' ').trim();
        if (priceText === '—' || priceText === '-') priceText = '';
      }

      var isMobile = false;
      try {
        isMobile = !!(window && window.matchMedia && window.matchMedia('(max-width: 767px)').matches);
      } catch (_) {}

      // Mobile header cleanup: hide venue + variant + price in collapsed header summary.
      // Keep date/time only (expanded summary below still shows address/datetime).
      var lineOne = (isMobile ? [dateText, timeText] : [venueText, dateText, timeText]).filter(Boolean).join(' · ').trim();
      var lineTwo = (isMobile ? [] : [variantText, priceText]).filter(Boolean).join(' · ').trim();

      if (panelTitle) panelTitle.textContent = lineOne || 'Session selected';
      if (panelHintEl) {
        panelHintEl.textContent = lineTwo;
        if (lineTwo) {
          panelHintEl.classList.remove('pdp-wb-hidden');
          panelHintEl.setAttribute('aria-hidden', 'false');
        } else {
          panelHintEl.classList.add('pdp-wb-hidden');
          panelHintEl.setAttribute('aria-hidden', 'true');
        }
      }
      return true;
    }

    var headerEl = booking.querySelector('[data-available-events-header]');
    var variantGuide = bookingBox ? bookingBox.querySelector('[data-wb-variant-guide]') : null;
    var selectedEventCard = eventsRoot ? eventsRoot.querySelector('.workshop-event-card.is-selected') : null;
    var hasEventSelected =
      !!selectedEventCard ||
      !!(selectedEventIdInput && String(selectedEventIdInput.value || '').trim());
    if (variantGuide) {
      if (hasEventSelected) {
        variantGuide.classList.remove('pdp-wb-hidden');
        variantGuide.setAttribute('aria-hidden', 'false');
      } else {
        variantGuide.classList.add('pdp-wb-hidden');
        variantGuide.setAttribute('aria-hidden', 'true');
      }
    }

    if (booking && booking.classList && booking.classList.contains('wb-wizard')) {
      var flatHeaderEl = booking.querySelector('[data-available-events-header]');
      if (flatHeaderEl) {
        wbApplyEventsStepHeading(flatHeaderEl, 1, 'Choose your Venue', '');
      }
      return;
    }

    var missingVenueShell = !eventsRoot || !eventsRoot.querySelector('.wb-venue-group');
    if (!useLocationFirst || missingVenueShell) {
      var legacyHasStructure = !!(headerEl && headerEl.querySelector && headerEl.querySelector('[data-wb-step-title]'));
      if (legacyHasStructure) {
        if (!hasEventSelected) {
          wbApplyEventsStepHeading(headerEl, 1, 'Choose your session', 'Use the filters or list above to find a time slot.');
          if (panelTitle) panelTitle.textContent = 'Book your workshop';
          if (panelHintEl) {
            panelHintEl.textContent = 'Complete the steps above to pick a time slot.';
            panelHintEl.classList.remove('pdp-wb-hidden');
            panelHintEl.setAttribute('aria-hidden', 'false');
          }
        } else {
          wbApplyEventsStepHeading(
            headerEl,
            4,
            '',
            ''
          );
          if (!wbUpdateCollapsedPanelSelectionSummary()) {
            if (panelTitle) panelTitle.textContent = 'Workshop selected';
            if (panelHintEl) {
              panelHintEl.textContent = '';
              panelHintEl.classList.add('pdp-wb-hidden');
              panelHintEl.setAttribute('aria-hidden', 'true');
            }
          }
        }
      }
      if (bookingHint) {
        if (!hasEventSelected) {
          bookingHint.classList.remove('pdp-wb-hidden');
          bookingHint.textContent = 'Select a venue and session to see details';
        } else {
          bookingHint.classList.add('pdp-wb-hidden');
        }
      }
      return;
    }

    if (hasEventSelected) {
      wbApplyEventsStepHeading(
        headerEl,
        4,
        'Choose a Venue',
        'Venues are shown for your location. Open one below to review or change your session.'
      );
      if (!wbUpdateCollapsedPanelSelectionSummary()) {
        if (panelTitle) panelTitle.textContent = 'Workshop selected';
        if (panelHintEl) {
          panelHintEl.textContent = '';
          panelHintEl.classList.add('pdp-wb-hidden');
          panelHintEl.setAttribute('aria-hidden', 'true');
        }
      }
      if (bookingHint) bookingHint.classList.add('pdp-wb-hidden');
      return;
    }

    var openToggle = eventsRoot.querySelector('[data-workshop-group-toggle][aria-expanded="true"]');
    if (!openToggle) {
      wbApplyEventsStepHeading(
        headerEl,
        1,
        'Choose a Venue',
        'Venues are shown for your location. Open one below for sessions or waitlist.'
      );
      if (panelTitle) panelTitle.textContent = 'Book your workshop';
      if (panelHintEl) {
        panelHintEl.textContent = 'Expand a venue below to see times or waitlist.';
        panelHintEl.classList.remove('pdp-wb-hidden');
        panelHintEl.setAttribute('aria-hidden', 'false');
      }
      if (bookingHint) {
        bookingHint.classList.remove('pdp-wb-hidden');
        bookingHint.textContent = 'Select a venue and session to see details';
      }
      return;
    }

    var vCard = wbGetFirstBookableVenueCardInOpenGroup(eventsRoot);
    if (!vCard) {
      wbApplyEventsStepHeading(headerEl, 2, 'Next steps', 'Try another venue or use the waitlist if no session dates are open yet.');
      if (panelTitle) panelTitle.textContent = 'Book your workshop';
      if (panelHintEl) {
        panelHintEl.textContent = 'No bookable sessions at these venues yet.';
        panelHintEl.classList.remove('pdp-wb-hidden');
        panelHintEl.setAttribute('aria-hidden', 'false');
      }
      if (bookingHint) {
        bookingHint.classList.remove('pdp-wb-hidden');
        bookingHint.textContent = 'Select a venue and session to see details';
      }
      return;
    }

    var dateTouched = vCard.getAttribute('data-wb-date-touched') === '1';
    if (!dateTouched) {
      wbApplyEventsStepHeading(headerEl, 2, 'Choose your date&session', 'Confirm the day, then tap an available start time.');
      if (panelTitle) panelTitle.textContent = 'Pick a session';
      if (panelHintEl) {
        panelHintEl.textContent = 'Select a date and start time on the venue card above.';
        panelHintEl.classList.remove('pdp-wb-hidden');
        panelHintEl.setAttribute('aria-hidden', 'false');
      }
    } else {
      wbApplyEventsStepHeading(headerEl, 3, 'Choose your time', 'Tap an available start time to lock in your session.');
      if (panelTitle) panelTitle.textContent = 'Pick a time slot';
      if (panelHintEl) {
        panelHintEl.textContent = 'Times appear under the date you selected.';
        panelHintEl.classList.remove('pdp-wb-hidden');
        panelHintEl.setAttribute('aria-hidden', 'false');
      }
    }
    if (bookingHint) {
      bookingHint.classList.remove('pdp-wb-hidden');
      bookingHint.textContent = 'Select a venue and session to see details';
    }
  }

  function clearEventSelection() {
    if (eventsRoot) {
      eventsRoot.querySelectorAll('.workshop-event-card.is-selected').forEach((el) => {
        el.classList.remove('is-selected');
        el.style.boxShadow = '';
        el.style.transform = '';
      });
    }
    if (selectedEventIdInput) selectedEventIdInput.value = '';
    if (bookingBox) bookingBox.classList.add('wb-no-event-selected');
    if (bookingHint) bookingHint.classList.remove('pdp-wb-hidden');
    if (selectedSummary) selectedSummary.classList.add('pdp-wb-hidden');
    if (bookNowBtn) {
      bookNowBtn.disabled = true;
      bookNowBtn.style.backgroundColor = '#e0e0e0';
      bookNowBtn.style.color = '#9e9e9e';
      bookNowBtn.style.cursor = 'not-allowed';
    }
    if (addToCartBtn) {
      addToCartBtn.disabled = true;
      addToCartBtn.style.backgroundColor = '#e0e0e0';
      addToCartBtn.style.color = '#9e9e9e';
      addToCartBtn.style.cursor = 'not-allowed';
    }
    if (summaryStatus) { summaryStatus.textContent = ''; summaryStatus.classList.add('pdp-wb-hidden'); }
    // Clear global selection state and capacity
    window.__WORKSHOP_SELECTION__ = null;
    window.__WORKSHOP_CAPACITY__ = null;
    wbDispatchSelection({});
    var crossHost = booking && booking.querySelector('#wb-cross-product-booking');
    if (crossHost) { crossHost.innerHTML = ''; crossHost.style.display = 'none'; }
    var old = booking && booking.querySelector('[data-wb-also-available]');
    if (old) old.remove();
    // Hide capacity warning and helper text
    const warningEl = bookingBox ? bookingBox.querySelector('[data-workshop-capacity-warning]') : null;
    const seatsAvailableEl = bookingBox ? bookingBox.querySelector('[data-workshop-seats-available]') : null;
    if (warningEl) {
      warningEl.classList.add('hidden');
    }
    if (seatsAvailableEl) {
      seatsAvailableEl.classList.add('hidden');
    }
    
    // Reset quantity input max attribute
    const quantityInput = bookingBox ? bookingBox.querySelector('[data-workshop-quantity]') : null;
    if (quantityInput) {
      quantityInput.removeAttribute('max');
    }
    applyVariantCardVisibility(null);
    window.__WB_URL_EVENT_ID_NUMERIC__ = '';
    updateUrlForEventId(null);
    try {
      wbRefreshBookingGuideUi();
    } catch (_) {}
    wstUpdate(1, {});
  }

  /**
   * Get set of allowed variant IDs (numeric string) from event card.
   * Uses card._wbEv.available_variants or data-available-variants. Falls back to single variant_id if no list.
   * Returns null to mean "show all".
   */
  function getAvailableVariantIdsFromCard(card) {
    if (!card) return null;
    var raw = null;
    if (card._wbEv && Array.isArray(card._wbEv.available_variants) && card._wbEv.available_variants.length > 0) {
      raw = card._wbEv.available_variants;
    } else if (card._wbEv && card._wbEv.available_variants) {
      raw = Array.isArray(card._wbEv.available_variants) ? card._wbEv.available_variants : [card._wbEv.available_variants];
    }
    if (!raw && (card.getAttribute('data-available-variants') || (card.dataset && card.dataset.availableVariants))) {
      var attr = card.getAttribute('data-available-variants') || (card.dataset && card.dataset.availableVariants) || '';
      try { raw = JSON.parse(attr); } catch (e) { raw = null; }
    }
    if (raw && Array.isArray(raw) && raw.length > 0) {
      var set = new Set();
      for (var i = 0; i < raw.length; i++) {
        var it = raw[i];
        var id = null;
        if (it != null) {
          if (typeof it === 'number' || (typeof it === 'string' && /^\d+$/.test(String(it)))) id = String(it);
          else if (it.id != null) id = String(parseVariantIdFromGid(it.id) || it.id);
          else if (it.variant_id != null) id = String(parseVariantIdFromGid(it.variant_id) || it.variant_id);
        }
        if (id) set.add(id);
      }
      if (set.size) return set;
    }
    var singleVariantId = (card.dataset.variantId || card.dataset.variantGid || '').toString().trim();
    if (singleVariantId && singleVariantId !== 'none') {
      var one = parseVariantIdFromGid(singleVariantId);
      if (one) return new Set([String(one)]);
    }
    return null;
  }

  function applyVariantCardVisibility(allowedIds) {
    var btns = booking ? booking.querySelectorAll('.wb-variant-btn') : [];
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      var vid = (btn.dataset.variantId || '').toString().trim();
      var numericId = parseVariantIdFromGid(vid) || vid;
      var show = !allowedIds || allowedIds.has(vid) || allowedIds.has(String(numericId));
      if (show) {
        btn.classList.remove('wb-variant-unavailable');
        btn.removeAttribute('disabled');
        btn.style.display = '';
      } else {
        btn.classList.add('wb-variant-unavailable');
        btn.setAttribute('disabled', '');
        btn.style.display = '';
      }
      btn.setAttribute('aria-hidden', show ? 'false' : 'true');
    }
    if (allowedIds && variantSelect) {
      var activeBtn = booking && booking.querySelector('.wb-variant-btn.is-active');
      if (activeBtn && activeBtn.classList.contains('wb-variant-unavailable')) {
        var firstVisible = booking && Array.prototype.find.call(booking.querySelectorAll('.wb-variant-btn'), function (b) { return !b.classList.contains('wb-variant-unavailable'); });
        if (firstVisible) {
          var firstId = (firstVisible.dataset.variantId || '').toString().trim();
          if (firstId) {
            variantSelect.value = firstId;
            booking.querySelectorAll('.wb-variant-btn').forEach(function (b) {
              b.classList.toggle('is-active', b === firstVisible);
            });
            motionTriggerVariantById(firstId);
          }
        }
      }
    }
  }

  /** After programmatic variant changes, ensure hidden input + .is-active match a visible tile (no extra tap). */
  function ensureVariantUiSyncedFromSelect() {
    if (!booking || !variantSelect) return;
    function firstVisibleVariantBtn() {
      var btns = booking.querySelectorAll('.wb-variant-btn');
      for (var i = 0; i < btns.length; i++) {
        if (btns[i].style.display !== 'none') return btns[i];
      }
      return null;
    }
    var val = (variantSelect.value || '').toString().trim();
    if (!val) {
      var fv = firstVisibleVariantBtn();
      if (fv) {
        var fid = (fv.dataset.variantId || '').toString().trim();
        var parsed = parseVariantIdFromGid(fid) || fid;
        if (parsed) variantSelect.value = String(parsed);
        booking.querySelectorAll('.wb-variant-btn').forEach(function (b) {
          b.classList.toggle('is-active', b === fv);
        });
      }
      return;
    }
    if (booking.querySelector('.wb-variant-btn.is-active')) return;
    var btn = null;
    try {
      if (typeof CSS !== 'undefined' && CSS.escape) {
        btn = booking.querySelector('.wb-variant-btn[data-variant-id="' + CSS.escape(val) + '"]');
      }
    } catch (e) {}
    if (!btn) {
      var all = booking.querySelectorAll('.wb-variant-btn');
      for (var j = 0; j < all.length; j++) {
        var vv = (all[j].dataset.variantId || '').toString().trim();
        var nv = parseVariantIdFromGid(vv) || vv;
        if (String(nv) === String(val)) {
          btn = all[j];
          break;
        }
      }
    }
    if (btn && btn.style.display !== 'none') {
      booking.querySelectorAll('.wb-variant-btn').forEach(function (b) {
        b.classList.toggle('is-active', b === btn);
      });
      return;
    }
    var fv2 = firstVisibleVariantBtn();
    if (fv2) {
      var fid2 = (fv2.dataset.variantId || '').toString().trim();
      var p2 = parseVariantIdFromGid(fid2) || fid2;
      if (p2) variantSelect.value = String(p2);
      booking.querySelectorAll('.wb-variant-btn').forEach(function (b) {
        b.classList.toggle('is-active', b === fv2);
      });
    }
  }

  function showSoldOutItemWarning() {
    var warningEl = bookingBox ? bookingBox.querySelector('[data-capacity-warning]') : null;
    if (warningEl) warningEl.style.display = 'none';
    showSoldOutCapacityModal();
    if (summaryStatus) {
      summaryStatus.textContent = 'SOLD OUT';
      summaryStatus.classList.remove('pdp-wb-hidden');
    }
    if (eventsRoot) {
      eventsRoot.querySelectorAll('.workshop-event-card.is-selected').forEach(function (el) {
        el.classList.remove('is-selected');
      });
    }
    if (bookingBox) bookingBox.classList.add('wb-no-event-selected');
    var addBtn = bookingBox ? bookingBox.querySelector('[data-add-to-cart]') : null;
    var buyBtn = bookingBox ? bookingBox.querySelector('[data-book-now]') : null;
    if (addBtn) { addBtn.disabled = true; addBtn.style.cursor = 'not-allowed'; }
    if (buyBtn) { buyBtn.disabled = true; buyBtn.style.cursor = 'not-allowed'; }
  }

  function setEventSelectionFromCard(card) {
    if (!card) return;
    if (wbIsEventSoldOut(card) || !wbIsEventBookable(card)) return;
    var venueCardForLock = card.closest ? card.closest('.wb-venue-card') : null;
    if (venueCardForLock) wbLockVenueContextFromCard(venueCardForLock);

    if (eventsRoot) {
      eventsRoot.querySelectorAll('.workshop-event-card.is-selected').forEach((el) => {
        el.classList.remove('is-selected');
      });
    }
    card.classList.add('is-selected');

    const eventKey = (card.dataset.eventMetaobjectGid || card.dataset.eventId || '').toString().trim();
    const eventId = eventKey;
    const eventHandle = (card.dataset.eventHandle || '').trim();
    const city = card.dataset.city || '';
    const state = card.dataset.state || '';
    const venue = (card.dataset.venueName || '').trim();
    const eventName = (card.dataset.eventName || '').trim();
    const dateLabel = (card.dataset.dateLabel || '').trim();
    const timeLabel = card.dataset.timeLabel || '';
    var datetime = dateLabel && timeLabel ? `${dateLabel} ${timeLabel}`.trim() : (dateLabel || timeLabel);
    if (!datetime) datetime = (card.getAttribute('data-datetime') || '').trim();

    // Store selection state globally (DO NOT calculate price here). eventHandle required for cart.
    window.__WORKSHOP_SELECTION__ = {
      eventId,
      eventHandle,
      city,
      state,
      venue,
      datetime
    };

    if (selectedEventIdInput) selectedEventIdInput.value = eventId;
    var numericEventIdForUrl = extractNumericEventIdForUrl(eventId);
    window.__WB_URL_EVENT_ID_NUMERIC__ = numericEventIdForUrl || '';
    setTimeout(function () {
      updateUrlForEventId(numericEventIdForUrl);
    }, 0);

    if (bookingHint) bookingHint.classList.add('pdp-wb-hidden');
    if (selectedSummary) selectedSummary.classList.remove('pdp-wb-hidden');
    const venueText = (card._wbEv && (card._wbEv.venue_name || card._wbEv.venue || card._wbEv.city)) || venue || '';
    if (summaryVenue) summaryVenue.textContent = venueText;
    // Venue details (address, phone, work hours) with labels – same structure as venue card
    if (summaryVenueDetails) {
      var addr = (card.dataset.venueAddress || '').trim();
      var phone = (card.dataset.venuePhone || '').trim();
      var hours = (card.dataset.venueWorkHours || '').trim();
      var esc = function (s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; };
      var phoneEsc = phone ? esc(phone).replace(/"/g, '&quot;') : '';
      var parts = [];
      if (addr) parts.push('<div class="wb-venue-field"><div class="wb-venue-label">Address</div><div class="wb-venue-value wb-venue-addr">' + esc(addr) + '</div></div>');
      if (phone) parts.push('<div class="wb-venue-field"><div class="wb-venue-label">Phone</div><div class="wb-venue-value wb-venue-phone"><a class="wb-venue-phone-link" href="tel:' + phoneEsc + '">' + esc(phone) + '</a></div></div>');
      if (hours) parts.push('<div class="wb-venue-field"><div class="wb-venue-label">Work Hours</div><div class="wb-venue-value wb-venue-work-hours">' + esc(hours) + '</div></div>');
      summaryVenueDetails.innerHTML = parts.join('');
    }
    if (summaryDatetime) summaryDatetime.textContent = datetime || (card.getAttribute('data-datetime') || '').trim();
    if (summaryCapacity) {
      const cap = card.dataset.capacityRemaining;
      var isStandaloneMobile = !!(booking && booking.classList && booking.classList.contains('wb-standalone') && window.matchMedia && window.matchMedia('(max-width: 750px)').matches);
      summaryCapacity.textContent = isStandaloneMobile ? '' : (cap ? `Capacity: ${cap} remaining` : '');
    }
    var remaining = getRemainingCapacityFromCard(card);
    var totalRaw = card.dataset.capacityTotal || card.dataset.capacity;
    var total = totalRaw != null && totalRaw !== '' ? Number(totalRaw) : null;
    wbRenderCapacityBadge(bookingBox, remaining, total);
    wbOpenStickyPanel(bookingBox);
    try {
      var eventObj = card._wbEv || null;
      console.debug('[WB] capacity debug', {
        remainingComputed: remaining,
        totalComputed: total,
        cardDataset: pickCapacityFields(card.dataset),
        eventObjCapacity: pickCapacityFields(eventObj || {})
      });
    } catch (_) {}
    if (summaryStatus) {
      var statusVal = (card.dataset.status || '').toLowerCase().replace(/\s/g, '_');
      if (statusVal && statusVal !== 'active') {
        summaryStatus.textContent = statusVal.toUpperCase();
        summaryStatus.classList.remove('pdp-wb-hidden');
      } else {
        summaryStatus.textContent = '';
        summaryStatus.classList.add('pdp-wb-hidden');
      }
    }

    // Show only variant cards for this event (available_variants or fallback to variant_id)
    var allowedVariantIds = getAvailableVariantIdsFromCard(card);
    applyVariantCardVisibility(allowedVariantIds);

    // Sync Motion native picker + booking panel UI to event's variant
    const rawVariantId = (card.dataset.variantId || card.dataset.variantGid || '').trim();
    if (rawVariantId && rawVariantId !== 'none') {
      const parsedVid = parseVariantIdFromGid(rawVariantId);
      const vidStr = parsedVid != null && String(parsedVid).trim() !== '' ? String(parsedVid).trim() : '';
      if (vidStr) {
        if (variantSelect) variantSelect.value = vidStr;
        booking.querySelectorAll('.wb-variant-btn').forEach(function (b) {
          var bid = (b.dataset.variantId || '').toString().trim();
          var bNum = parseVariantIdFromGid(bid) || bid;
          b.classList.toggle('is-active', String(bNum) === vidStr);
        });
        motionTriggerVariantById(vidStr);
      }
    }
    ensureVariantUiSyncedFromSelect();

    // Show preview price (UI-only, never writes to state or dataset)
    showPreviewPrice(card);
    updateAllVariantCardPrices();

    // Selection summary bar (sticky): dispatch so bar can show venue, date, time, variant, price
    var detail = buildSelectionDetailFromCard(card);
    if (detail) wbDispatchSelection(detail);

    // Cross-product "Also available in:" in Booking panel (under variant tiles)
    renderCrossProductBooking(booking, eventId);

    var selEv = card._wbEv || findEventById(eventId);
    renderAlsoAvailableProducts(selEv, bookingBox || booking);

    // Get capacity from card (metaobject-based, no webhook)
    const remainingCapacity = getRemainingCapacityFromCard(card);
    
    // Update quantity input max attribute and helper text
    const quantityInput = bookingBox ? bookingBox.querySelector('[data-workshop-quantity]') : null;
    const seatsAvailableEl = bookingBox ? bookingBox.querySelector('[data-workshop-seats-available]') : null;
    
    if (quantityInput) {
      if (remainingCapacity != null && remainingCapacity > 0) {
        quantityInput.setAttribute('max', String(remainingCapacity));
        if (seatsAvailableEl) {
          seatsAvailableEl.textContent = `Seats available: ${remainingCapacity}`;
          seatsAvailableEl.classList.remove('hidden');
        }
      } else {
        quantityInput.removeAttribute('max');
        if (seatsAvailableEl) seatsAvailableEl.classList.add('hidden');
      }
    }

    // Validate initial quantity (default 1) using metaobject capacity
    // This will enable/disable buttons based on capacity
    validateSeatSelection();
    try {
      wbRefreshBookingGuideUi();
    } catch (_) {}
  }

  function syncInCardSummaryForVenueCard(vc) {
    if (!vc) return;
    var meta = vc.querySelector('[data-wb-venue-meta]');
    if (!meta) return;
    var sel = vc.querySelector('.workshop-event-card.is-selected');
    if (!sel) { meta.innerHTML = ''; return; }
    var dl = (sel.getAttribute('data-date-label') || '').trim();
    var tl = (sel.getAttribute('data-time-label') || '').trim();
    var cap = sel.getAttribute('data-capacity-remaining');
    var line1 = dl || '';
    var line2Parts = [];
    if (tl) line2Parts.push(tl);
    if (cap !== null && cap !== '' && Number(cap) > 0) line2Parts.push(cap + ' seats');
    var line2 = line2Parts.join(' · ');
    meta.innerHTML =
      (line1 ? '<div class="wb-meta-line1">' + line1 + '</div>' : '') +
      (line2 ? '<div class="wb-meta-line2">' + line2 + '</div>' : '');
  }

  function applyFilters() {
    if (useLocationFirst) return;
    const { state, city, variant } = getSelections();

    const cards = Array.from(eventsRoot.querySelectorAll('.workshop-event-card'));
    wbDiag('A_totalCardsInDOM', { count: cards.length });

    cards.forEach((card) => {
      const cardState = normalize(card.dataset.state);
      const cardCity = normalize(card.dataset.city);
      const cardVariantId = (card.dataset.variantId || 'none').toString().trim();

      let ok = true;

      // 1) State – case-insensitive
      if (state && normKey(cardState) !== normKey(state)) ok = false;
      // 2) City – case-insensitive
      if (ok && city && normKey(cardCity) !== normKey(city)) ok = false;
      // 3) Variant – string compare (only after user has changed variant selector)
      if (ok && variantFilterActive && variant && cardVariantId !== 'none' && String(cardVariantId) !== String(variant)) ok = false;

      card.style.display = ok ? '' : 'none';
    });

    sortVisibleByStartsAt();

    // If selected card becomes hidden by filters, clear selection
    const selected = eventsRoot.querySelector('.workshop-event-card.is-selected');
    if (selected && selected.style.display === 'none') {
      clearEventSelection();
    }

    const visibleAfter = Array.from(eventsRoot.querySelectorAll('.workshop-event-card')).filter(function (c) { return window.getComputedStyle(c).display !== 'none'; });
    wbDiag('B_visibleAfterFilters', { count: visibleAfter.length, selections: getSelections() });
  }

  function sortVisibleByStartsAt() {
    const visibleCards = Array.from(eventsRoot.querySelectorAll('.workshop-event-card'))
      .filter((el) => el.style.display !== 'none');

    visibleCards.sort((a, b) => {
      const aTs = parseInt(a.dataset.startsAt || '0', 10);
      const bTs = parseInt(b.dataset.startsAt || '0', 10);
      return aTs - bTs;
    });

    // Re-append in sorted order (stable for visible list)
    visibleCards.forEach((el) => eventsRoot.appendChild(el));
  }

  // ---- Location-first flow: state 0 INIT, 1 LOCATION_RESOLVED, 2 EVENT_TIME_SELECTED, 3 VARIANT_SELECTED ----
  let wbState = 0;
  let allEventsPayload = [];

  function wbInitEventStatusLookup() {
    try {
      var payloadEl = eventsPayloadScript || document.querySelector('[data-workshop-events-payload]');
      var arr = payloadEl ? JSON.parse(payloadEl.textContent || '[]') : [];
      window.__WB_EVENT_STATUS_LOOKUP__ = wbBuildEventStatusLookup(arr);
      if (window.__WB_DIAG__) console.debug('[WB] event status lookup keys', Object.keys(window.__WB_EVENT_STATUS_LOOKUP__ || {}).length);
    } catch (_) {
      window.__WB_EVENT_STATUS_LOOKUP__ = Object.create(null);
    }
  }
  wbInitEventStatusLookup();

  function setWbState(s) {
    wbState = s;
    if (variantWrap) {
      if (s === 2 || s === 3) {
        variantWrap.classList.remove('pdp-wb-hidden');
        variantWrap.setAttribute('aria-hidden', 'false');
      } else {
        variantWrap.classList.add('pdp-wb-hidden');
        variantWrap.setAttribute('aria-hidden', 'true');
      }
    }
    if (s === 3) {
      if (bookingBox) bookingBox.classList.remove('wb-no-event-selected');
      if (lastPriceEl) {
        const sel = eventsRoot.querySelector('.workshop-event-card.is-selected');
        if (sel) showPreviewPrice(sel);
      }
      updateAllVariantCardPrices();
      if (bookNowBtn) { bookNowBtn.disabled = false; bookNowBtn.style.backgroundColor = ''; bookNowBtn.style.color = ''; bookNowBtn.style.cursor = 'pointer'; }
      if (addToCartBtn) { addToCartBtn.disabled = false; addToCartBtn.style.backgroundColor = ''; addToCartBtn.style.color = ''; addToCartBtn.style.cursor = 'pointer'; }
    } else {
      if (bookNowBtn) { bookNowBtn.disabled = true; bookNowBtn.style.backgroundColor = '#e0e0e0'; bookNowBtn.style.color = '#9e9e9e'; bookNowBtn.style.cursor = 'not-allowed'; }
      if (addToCartBtn) { addToCartBtn.disabled = true; addToCartBtn.style.backgroundColor = '#e0e0e0'; addToCartBtn.style.color = '#9e9e9e'; addToCartBtn.style.cursor = 'not-allowed'; }
    }
  }

    if (useLocationFirst) {
      try {
        const payloadEl = eventsPayloadScript;
        allEventsPayload = JSON.parse(payloadEl.textContent || '[]');
        window.__WB_EVENT_STATUS_LOOKUP__ = wbBuildEventStatusLookup(allEventsPayload);
      } catch (_) {
        allEventsPayload = [];
      }

      const GOOGLE_PLACES_TIMEOUT_MS = 8000;
      let googlePlacesLoadPromise = null;
      let gmapsLastError = null;
      function ensureGooglePlacesLoaded(key) {
        if (!key || !String(key).trim()) {
          gmapsLastError = '[WB] No Google key';
          return Promise.reject(new Error(gmapsLastError));
        }
        if (typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places) return Promise.resolve();
        if (googlePlacesLoadPromise) return googlePlacesLoadPromise;
        const scriptEl = document.querySelector('script[src*="maps.googleapis.com"]');
        if (scriptEl) {
          googlePlacesLoadPromise = new Promise((resolve, reject) => {
            const start = Date.now();
            const check = () => {
              if (window.google && window.google.maps && window.google.maps.places) { googlePlacesLoadPromise = null; resolve(); return; }
              if (Date.now() - start > GOOGLE_PLACES_TIMEOUT_MS) {
                googlePlacesLoadPromise = null;
                gmapsLastError = '[WB] Google Places load timeout';
                reject(new Error(gmapsLastError));
                return;
              }
              setTimeout(check, 100);
            };
            check();
          });
          return googlePlacesLoadPromise;
        }
        googlePlacesLoadPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            googlePlacesLoadPromise = null;
            gmapsLastError = '[WB] Google Places load timeout';
            if (window.__WB_DIAG__) console.warn(gmapsLastError + ' (blocked?)');
            reject(new Error(gmapsLastError));
          }, GOOGLE_PLACES_TIMEOUT_MS);
          const cbName = '__WB_GMAPS_READY_' + Date.now();
          window[cbName] = function () {
            clearTimeout(timeout);
            delete window[cbName];
            if (!window.google || !window.google.maps) {
              googlePlacesLoadPromise = null;
              gmapsLastError = '[WB] Google Places not available after load';
              reject(new Error(gmapsLastError));
              return;
            }
            const imp = window.google.maps.importLibrary;
            if (typeof imp === 'function') {
              imp('places').then(() => { googlePlacesLoadPromise = null; resolve(); }).catch((e) => {
                googlePlacesLoadPromise = null;
                gmapsLastError = (e && e.message) || '[WB] Google Places load failed';
                if (window.__WB_DIAG__) console.warn(gmapsLastError + ' (blocked?)');
                reject(e || new Error(gmapsLastError));
              });
            } else {
              if (window.google.maps.places) { googlePlacesLoadPromise = null; resolve(); }
              else { googlePlacesLoadPromise = null; gmapsLastError = '[WB] Google Places not available after load'; reject(new Error(gmapsLastError)); }
            }
          };
          const s = document.createElement('script');
          s.async = true;
          s.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(key) + '&libraries=places&v=weekly&callback=' + cbName;
          s.onerror = () => {
            clearTimeout(timeout);
            delete window[cbName];
            googlePlacesLoadPromise = null;
            gmapsLastError = '[WB] Google Places script failed (blocked?)';
            if (window.__WB_DIAG__) console.warn(gmapsLastError);
            reject(new Error(gmapsLastError));
          };
          document.head.appendChild(s);
        });
        return googlePlacesLoadPromise;
      }

    const locInput = document.getElementById('workshop-location-input');
    const locSummary = booking.querySelector('[data-workshop-location-summary]');
    const useLocBtn = booking.querySelector('[data-workshop-use-location]');
    const retryBtn = document.getElementById('workshop-retry-location');
    const initialFetchLatRaw = (booking.dataset.wbInitialFetchLat || '').trim();
    const initialFetchLngRaw = (booking.dataset.wbInitialFetchLng || '').trim();
    const initialFetchRadiusRaw = (booking.dataset.wbInitialFetchRadiusKm || '').trim();
    const initialFetchLat = Number(initialFetchLatRaw);
    const initialFetchLng = Number(initialFetchLngRaw);
    let initialFetchRadiusKm = Number(initialFetchRadiusRaw);
    if (!Number.isFinite(initialFetchRadiusKm) || initialFetchRadiusKm <= 0) initialFetchRadiusKm = 1000;

    var WB_DEFAULT_LOCATION_LABEL = '';
    if (typeof window !== 'undefined') window.__WB_USER_CHOSEN_LOCATION__ = false;

    /**
     * Single UI location writer. Only this function sets location input/summary text.
     * @param {{ label?: string, isDefault?: boolean, clear?: boolean }} opts - Use isDefault: true for default; clear: true to empty input; otherwise label for user selection.
     */
    function wbSetLocationUi(opts) {
      if (opts && opts.clear) {
        var label = '';
      } else {
        var displayLabel = (opts && opts.isDefault) ? WB_DEFAULT_LOCATION_LABEL : (opts && opts.label);
        if (displayLabel == null || String(displayLabel).trim() === '') displayLabel = WB_DEFAULT_LOCATION_LABEL;
        var label = String(displayLabel).trim();
      }
      if (locSummary) locSummary.textContent = label;
      if (locInput) locInput.value = label;
      var placeEl = document.querySelector('#workshop-location-place-autocomplete,[data-workshop-place-autocomplete]');
      if (placeEl) {
        try {
          if (typeof placeEl.value !== 'undefined') placeEl.value = label;
          var innerInput = placeEl.shadowRoot && placeEl.shadowRoot.querySelector('input');
          if (innerInput) innerInput.value = label;
        } catch (_) {}
      }
      var hiddenInput = document.getElementById('workshop-location-input');
      if (hiddenInput) hiddenInput.value = label;
      var byAttr = document.querySelector('[data-workshop-location-input]');
      if (byAttr && byAttr !== placeEl) byAttr.value = label;
    }

    /** Google Place Details cache: place_id -> normalized ResolvedLocation. TTL optional. */
    const googlePlaceCache = Object.create(null);
    const GOOGLE_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

    function renderEventsList(nearbyCityKeys, resolvedLat, resolvedLng) {
      const Flow = window.WorkshopLocationFlow;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayStr = today.toISOString().slice(0, 10);
      const end30 = new Date(today);
      end30.setDate(end30.getDate() + 30);
      const end30Str = end30.toISOString().slice(0, 10);
      const end60 = new Date(today);
      end60.setDate(end60.getDate() + 60);
      const end60Str = end60.toISOString().slice(0, 10);

      let filtered;
      if (Flow) {
        const deriveEventCityKey = Flow.deriveEventCityKey;
        const getDistanceBucketIndex = Flow.getDistanceBucketIndex;
        const distanceKm = Flow.distanceKm;

        function getEventCityKey(ev) {
          if (ev.city_key && String(ev.city_key).trim()) return String(ev.city_key).trim();
          return deriveEventCityKey(ev.state_code || ev.state, ev.city);
        }

        const nearbySet = new Set(nearbyCityKeys || []);

        if (window.__WB_DIAG__ && allEventsPayload.length > 0) {
          const sample = allEventsPayload[0];
          console.debug('[WORKSHOP] sample event', { city: sample.city, state: sample.state, state_code: sample.state_code, city_key: sample.city_key });
          console.debug('[WORKSHOP] nearby city_keys count', (nearbyCityKeys || []).length, 'first 10', (nearbyCityKeys || []).slice(0, 10));
        }

        let excludeReasons = { product: 0, status: 0, cityKey: 0, date: 0 };
        filtered = allEventsPayload.filter((ev) => {
          const ph = (ev.product_handle || '').trim().toLowerCase();
          if (ph !== productHandle) { excludeReasons.product++; return false; }
          const st = (ev.status || '').toLowerCase().replace(/\s/g, '_');
          if (st !== 'active' && st !== 'sold_out') { excludeReasons.status++; return false; }
          const evCityKey = getEventCityKey(ev);
          if (!evCityKey || !nearbySet.has(evCityKey)) { excludeReasons.cityKey++; return false; }
          const dk = ev.date_key || '';
          if (dk < todayStr) { excludeReasons.date++; return false; }
          if (dk <= end30Str) return true;
          return dk <= end60Str;
        });

        if (window.__WB_DIAG__) {
          console.debug('[WORKSHOP] filtered events count', filtered.length, 'exclusions', excludeReasons);
        }

        const N_EXPAND = 8;
        if (filtered.length < N_EXPAND) {
          filtered = allEventsPayload.filter((ev) => {
            const ph = (ev.product_handle || '').trim().toLowerCase();
            if (ph !== productHandle) return false;
            const st = (ev.status || '').toLowerCase().replace(/\s/g, '_');
            if (st !== 'active' && st !== 'sold_out') return false;
            const evCityKey = getEventCityKey(ev);
            if (!evCityKey || !nearbySet.has(evCityKey)) return false;
            const dk = ev.date_key || '';
            if (dk < todayStr) return false;
            return dk <= end60Str;
          });
        }
      } else {
        filtered = [];
      }

      var normalizedList = (filtered || []).map(function (ev) { return normalizeApiEvent(ev); }).filter(Boolean);
      // Venue-centric rendering
      var venueList = buildVenueListFromEvents(normalizedList, resolvedLat, resolvedLng);
      window.__WORKSHOP_VENUE_LIST__ = venueList;
      if (eventsPlaceholder) eventsPlaceholder.classList.add('pdp-wb-hidden');
      renderVenueCentricList(venueList);
      trySelectEventFromUrl();
    }

    function escapeHtml(s) {
      const div = document.createElement('div');
      div.textContent = s;
      return div.innerHTML;
    }

    let workshopFetchRequestId = 0;
    var workshopPerfCycleId = 0;
    var cityAbortController = null;
    var stateAbortController = null;
    var latLngAbortController = null;
    var WORKSHOP_CACHE_TTL_MS = 3 * 60 * 1000;
    var workshopEventsCache = Object.create(null);
    var workshopCacheShop = (typeof window !== 'undefined' && window.Shopify && window.Shopify.shop) ? String(window.Shopify.shop) : '';

    /** Cache key for lat/lng (rounded so same area hits cache). */
    function locationKey(lat, lng) {
      if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return '';
      var a = Math.round(Number(lat) * 100000) / 100000;
      var b = Math.round(Number(lng) * 100000) / 100000;
      return workshopCacheShop + '|' + (productGid || '') + '|latlng:' + a + ',' + b;
    }
    var defaultRadiusRaw = Number((booking.dataset.wbDefaultRadiusKm || '').trim());
    var WB_DEFAULT_RADIUS = Number.isFinite(defaultRadiusRaw) && defaultRadiusRaw > 0 ? defaultRadiusRaw : 1000;
    /**
     * Single source of truth for radius from theme settings.
     * @param {Element} root — the booking component root (containerEl).
     * @returns {number} — radius in km; falls back to configured default.
     */
    function getRadiusKm(root) {
      var fromRoot = root ? Number((root.dataset.wbDefaultRadiusKm || '').trim()) : NaN;
      if (Number.isFinite(fromRoot) && fromRoot > 0) return fromRoot;
      if (DEV_MODE) console.warn('[WB_RADIUS] missing configured default radius, using fallback=' + WB_DEFAULT_RADIUS);
      return WB_DEFAULT_RADIUS;
    }
    /** Cache key includes the radius that was computed for this action (passed in, not re-read). */
    function cacheKeyWithRadius(lat, lng, radiusKm) { var k = locationKey(lat, lng); return k ? k + '|r' + radiusKm + '|wbv1' : ''; }

    /** @deprecated Local workshop-locations JSON removed; distances use API venue coords only. */
    function getCityList() {
      return [];
    }

    /** Safe display city for accordion header: city → city_key (humanized) → venue_name → venue_handle (humanized) → "Unknown". */
    function getDisplayCity(event) {
      var clean = function (s) { return (s == null ? '' : String(s)).trim(); };
      if (clean(event.city)) return clean(event.city);
      if (clean(event.city_key)) {
        return clean(event.city_key)
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, function (m) { return m.toUpperCase(); });
      }
      if (clean(event.venue_name)) return clean(event.venue_name);
      if (clean(event.venue_handle)) {
        return clean(event.venue_handle)
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, function (m) { return m.toUpperCase(); });
      }
      return 'Unknown';
    }

    /** Safe numeric distance: returns finite number or null (empty string → null to avoid "0 km away" from Number('')). */
    function toNum(val) {
      if (val == null) return null;
      if (typeof val === 'string') {
        var s = val.trim();
        if (!s) return null;
        s = s.replace(/,/g, '');
        val = s;
      }
      var n = Number(val);
      return Number.isFinite(n) ? n : null;
    }

    /** Reject impossible or placeholder coords (0,0 and out-of-range) before haversine. */
    function isValidCoord(lat, lng) {
      if (lat == null || lng == null) return false;
      if (lat === 0 && lng === 0) return false;
      if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return false;
      return true;
    }

    /** Haversine distance in km (for group distance when cityLoc is missing). */
    function haversineKm(lat1, lon1, lat2, lon2) {
      var toRad = function (d) { return (d * Math.PI) / 180; };
      var R = 6371;
      var dLat = toRad(lat2 - lat1);
      var dLon = toRad(lon2 - lon1);
      var a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
      return 2 * R * Math.asin(Math.sqrt(a));
    }

    /** Parse coordinate value to finite number or null. Handles 0, '', ' ', null, undefined safely. */
    function wbParseCoord(v) {
      if (v == null) return null;
      if (typeof v === 'string') { v = v.trim(); if (v === '') return null; }
      var n = Number(v);
      return Number.isFinite(n) ? n : null;
    }

    /** First non-null result of wbParseCoord across candidates. Preserves 0 as valid. */
    function wbFirstCoord() {
      for (var i = 0; i < arguments.length; i++) {
        var n = wbParseCoord(arguments[i]);
        if (n != null) return n;
      }
      return null;
    }

    /** Grouping key: lat/lng first (ll:); else city_key (ck:); else name. Waitlist catalog is lat/lng-only. */
    function wbVenueGroupKey(v) {
      var lat = wbFirstCoord(v.lat, v.venue_lat);
      var lng = wbFirstCoord(v.lng, v.venue_lng);
      if (lat != null && lng != null) return 'll:' + lat.toFixed(4) + ',' + lng.toFixed(4);
      var ck = (v.city_key || '').toString().trim();
      if (ck) return 'ck:' + ck;
      return 'name:' + String(v.venue_name || v.name || v.city_name || '').trim().toLowerCase();
    }

    /** Display label for venue group: city_name → city → venue_city → venue_name → name. */
    function wbVenueGroupLabel(v) {
      return (v.city_name || v.city || v.venue_city || v.venue_name || v.name || '').toString().trim();
    }

    /** Haversine distance in km (user for distance display; safe asin). */
    function wbHaversineKm(lat1, lng1, lat2, lng2) {
      var R = 6371;
      var dLat = (lat2 - lat1) * Math.PI / 180;
      var dLng = (lng2 - lng1) * Math.PI / 180;
      var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
      return 2 * R * Math.asin(Math.min(1, Math.sqrt(a)));
    }

    /** Venue has at least one bookable event row (not waitlist-only stub). */
    function wbVenueHasScheduledEvents(v) {
      if (!v || typeof v !== 'object') return false;
      if (v._wbWaitlistVenue === true) return false;
      var ev = v.events;
      return Array.isArray(ev) && ev.length > 0;
    }

    /** Wizard step 1: venue has a bookable dated session (not sold out). */
    function wbVenueHasBookableEvents(v) {
      if (!v || typeof v !== 'object') return false;
      if (v._wbWaitlistVenue === true) return false;
      var evs = v.events;
      if (!Array.isArray(evs) || !evs.length) return false;
      for (var i = 0; i < evs.length; i++) {
        var ev = evs[i];
        if (!ev || typeof ev !== 'object') continue;
        var dk = (ev.date_key || '').toString().trim();
        if (!dk && ev.schema_start) dk = String(ev.schema_start).slice(0, 10);
        if (!dk) continue;
        var st = String(ev.status || '').toLowerCase().replace(/\s+/g, '_');
        if (st === 'sold_out' || st === 'cancelled') continue;
        var cap = ev.capacity_remaining;
        if (cap != null && cap !== '' && Number(cap) <= 0) continue;
        return true;
      }
      return false;
    }

    /** Accordion group has any venue with scheduled events. */
    function wbGroupHasScheduledEvents(g) {
      if (!g || !Array.isArray(g.venues)) return false;
      for (var gi = 0; gi < g.venues.length; gi++) {
        if (wbVenueHasScheduledEvents(g.venues[gi])) return true;
      }
      return false;
    }

    function wbCmpDistanceKmProp(propA, propB) {
      var da = toNum(propA) != null ? toNum(propA) : Infinity;
      var db = toNum(propB) != null ? toNum(propB) : Infinity;
      return da - db;
    }

    /** Sort venues inside a city/lat-lng group (distance-only vs sessions-first). */
    function wbSortVenuesInGroup(venues) {
      if (!Array.isArray(venues)) return;
      venues.sort(function (a, b) {
        if (VENUE_LIST_SORT_MODE === 'events_first') {
          var ea = wbVenueHasScheduledEvents(a) ? 1 : 0;
          var eb = wbVenueHasScheduledEvents(b) ? 1 : 0;
          if (ea !== eb) return eb - ea;
        }
        return wbCmpDistanceKmProp(a.distance_km, b.distance_km);
      });
    }

    /** Sort accordion venue groups for display. */
    function wbSortVenueGroups(groups) {
      if (!Array.isArray(groups)) return;
      groups.sort(function (a, b) {
        if (VENUE_LIST_SORT_MODE === 'events_first') {
          var ha = wbGroupHasScheduledEvents(a) ? 1 : 0;
          var hb = wbGroupHasScheduledEvents(b) ? 1 : 0;
          if (ha !== hb) return hb - ha;
        }
        return wbCmpDistanceKmProp(a.distance_km, b.distance_km);
      });
    }

    /** Single source for user location. Only window.__wbUserLoc is set here. */
    function wbSetUserLoc(lat, lng) {
      if (lat == null && lng == null) {
        window.__wbUserLoc = null;
        return;
      }
      if (Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))) {
        window.__wbUserLoc = { lat: Number(lat), lng: Number(lng) };
      }
    }

    /** Build venue list from already-normalized events (used when timing normalize separately). */
    function buildVenueListFromEventsNormalized(list, userLat, userLng) {
      list = (Array.isArray(list) ? list : []).filter(Boolean);
      var cityList = getCityList();
      var cityByKey = Object.create(null);
      cityList.forEach(function (loc) {
        var k = (loc.city_key || '').toString().trim().toLowerCase();
        if (k) cityByKey[k] = loc;
      });
      var venuesByRef = Object.create(null);
      list.forEach(function (ev) {
        var vref = (ev.venue_ref_id || '').toString().trim() || ('v_' + (ev.venue_name || '').toString().trim());
        if (!vref) return;
        if (!venuesByRef[vref]) {
          var first = ev;
          var vlat = wbFirstCoord(first.venue_lat, first.lat);
          var vlng = wbFirstCoord(first.venue_lng, first.lng);
          var ck = (ev.city_key || '').toString().trim();
          var cityLoc = ck ? cityByKey[ck.toLowerCase()] : null;
          var userLoc = (typeof userLat === 'number' && typeof userLng === 'number') ? { lat: userLat, lng: userLng } : (typeof window !== 'undefined' && window.__wbUserLoc) || null;
          var ulat = userLoc ? toNum(userLoc.lat) : null;
          var ulng = userLoc ? toNum(userLoc.lng) : null;
          var distKm = null;
          if (ulat != null && ulng != null && Number.isFinite(ulat) && Number.isFinite(ulng)) {
            if (vlat != null && vlng != null) {
              distKm = wbHaversineKm(ulat, ulng, vlat, vlng);
            } else if (cityLoc && isValidCoord(toNum(cityLoc.lat), toNum(cityLoc.lng))) {
              distKm = wbHaversineKm(ulat, ulng, toNum(cityLoc.lat), toNum(cityLoc.lng));
            }
          }
          if (distKm != null && !Number.isFinite(distKm)) distKm = null;
          if (window.__WB_DEBUG__) console.debug('[WB BUILD] venue', { label: first.city_name || first.venue_name, venueLat: vlat, venueLng: vlng, userLat: ulat, userLng: ulng, cityKey: ck, cityLocLat: cityLoc ? cityLoc.lat : null, cityLocLng: cityLoc ? cityLoc.lng : null, distKm: distKm });
          var cityName = (first.city_name || first.city || '').toString().trim();
          venuesByRef[vref] = {
            venue_ref_id: vref,
            venue_name: (ev.venue_name || '').toString().trim(),
            venue_address: (ev.venue_address || '').toString().trim(),
            venue_phone: (ev.venue_phone || '').toString().trim(),
            venue_work_hours: (ev.venue_work_hours || '').toString().trim(),
            venue_tags: Array.isArray(ev.venue_tags) ? ev.venue_tags.slice() : [],
            city_key: ck,
            city_name: cityName,
            venue_lat: vlat,
            venue_lng: vlng,
            lat: vlat,
            lng: vlng,
            distance_km: distKm,
            venue_image: wbVenueImageFromApiEvent(first),
            events: []
          };
        }
        venuesByRef[vref].events.push(ev);
        var evImgW = wbVenueImageFromApiEvent(ev);
        if (evImgW && !venuesByRef[vref].venue_image) venuesByRef[vref].venue_image = evImgW;
      });
      var groupsByCity = Object.create(null);
      Object.keys(venuesByRef).forEach(function (vref) {
        var v = venuesByRef[vref];
        var groupKey = wbVenueGroupKey(v);
        if (!groupsByCity[groupKey]) {
          var firstEvent = (v.events && v.events[0]) || {};
          var ckLower = (v.city_key || '').toString().trim().toLowerCase();
          var cityLoc = ckLower ? cityByKey[ckLower] : null;
          var cityLabel = wbVenueGroupLabel(v);
          if (!cityLabel) cityLabel = (cityLoc && cityLoc.city) ? cityLoc.city : getDisplayCity(firstEvent);
          var userLoc = (typeof userLat === 'number' && typeof userLng === 'number') ? { lat: userLat, lng: userLng } : (typeof window !== 'undefined' && window.__wbUserLoc) || null;
          var ulat = toNum(userLoc && userLoc.lat);
          var ulng = toNum(userLoc && userLoc.lng);
          var vlat = toNum(v.venue_lat);
          var vlng = toNum(v.venue_lng);
          var distanceKm = null;
          if (ulat != null && ulng != null && Number.isFinite(ulat) && Number.isFinite(ulng)) {
            if (vlat != null && vlng != null && Number.isFinite(vlat) && Number.isFinite(vlng)) {
              distanceKm = wbHaversineKm(ulat, ulng, vlat, vlng);
            } else if (cityLoc && isValidCoord(toNum(cityLoc.lat), toNum(cityLoc.lng))) {
              distanceKm = wbHaversineKm(ulat, ulng, toNum(cityLoc.lat), toNum(cityLoc.lng));
            }
          }
          if (window.__WB_DEBUG__) console.debug('[WB BUILD] group', { label: cityLabel, groupKey: groupKey, city_key: ckLower, vlat: vlat, vlng: vlng, cityLocLat: cityLoc ? cityLoc.lat : null, cityLocLng: cityLoc ? cityLoc.lng : null, ulat: ulat, ulng: ulng, distKm: distanceKm });
          groupsByCity[groupKey] = {
            city_key: (v.city_key || '').toString().trim(),
            group_key: groupKey,
            city_label: cityLabel,
            distance_km: distanceKm,
            venues: []
          };
        }
        groupsByCity[groupKey].venues.push(v);
        var vDist = toNum(v.distance_km);
        if (vDist != null && Number.isFinite(vDist) && (groupsByCity[groupKey].distance_km == null || vDist < groupsByCity[groupKey].distance_km)) {
          groupsByCity[groupKey].distance_km = vDist;
        }
      });
      var groups = Object.keys(groupsByCity).map(function (k) { return groupsByCity[k]; });
      groups.forEach(function (g) {
        wbSortVenuesInGroup(g.venues);
      });
      wbSortVenueGroups(groups);
      return { groups: groups };
    }

    /** Build __WORKSHOP_VENUE_LIST__ from API events: groups by city_key or lat/lng when city_key missing, distances from user lat/lng. */
    function buildVenueListFromEvents(events, userLat, userLng) {
      var list = (Array.isArray(events) ? events : []).map(normalizeApiEvent).filter(Boolean);
      var cityList = getCityList();
      var cityByKey = Object.create(null);
      cityList.forEach(function (loc) {
        var k = (loc.city_key || '').toString().trim().toLowerCase();
        if (k) cityByKey[k] = loc;
      });

      var venuesByRef = Object.create(null);
      list.forEach(function (ev) {
        var vref = (ev.venue_ref_id || '').toString().trim() || ('v_' + (ev.venue_name || '').toString().trim());
        if (!vref) return;
        if (!venuesByRef[vref]) {
          var first = ev;
          var vlat = wbFirstCoord(first.venue_lat, first.lat);
          var vlng = wbFirstCoord(first.venue_lng, first.lng);
          var ck = (ev.city_key || '').toString().trim();
          var cityLoc = ck ? cityByKey[ck.toLowerCase()] : null;
          var userLoc = (typeof userLat === 'number' && typeof userLng === 'number') ? { lat: userLat, lng: userLng } : (typeof window !== 'undefined' && window.__wbUserLoc) || null;
          var ulat = userLoc ? toNum(userLoc.lat) : null;
          var ulng = userLoc ? toNum(userLoc.lng) : null;
          var distKm = null;
          if (ulat != null && ulng != null && Number.isFinite(ulat) && Number.isFinite(ulng)) {
            if (vlat != null && vlng != null) {
              distKm = wbHaversineKm(ulat, ulng, vlat, vlng);
            } else if (cityLoc && isValidCoord(toNum(cityLoc.lat), toNum(cityLoc.lng))) {
              distKm = wbHaversineKm(ulat, ulng, toNum(cityLoc.lat), toNum(cityLoc.lng));
            }
          }
          if (distKm != null && !Number.isFinite(distKm)) distKm = null;
          if (window.__WB_DEBUG__) console.debug('[WB BUILD] venue', { label: first.city_name || first.venue_name, venueLat: vlat, venueLng: vlng, userLat: ulat, userLng: ulng, cityKey: ck, cityLocLat: cityLoc ? cityLoc.lat : null, cityLocLng: cityLoc ? cityLoc.lng : null, distKm: distKm });
          var cityName = (first.city_name || first.city || '').toString().trim();
          venuesByRef[vref] = {
            venue_ref_id: vref,
            venue_name: (ev.venue_name || '').toString().trim(),
            venue_address: (ev.venue_address || '').toString().trim(),
            venue_phone: (ev.venue_phone || '').toString().trim(),
            venue_work_hours: (ev.venue_work_hours || '').toString().trim(),
            venue_tags: Array.isArray(ev.venue_tags) ? ev.venue_tags.slice() : [],
            city_key: ck,
            city_name: cityName,
            venue_lat: vlat,
            venue_lng: vlng,
            lat: vlat,
            lng: vlng,
            distance_km: distKm,
            venue_image: wbVenueImageFromApiEvent(first),
            events: []
          };
        }
        venuesByRef[vref].events.push(ev);
        var evImgW2 = wbVenueImageFromApiEvent(ev);
        if (evImgW2 && !venuesByRef[vref].venue_image) venuesByRef[vref].venue_image = evImgW2;
      });

      var groupsByCity = Object.create(null);
      Object.keys(venuesByRef).forEach(function (vref) {
        var v = venuesByRef[vref];
        var groupKey = wbVenueGroupKey(v);
        if (!groupsByCity[groupKey]) {
          var firstEvent = (v.events && v.events[0]) || {};
          var ckLower = (v.city_key || '').toString().trim().toLowerCase();
          var cityLoc = ckLower ? cityByKey[ckLower] : null;
          var cityLabel = wbVenueGroupLabel(v);
          if (!cityLabel) cityLabel = (cityLoc && cityLoc.city) ? cityLoc.city : getDisplayCity(firstEvent);
          var userLoc = (typeof userLat === 'number' && typeof userLng === 'number') ? { lat: userLat, lng: userLng } : (typeof window !== 'undefined' && window.__wbUserLoc) || null;
          var ulat = toNum(userLoc && userLoc.lat);
          var ulng = toNum(userLoc && userLoc.lng);
          var vlat = toNum(v.venue_lat);
          var vlng = toNum(v.venue_lng);
          var distanceKm = null;
          if (ulat != null && ulng != null && Number.isFinite(ulat) && Number.isFinite(ulng)) {
            if (vlat != null && vlng != null && Number.isFinite(vlat) && Number.isFinite(vlng)) {
              distanceKm = wbHaversineKm(ulat, ulng, vlat, vlng);
            } else if (cityLoc && isValidCoord(toNum(cityLoc.lat), toNum(cityLoc.lng))) {
              distanceKm = wbHaversineKm(ulat, ulng, toNum(cityLoc.lat), toNum(cityLoc.lng));
            }
          }
          if (window.__WB_DEBUG__) console.debug('[WB BUILD] group', { label: cityLabel, groupKey: groupKey, city_key: ckLower, vlat: vlat, vlng: vlng, cityLocLat: cityLoc ? cityLoc.lat : null, cityLocLng: cityLoc ? cityLoc.lng : null, ulat: ulat, ulng: ulng, distKm: distanceKm });
          groupsByCity[groupKey] = {
            city_key: (v.city_key || '').toString().trim(),
            group_key: groupKey,
            city_label: cityLabel,
            distance_km: distanceKm,
            venues: []
          };
        }
        groupsByCity[groupKey].venues.push(v);
        var vDist = toNum(v.distance_km);
        if (vDist != null && Number.isFinite(vDist) && (groupsByCity[groupKey].distance_km == null || vDist < groupsByCity[groupKey].distance_km)) {
          groupsByCity[groupKey].distance_km = vDist;
        }
      });

      var groups = Object.keys(groupsByCity).map(function (k) { return groupsByCity[k]; });
      groups.forEach(function (g) {
        wbSortVenuesInGroup(g.venues);
      });
      wbSortVenueGroups(groups);

      return { groups: groups };
    }

    /**
     * Append venues from API groups that have no bookable events (include_empty_venues).
     * Mutates venueList.groups in place; re-sorts groups and venues by distance.
     *
     * Empty-venue pipeline (has_events === false or events.length === 0):
     * 1) Here: each API venue becomes a venue stub with _wbWaitlistVenue: true, venue_name from v.venue_name, lat/lng from API.
     * 2) renderVenueCentricList: iterates venueList.groups[].venues[]; for each stub builds a .workshop-venue-card with
     *    h4.wb-venue-name (venue title) + Join Waitlist; see isWaitlistOnly branch (~line 4425+).
     */
    function mergeEmptyVenuesFromApi(venueList, apiGroups, userLat, userLng) {
      if (!venueList || !Array.isArray(venueList.groups)) return;
      var apiGroupsResolved = Array.isArray(apiGroups) ? apiGroups : [];
      var cityList = getCityList();
      var cityByKey = Object.create(null);
      cityList.forEach(function (loc) {
        var k = (loc.city_key || '').toString().trim().toLowerCase();
        if (k) cityByKey[k] = loc;
      });
      var seenRef = Object.create(null);
      venueList.groups.forEach(function (g) {
        (g.venues || []).forEach(function (v) {
          var r = (v.venue_ref_id || '').toString().trim();
          if (r) seenRef[r] = true;
        });
      });
      for (var gix = 0; gix < apiGroupsResolved.length; gix++) {
        var g = apiGroupsResolved[gix];
        if (!g || !Array.isArray(g.venues)) continue;
        var apiVenues = g.venues;
        for (var vix = 0; vix < apiVenues.length; vix++) {
          var v = apiVenues[vix];
          if (!v || typeof v !== 'object') continue;
          var evArr = Array.isArray(v.events) ? v.events : Array.isArray(v.venue_events) ? v.venue_events : [];
          var hasEvRaw = v.has_events;
          if (hasEvRaw === undefined && v.hasEvents !== undefined) hasEvRaw = v.hasEvents;
          var hasEvStr = typeof hasEvRaw === 'string' ? hasEvRaw.trim().toLowerCase() : '';
          var isWaitlistVenue =
            hasEvRaw === false ||
            hasEvRaw === 0 ||
            hasEvStr === 'false' ||
            hasEvStr === '0' ||
            hasEvStr === 'no' ||
            evArr.length === 0;
          if (!isWaitlistVenue) continue;
          var vname = (v.venue_name || v.name || v.title || '').toString().trim();
          var vlat = wbFirstCoord(v.lat, v.latitude, v.venue_lat);
          var vlng = wbFirstCoord(v.lng, v.longitude, v.venue_lng);
          var vref =
            (v.id != null && String(v.id).trim() !== '')
              ? String(v.id).trim()
              : (v.handle != null && String(v.handle).trim() !== '')
                ? String(v.handle).trim()
                : 'v_waitlist_' + gix + '_' + vix + '_' + (vname ? String(vname).replace(/\W+/g, '-').replace(/^-|-$/g, '').toLowerCase().slice(0, 80) : 'venue');
          if (!vref || seenRef[vref]) continue;
          seenRef[vref] = true;
          var ck = ((v.city_key != null && String(v.city_key).trim()) ? String(v.city_key).trim() : (g.city_key != null && String(g.city_key).trim()) ? String(g.city_key).trim() : '');
          var cityLoc = ck ? cityByKey[ck.toLowerCase()] : null;
          var userLoc = (typeof userLat === 'number' && typeof userLng === 'number') ? { lat: userLat, lng: userLng } : (typeof window !== 'undefined' && window.__wbUserLoc) || null;
          var ulat = userLoc ? toNum(userLoc.lat) : null;
          var ulng = userLoc ? toNum(userLoc.lng) : null;
          var distKm = null;
          if (ulat != null && ulng != null && Number.isFinite(ulat) && Number.isFinite(ulng)) {
            if (vlat != null && vlng != null) {
              distKm = wbHaversineKm(ulat, ulng, vlat, vlng);
            } else if (cityLoc && isValidCoord(toNum(cityLoc.lat), toNum(cityLoc.lng))) {
              distKm = wbHaversineKm(ulat, ulng, toNum(cityLoc.lat), toNum(cityLoc.lng));
            }
          }
          var cityName = (v.city_name || v.city || g.city_name || g.city || '').toString().trim() || vname;
          var venueStub = {
            venue_ref_id: vref,
            venue_api_id: (v.id != null ? v.id : vref),
            venue_name: vname,
            venue_address: (v.address || '').toString().trim(),
            venue_phone: (v.phone || '').toString().trim(),
            venue_work_hours: (v.work_hours || '').toString().trim(),
            venue_tags: Array.isArray(v.tags) ? v.tags.map(function (x) { return String(x); }) : [],
            city_key: ck,
            city_name: cityName,
            venue_lat: vlat,
            venue_lng: vlng,
            lat: vlat,
            lng: vlng,
            distance_km: distKm,
            venue_image: wbVenueImageFromStub(v),
            events: [],
            _wbWaitlistVenue: true
          };
          var groupKey = wbVenueGroupKey(venueStub);
          var targetGroup = null;
          for (var gi = 0; gi < venueList.groups.length; gi++) {
            if (venueList.groups[gi].group_key === groupKey) {
              targetGroup = venueList.groups[gi];
              break;
            }
          }
          if (!targetGroup) {
            var ckLower = (venueStub.city_key || '').toString().trim().toLowerCase();
            var cLoc = ckLower ? cityByKey[ckLower] : null;
            var cityLabel = wbVenueGroupLabel(venueStub);
            if (!cityLabel) cityLabel = (cLoc && cLoc.city) ? cLoc.city : getDisplayCity({ city: g.city, city_key: g.city_key, venue_name: venueStub.venue_name, venue_handle: v.handle });
            targetGroup = {
              city_key: ck,
              group_key: groupKey,
              city_label: cityLabel,
              distance_km: distKm,
              venues: []
            };
            venueList.groups.push(targetGroup);
          }
          targetGroup.venues.push(venueStub);
          var vDist = toNum(venueStub.distance_km);
          if (vDist != null && Number.isFinite(vDist) && (targetGroup.distance_km == null || vDist < targetGroup.distance_km)) {
            targetGroup.distance_km = vDist;
          }
        }
      }
      venueList.groups.forEach(function (g) {
        if (!g.venues) return;
        wbSortVenuesInGroup(g.venues);
      });
      wbSortVenueGroups(venueList.groups);
    }

    /**
     * App Proxy often returns only venues that already have events. Shop `venue` metaobjects
     * (Liquid: shop.metaobjects.venue.values → data-wb-venues-waitlist-catalog, lat/lng only)
     * fill gaps: within radiusKm, venues not already listed with events become waitlist stubs.
     */
    function mergeShopCatalogVenuesForWaitlist(venueList, bookingRoot, userLat, userLng, radiusKm) {
      if (!venueList || !Array.isArray(venueList.groups) || !bookingRoot) return;
      if (userLat == null || userLng == null || !Number.isFinite(Number(userLat)) || !Number.isFinite(Number(userLng))) return;
      var rKm = Number(radiusKm);
      if (!Number.isFinite(rKm) || rKm <= 0) return;
      var el = bookingRoot.querySelector('script[type="application/json"][data-wb-venues-waitlist-catalog]');
      if (!el || !el.textContent) return;
      var rows;
      try {
        rows = JSON.parse(el.textContent);
      } catch (e) {
        return;
      }
      if (!Array.isArray(rows) || !rows.length) return;

      var refsWithEvents = Object.create(null);
      venueList.groups.forEach(function (g) {
        (g.venues || []).forEach(function (v) {
          var er = (v.venue_ref_id || '').toString().trim();
          if (!er) return;
          var evs = Array.isArray(v.events) ? v.events : [];
          if (evs.length > 0) refsWithEvents[er] = true;
        });
      });

      var synthetic = [];
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (!row || typeof row !== 'object') continue;
        var vref = (row.venue_ref_id != null && String(row.venue_ref_id).trim()) ? String(row.venue_ref_id).trim() : '';
        if (!vref || refsWithEvents[vref]) continue;
        var vlat = row.lat != null ? Number(row.lat) : NaN;
        var vlng = row.lng != null ? Number(row.lng) : NaN;
        if (!Number.isFinite(vlat) || !Number.isFinite(vlng)) continue;
        var distKm = wbHaversineKm(Number(userLat), Number(userLng), vlat, vlng);
        if (distKm == null || !Number.isFinite(distKm) || distKm > rKm) continue;
        var catName = (row.venue_name != null && String(row.venue_name).trim()) ? String(row.venue_name).trim() : 'Venue';
        synthetic.push({
          id: vref,
          venue_name: catName,
          venue_ref_id: vref,
          address: (row.venue_address != null && String(row.venue_address).trim()) ? String(row.venue_address).trim() : '',
          lat: vlat,
          lng: vlng,
          city_key: '',
          city_name: catName,
          venue_image: wbCoerceImageUrl(row.venue_image != null ? row.venue_image : ''),
          events: [],
          has_events: false
        });
      }
      if (!synthetic.length) return;
      mergeEmptyVenuesFromApi(venueList, [{ city_key: '', city_name: '', venues: synthetic }], userLat, userLng);
    }

    function getShopMyshopifyDomain() {
      return (typeof window !== 'undefined' && window.Shopify && window.Shopify.shop) ? String(window.Shopify.shop).trim() : '';
    }

    function resolveWorkshopTypeFromProductTags() {
      if (window.__WB_WORKSHOP_TYPE_PROMISE) return window.__WB_WORKSHOP_TYPE_PROMISE;
      window.__WB_WORKSHOP_TYPE_PROMISE = new Promise(function (resolve) {
        if (window.__WB_WORKSHOP_TYPE_RESOLVED != null) {
          resolve(window.__WB_WORKSHOP_TYPE_RESOLVED);
          return;
        }
        var handle = (booking.dataset && booking.dataset.productHandle) ? String(booking.dataset.productHandle).trim() : '';
        if (!handle) {
          window.__WB_WORKSHOP_TYPE_RESOLVED = '';
          resolve('');
          return;
        }
        fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (p) {
            var tags = [];
            if (p && p.tags) {
              if (Array.isArray(p.tags)) tags = p.tags;
              else tags = String(p.tags).split(',').map(function (t) { return String(t).trim(); }).filter(Boolean);
            }
            var types = ['mosaic', 'resin', 'ceramic', 'flower'];
            var found = '';
            for (var ti = 0; ti < tags.length; ti++) {
              var raw = String(tags[ti]).toLowerCase().trim();
              for (var ui = 0; ui < types.length; ui++) {
                if (raw === types[ui] || raw.indexOf(types[ui]) !== -1) {
                  found = types[ui];
                  break;
                }
              }
              if (found) break;
            }
            window.__WB_WORKSHOP_TYPE_RESOLVED = found;
            resolve(found);
          })
          .catch(function () {
            window.__WB_WORKSHOP_TYPE_RESOLVED = '';
            resolve('');
          });
      });
      return window.__WB_WORKSHOP_TYPE_PROMISE;
    }

    /** Session-scoped HTTP response cache (sessionStorage); used by fetchEventsByLatLngEndpoint + applyEventsByLatLngPayload. TTL 8m. */
    var WB_EVENTS_SS_TTL_MS = 8 * 60 * 1000;

    /**
     * Key for sessionStorage events cache: ~1km grid (2 decimal lat/lng) + radius + product scope.
     * Example: wb_events_v1_-36.86_174.76_100_gid…
     */
    function eventsSessionStorageKey(lat, lng, radiusKm) {
      if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return '';
      var la = Math.round(Number(lat) * 100) / 100;
      var ln = Math.round(Number(lng) * 100) / 100;
      var r = Number(radiusKm);
      if (!Number.isFinite(r)) return '';
      var base = 'wb_events_v1_' + la + '_' + ln + '_' + r;
      if (productGid) base += '_' + String(productGid).replace(/[^a-zA-Z0-9_-]/g, '_');
      return base;
    }

    /** True when API payload has at least one event (top-level or nested under groups); never cache empty OK responses. */
    function isEventsPayloadCacheable(data) {
      if (!data || data.ok !== true) return false;
      if (Array.isArray(data.events) && data.events.length > 0) return true;
      if (Array.isArray(data.groups)) {
        for (var gi = 0; gi < data.groups.length; gi++) {
          var g = data.groups[gi];
          if (!g || !Array.isArray(g.venues)) continue;
          for (var vi = 0; vi < g.venues.length; vi++) {
            var v = g.venues[vi];
            if (v && Array.isArray(v.events) && v.events.length > 0) return true;
          }
        }
      }
      if (Array.isArray(data.waitlist_venues) && data.waitlist_venues.length > 0) return true;
      return false;
    }

    function readEventsCache(key) {
      if (!key) return null;
      try {
        var raw = sessionStorage.getItem(key);
        if (!raw) return null;
        var entry = JSON.parse(raw);
        if (!entry || typeof entry.cachedAt !== 'number' || entry.payload == null) return null;
        if (Date.now() - entry.cachedAt >= WB_EVENTS_SS_TTL_MS) return null;
        if (!isEventsPayloadCacheable(entry.payload)) return null;
        return entry;
      } catch (_) {
        return null;
      }
    }

    function writeEventsCache(key, payload) {
      if (!key || !payload || !isEventsPayloadCacheable(payload)) return;
      try {
        sessionStorage.setItem(key, JSON.stringify({ cachedAt: Date.now(), payload: payload }));
      } catch (_) {}
    }

    /**
     * Normalize/render workshop events API payload (shared by network fetch and sessionStorage replay).
     * @param {object} [opts] Pass { fromSessionCache: true } when serving from readEventsCache (skips re-write).
     */
    function applyEventsByLatLngPayload(data, lat, lng, locationLabelForEmpty, cycleId, radiusKm, requestId, ssCacheKey, opts) {
      opts = opts || {};
      if (window.__WB_DIAG__) console.log('[WB_DIAG] requestId guard', { requestId: requestId, workshopFetchRequestId: workshopFetchRequestId, pass: requestId === workshopFetchRequestId });
      if (requestId !== workshopFetchRequestId) return data;
      setLoading(booking, false);
      var ph = booking.querySelector('[data-workshop-events-placeholder]');
      try {
        var events = data.events || [];
        // ── Flatten groups[].venues[].events[] → flat events array ──
        // The API returns { groups: [{ venues: [{ events: [...] }] }] }
        // but the normalize/build pipeline expects a flat event array.
        if (events.length === 0 && Array.isArray(data.groups)) {
          data.groups.forEach(function (g) {
            if (!g || !Array.isArray(g.venues)) return;
            g.venues.forEach(function (v) {
              if (!v || !Array.isArray(v.events)) return;
              v.events.forEach(function (ev) {
                if (!ev || typeof ev !== 'object') return;
                var merged = {};
                for (var k in ev) { if (ev.hasOwnProperty(k)) merged[k] = ev[k]; }
                // Carry venue-level fields so normalizeApiEvent can read them
                if (!merged.venue || typeof merged.venue !== 'object') merged.venue = {};
                if (v.name != null && !merged.venue.name) merged.venue.name = v.name;
                if (v.title != null && !merged.venue.title) merged.venue.title = v.title;
                if (v.address != null && !merged.venue.address) merged.venue.address = v.address;
                if (v.phone != null && !merged.venue.phone) merged.venue.phone = v.phone;
                if (v.work_hours != null && !merged.venue.work_hours) merged.venue.work_hours = v.work_hours;
                if (v.tags != null && merged.venue.tags == null) merged.venue.tags = v.tags;
                if (v.id != null && merged.venue.id == null) merged.venue.id = v.id;
                // Coords: use wbParseCoord to overwrite empty-string placeholders
                if (wbParseCoord(v.lat) != null && wbParseCoord(merged.venue.lat) == null) merged.venue.lat = v.lat;
                if (wbParseCoord(v.lng) != null && wbParseCoord(merged.venue.lng) == null) merged.venue.lng = v.lng;
                if (wbParseCoord(v.latitude) != null && wbParseCoord(merged.venue.latitude) == null) merged.venue.latitude = v.latitude;
                if (wbParseCoord(v.longitude) != null && wbParseCoord(merged.venue.longitude) == null) merged.venue.longitude = v.longitude;
                if (v.state != null && !merged.venue.state) merged.venue.state = v.state;
                if (v.state_code != null && !merged.venue.state_code) merged.venue.state_code = v.state_code;
                if (v.city != null && !merged.venue.city) merged.venue.city = v.city;
                if (v.city_name != null && !merged.venue.city_name) merged.venue.city_name = v.city_name;
                if (v.city_key != null && !merged.venue.city_key) merged.venue.city_key = v.city_key;
                // Also set top-level venue_lat/venue_lng on merged event as direct fallback
                if (wbParseCoord(merged.venue_lat) == null) {
                  var _vl = wbFirstCoord(v.lat, v.latitude, v.venue_lat);
                  if (_vl != null) merged.venue_lat = _vl;
                }
                if (wbParseCoord(merged.venue_lng) == null) {
                  var _vn = wbFirstCoord(v.lng, v.longitude, v.venue_lng);
                  if (_vn != null) merged.venue_lng = _vn;
                }
                // Group-level fields
                if (g.city_key && !merged.city_key) merged.city_key = g.city_key;
                if (g.state && !merged.state) merged.state = g.state;
                if (g.state_code && !merged.state_code) merged.state_code = g.state_code;
                if (g.city && !merged.city) merged.city = g.city;
                if (g.city_name && !merged.city_name) merged.city_name = g.city_name;
                // Group-level coords as last resort
                if (wbParseCoord(merged.venue_lat) == null && wbParseCoord(g.lat) != null) merged.venue_lat = g.lat;
                if (wbParseCoord(merged.venue_lng) == null && wbParseCoord(g.lng) != null) merged.venue_lng = g.lng;
                events.push(merged);
              });
            });
          });
          if (window.__WB_DIAG__) console.log('[WB_DIAG] flattened groups→events', { groups: data.groups.length, flatEvents: events.length });
        }
        if (window.__WB_DIAG__) {
          var sumVenues = 0, sumEvents = 0;
          if (Array.isArray(data.groups)) data.groups.forEach(function (g) { if (g && Array.isArray(g.venues)) { sumVenues += g.venues.length; g.venues.forEach(function (v) { if (v && Array.isArray(v.events)) sumEvents += v.events.length; }); } });
          console.log('[WB_DIAG] response payload', { ok: data.ok, hasTopEvents: !!(data.events && data.events.length), groups: (data.groups || []).length, sumVenues: sumVenues, sumEvents: sumEvents, flatEvents: events.length, metaCount: data.meta && data.meta.count });
        }
        var tNorm = performance.now();
        var normalizedList = (Array.isArray(events) ? events : []).map(normalizeApiEvent).filter(Boolean);
        var normalizeMs = performance.now() - tNorm;
        wbPerf(cycleId, requestId, 'normalize_ms', normalizeMs, { count: normalizedList.length });
        var tBuild = performance.now();
        var venueList = buildVenueListFromEventsNormalized(normalizedList, lat, lng);
        var apiGroupsForWaitlist = Array.isArray(data.groups)
          ? data.groups
          : Array.isArray(data.venue_groups)
            ? data.venue_groups
            : [];
        if (Array.isArray(data.waitlist_venues) && data.waitlist_venues.length) {
          apiGroupsForWaitlist = apiGroupsForWaitlist.slice();
          apiGroupsForWaitlist.push({
            city_key: data.waitlist_city_key != null ? String(data.waitlist_city_key) : '',
            city_name: data.waitlist_city_name != null ? String(data.waitlist_city_name) : '',
            venues: data.waitlist_venues
          });
        }
        mergeEmptyVenuesFromApi(venueList, apiGroupsForWaitlist, lat, lng);
        mergeShopCatalogVenuesForWaitlist(venueList, booking, lat, lng, radiusKm);
        wbEnrichVenueListImagesFromCatalog(venueList, booking);
        var buildMs = performance.now() - tBuild;
        var groups = (venueList && venueList.groups) ? venueList.groups : [];
        var venueCount = groups.reduce(function (acc, g) { return acc + (g.venues ? g.venues.length : 0); }, 0);
        var eventCount = groups.reduce(function (acc, g) {
          if (!g.venues) return acc;
          return acc + g.venues.reduce(function (a, v) { return a + (v.events ? v.events.length : 0); }, 0);
        }, 0);
        wbPerf(cycleId, requestId, 'build_venue_list_ms', buildMs, { groups: groups.length, venues: venueCount, events: eventCount });
        window.__WORKSHOP_VENUE_LIST__ = venueList;
        var cacheKeySave = cacheKeyWithRadius(lat, lng, radiusKm);
        if (cacheKeySave) workshopEventsCache[cacheKeySave] = { venueList: venueList, ts: Date.now() };
        if (!groups || groups.length === 0) {
          if (window.__WB_DIAG__) console.log('[WB_DIAG] empty result → showing placeholder', { rawEvents: events.length, builtGroups: groups ? groups.length : 0, normalizedList: normalizedList.length });
          wbwDispatchVenueReset();
          eventsRoot.innerHTML = '';
          var locLabel = (locationLabelForEmpty != null && String(locationLabelForEmpty).trim()) ? String(locationLabelForEmpty).trim() : (window.__WORKSHOP_LOCATION__ && window.__WORKSHOP_LOCATION__.label) || (lat + ', ' + lng);
          if (ph) {
            ph.classList.remove('pdp-wb-hidden');
            ph.textContent = 'No sessions found near ' + locLabel + '. Try a different location or increase the radius.';
          }
          if (booking.classList && booking.classList.contains('wb-wizard')) {
            try {
              wbRefreshBookingGuideUi();
            } catch (_) {}
          }
          wbPerf(cycleId, requestId, 'placeholder_empty', null, { radius_km: radiusKm, label: locLabel });
        } else {
          if (window.__WB_DIAG__) console.log('[WB_DIAG] rendering venue list', { groups: groups.length, venues: venueCount, events: eventCount, firstGroup: groups[0] ? { city_key: groups[0].city_key, venues: groups[0].venues ? groups[0].venues.length : 0 } : null });
          var tRender = performance.now();
          var counts = renderVenueCentricList(venueList);
          var renderMs = performance.now() - tRender;
          wbPerf(cycleId, requestId, 'render_ms', renderMs, counts || {});
          if (ph) ph.classList.add('pdp-wb-hidden');
          trySelectEventFromUrl();
        }
        if (!opts.fromSessionCache && ssCacheKey) writeEventsCache(ssCacheKey, data);
      } catch (e) {
        if (window.__WB_DIAG__) console.error('[WB_DIAG] venue list process/render error', e);
        if (eventsRoot) eventsRoot.innerHTML = '';
        if (ph) { ph.classList.remove('pdp-wb-hidden'); ph.textContent = "Couldn't load sessions. Try again."; }
      }
      return data;
    }

    /**
     * Lat/lng-only fetch. ALL mutable state (AbortController, requestId) is managed by the CALLER
     * and passed in explicitly. This function never reads radius from the DOM.
     * @param {number} lat
     * @param {number} lng
     * @param {string} locationLabelForEmpty
     * @param {number} cycleId
     * @param {number} radiusKm — already resolved, never re-read.
     * @param {number} requestId — caller-incremented workshopFetchRequestId.
     * @param {AbortSignal} signal — from the caller's AbortController.
     */
    function fetchEventsByLatLngEndpoint(lat, lng, locationLabelForEmpty, cycleId, radiusKm, requestId, signal, mode) {
      if (!eventsRoot || lat == null || lng == null) return Promise.reject(new Error('Missing lat/lng or events root'));
      var nLat = Math.round(Number(lat) * 100000) / 100000;
      var nLng = Math.round(Number(lng) * 100000) / 100000;
      var ssCacheKey = eventsSessionStorageKey(lat, lng, radiusKm);
      var ssHit = readEventsCache(ssCacheKey);
      if (ssHit && ssHit.payload) {
        wbPerf(cycleId, requestId, 'session_cache_hit', null, { key: ssCacheKey || '' });
        return Promise.resolve(ssHit.payload).then(function (data) {
          return applyEventsByLatLngPayload(data, lat, lng, locationLabelForEmpty, cycleId, radiusKm, requestId, ssCacheKey, { fromSessionCache: true });
        }).catch(function (err) {
          if (err && err.name === 'AbortError') return Promise.reject(err);
          throw err;
        });
      }
      var cacheKey = cacheKeyWithRadius(nLat, nLng, radiusKm);
      var endpointPath = joinProxyPath(PROXY_BASE, EVENTS_PATH);
      var fetchKey = endpointPath + '|' + nLat + ',' + nLng + '|r' + radiusKm + (productGid ? '|g' + productGid : '') + '|ev1';
      if (window.__WB_EVENTS_IN_FLIGHT[fetchKey]) {
        if (window.WB_DEBUG_FETCH) console.log('[WB_FETCH_DEDUPE] key=' + fetchKey + ' deduped');
        return window.__WB_EVENTS_IN_FLIGHT[fetchKey];
      }
      var expandParams = (mode === 'user') ? '&min_cards=3&auto_expand_max_km=300' : '';
      var qs = 'lat=' + encodeURIComponent(nLat) + '&lng=' + encodeURIComponent(nLng) + '&radius_km=' + encodeURIComponent(radiusKm) + expandParams + '&include_empty_venues=true&request_id=' + encodeURIComponent(requestId) + (productGid ? '&product_gid=' + encodeURIComponent(productGid) : '');
      var url = endpointPath + '?' + qs;
      if (window.WB_DEBUG_FETCH) console.log('[WB_FETCH_DEDUPE] key=' + fetchKey + ' new');
      if (window.WB_DEBUG_FETCH) console.log('[WB_PROXY] base=' + PROXY_BASE + ' path=' + EVENTS_PATH + ' url=' + url);
      if (DEV_MODE) console.log('[WB_EVENTS] url=' + url + ' radius_km=' + radiusKm);
      wbPerf(cycleId, requestId, 'fetch_start', null, { url: url, radius_km: radiusKm, cacheKey: cacheKey || '' });
      var fetchStartPerf = performance.now();
      var promise = fetchWorkshopEvents(qs, { method: 'GET', credentials: 'same-origin', signal: signal, requestId: requestId })
        .then(function (res) {
          var ttfb = performance.now() - fetchStartPerf;
          wbPerf(cycleId, requestId, 'network_ttfb_ms', ttfb, {});
          if (!res.ok) throw new Error('proxy_http_error');
          return res.text();
        })
        .then(function (text) {
          var downloadMs = performance.now() - fetchStartPerf;
          var t0 = performance.now();
          var data;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            showWorkshopFetchError();
            throw new Error('Invalid response');
          }
          var jsonParseMs = performance.now() - t0;
          wbPerf(cycleId, requestId, 'network_download_ms', downloadMs, { bytes: (text && text.length) || 0 });
          wbPerf(cycleId, requestId, 'json_parse_ms', jsonParseMs, {});
          if (!data || data.ok !== true) {
            console.warn('[workshop-events]', data && data.error_code, data && data.message);
            showWorkshopFetchError(data && data.message ? data.message : undefined);
            throw new Error('Invalid response');
          }
          if (data.meta && data.meta.debug && data.meta.debug.rid) {
            wbPerf(cycleId, requestId, 'backend_rid', null, { rid: data.meta.debug.rid });
          }
          logBackendRidIfPresent(requestId, data);
          // Prove whether client sent correct radius or backend clamped
          if (DEV_MODE) {
            var respRadius = (data.meta && data.meta.radius_used_km != null) ? data.meta.radius_used_km : ((data.meta && data.meta.debug && data.meta.debug.radius_used_km != null) ? data.meta.debug.radius_used_km : '(not in response)');
            var backendRid = (data.meta && data.meta.debug && data.meta.debug.rid) || '(none)';
            console.log('[WB_RADIUS] request_id=' + requestId + ' backend_rid=' + backendRid + ' radius_requested=' + radiusKm + ' radius_used=' + respRadius);
          }
          return data;
        })
        .then(function (data) {
          return applyEventsByLatLngPayload(data, lat, lng, locationLabelForEmpty, cycleId, radiusKm, requestId, ssCacheKey, null);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return Promise.reject(err);
          throw err;
        });
      window.__WB_EVENTS_IN_FLIGHT[fetchKey] = promise;
      promise.finally(function () { delete window.__WB_EVENTS_IN_FLIGHT[fetchKey]; });
      return promise;
    }

    /**
     * Single entry point for every location-based fetch. Radius is always from getRadiusKm at call sites.
     * @param {Element} bookingRoot — the booking component root element (explicit, not closure).
     * @param {number} lat
     * @param {number} lng
     * @param {number} radiusKm — from getRadiusKm(bookingRoot) at every call site.
     * @param {Object} addressParts — { label, source }
     */
    function fetchEventsByLatLng(bookingRoot, lat, lng, radiusKm, addressParts) {
      if (lat == null || lng == null || !Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) return;
      var source = (addressParts && addressParts.source) || 'geolocation';
      var label = (addressParts && addressParts.label) || (lat + ', ' + lng);
      var forceRefresh = (source === 'geolocation' || source === 'radius_change');

      if (!Number.isFinite(radiusKm) || radiusKm <= 0) radiusKm = getRadiusKm(bookingRoot);
      var isInitial = (source === 'default');
      var fetchMode = isInitial ? 'initial' : 'user';
      if (window.__WB_DEBUG__) console.debug('[WB] fetch', { lat: Number(lat), lng: Number(lng), radiusKm: radiusKm, source: source, label: label });
      if (window.WB_DEBUG_FETCH) console.log('[WB_FETCH_MODE] mode=' + fetchMode + ' source=' + source + ' radius_km=' + radiusKm);
      var cycleId = ++workshopPerfCycleId;
      if (DEV_MODE) console.log('[WB_RADIUS] action source=' + source + ' selected_radius=' + radiusKm + ' force=' + forceRefresh);
      wbPerf(cycleId, null, 'ui_action', null, { source: source, label: label, lat: lat, lng: lng, radius_km: radiusKm });

      // Cache key includes the radius that was resolved for this action
      var newCacheKey = cacheKeyWithRadius(lat, lng, radiusKm);
      var prevCacheKey = (typeof window.__WB_LAST_CACHE_KEY__ !== 'undefined') ? String(window.__WB_LAST_CACHE_KEY__) : '';

      // RESET: on new location OR forced refresh, clear UI and show loading
      var isNewLocation = !prevCacheKey || prevCacheKey !== newCacheKey;
      var preserveWbAllListSelection =
        bookingRoot &&
        bookingRoot.closest &&
        bookingRoot.closest('#wb-sheet-body') &&
        document.querySelector('#wb-event-list .wb-all__event-card.is-selected');
      if (isNewLocation || forceRefresh) {
        if (!preserveWbAllListSelection) {
          clearEventSelection();
        }
        wbwDispatchVenueReset();
        eventsRoot.innerHTML = '';
        window.__WORKSHOP_VENUE_LIST__ = null;
        if (forceRefresh && newCacheKey && workshopEventsCache[newCacheKey]) {
          delete workshopEventsCache[newCacheKey];
        }
        setLoading(booking, true);
        wbPerf(cycleId, null, 'placeholder_loading', null, {});
      }

      window.__WB_LAST_RENDER_SOURCE__ = 'LOCATION';
      window.__WORKSHOP_LOCATION__ = { lat: Number(lat), lng: Number(lng), label: label, city: '', state: '', state_code: '', country: 'US', source: source };
      wbSetUserLoc(lat, lng);
      window.__WB_LAST_CACHE_KEY__ = newCacheKey;
      if (source === 'default') {
        wbSetLocationUi({ isDefault: true });
      } else {
        if (typeof window !== 'undefined') window.__WB_USER_CHOSEN_LOCATION__ = true;
        wbSetLocationUi({ label: label });
      }
      wbState = 1;
      setWbState(1);
      if (preserveWbAllListSelection && typeof validateSeatSelection === 'function') {
        validateSeatSelection();
      }

      // Skip fetch only when location+radius are unchanged, not forced, and UI already populated
      if (!isNewLocation && !forceRefresh && window.__WORKSHOP_VENUE_LIST__ && eventsRoot.children.length > 0) {
        if (DEV_MODE) console.log('[WB_RADIUS] skipping fetch — same location+radius, UI populated');
        return;
      }

      if (newCacheKey && workshopEventsCache[newCacheKey] && workshopEventsCache[newCacheKey].venueList) {
        wbPerf(cycleId, null, 'cache_check', null, { hit: true, cacheKey: newCacheKey });
        window.__WORKSHOP_VENUE_LIST__ = workshopEventsCache[newCacheKey].venueList;
        setLoading(booking, false);
        renderVenueCentricList(window.__WORKSHOP_VENUE_LIST__);
        trySelectEventFromUrl();
        return;
      }
      wbPerf(cycleId, null, 'cache_check', null, { hit: false, cacheKey: newCacheKey || '' });

      // ── Manage AbortController + requestId HERE, pass them to the endpoint function. ──
      if (latLngAbortController) {
        if (DEV_MODE) console.log('[WB_RADIUS] request_id=' + workshopFetchRequestId + ' aborted=true');
        wbPerf(cycleId, workshopFetchRequestId, 'fetch_aborted', null, {});
        latLngAbortController.abort();
      }
      latLngAbortController = new AbortController();
      var requestId = ++workshopFetchRequestId;
      var signal = latLngAbortController.signal;

      // Definitive pre-fetch log with all correlation fields
      if (DEV_MODE) {
        var preUrl = joinProxyPath(PROXY_BASE, EVENTS_PATH) + '?lat=' + Number(lat) + '&lng=' + Number(lng) + '&radius_km=' + radiusKm + '&include_empty_venues=true&request_id=' + requestId;
        console.log('[WB_RADIUS] request_id=' + requestId + ' source=' + source + ' selected_radius=' + radiusKm + ' url=' + preUrl);
      }

      setLoading(booking, true);
      wbPerf(cycleId, null, 'placeholder_loading', null, {});
      fetchEventsByLatLngEndpoint(lat, lng, label, cycleId, radiusKm, requestId, signal, fetchMode).catch(function (err) {
        if (requestId !== workshopFetchRequestId) return;
        setLoading(booking, false);
        if (err && err.name === 'AbortError') {
          if (DEV_MODE) console.log('[WB_RADIUS] request_id=' + requestId + ' aborted=true (superseded)');
          return;
        }
        wbwDispatchVenueReset();
        var root = booking.querySelector('[data-workshop-events]');
        if (root) root.innerHTML = '';
        var phErr = booking.querySelector('[data-workshop-events-placeholder]');
        if (phErr) { phErr.classList.remove('pdp-wb-hidden'); phErr.textContent = "Couldn't load sessions. Try again."; }
        if (booking.classList && booking.classList.contains('wb-wizard')) {
          try {
            wbRefreshBookingGuideUi();
          } catch (_) {}
        }
      });
    }
    booking.__wbFetchByLatLng = function (lat, lng, addressParts) {
      fetchEventsByLatLng(booking, lat, lng, getRadiusKm(booking), addressParts);
    };
    booking.__wbTriggerDefaultPrefetch = triggerDefaultPrefetch;
    if (typeof window !== 'undefined') {
      window.__WB_fetchEventsByLatLng = booking.__wbFetchByLatLng;
      window.__WB_ACTIVE_BOOKING_ROOT__ = booking;
    }

    // ── Default prefetch: if no location selected yet and no lat/lng in URL, fetch default location ──
    function triggerDefaultPrefetch(opts) {
      var force = !!(opts && opts.force);
      /** List page + bottom sheet: booking is cloned into #wb-sheet-body; default Auckland fetch runs after hydrate and calls clearEventSelection + setWbState(1), which disables CTAs until qty is touched. Skip prefetch for this instance (list card already drives selection). */
      if (booking && booking.closest && booking.closest('#wb-sheet-body')) return;
      if (!force && typeof window !== 'undefined' && window.__WB_USER_CHOSEN_LOCATION__) return;
      var prevLoc = window.__WORKSHOP_LOCATION__;
      var hasLocation = prevLoc && prevLoc.lat != null && prevLoc.lng != null && Number.isFinite(Number(prevLoc.lat)) && Number.isFinite(Number(prevLoc.lng));
      if (!force && hasLocation) return;
      var params = new URLSearchParams(window.location.search);
      if (!force && params.get('lat') && params.get('lng')) return;
      if (!eventsRoot) return;
      // Guard: avoid duplicate default fetch when init runs from both DOMContentLoaded and section:load
      var now = Date.now();
      if (!force && window.__WB_DEFAULT_PREFETCH_AT != null && (now - window.__WB_DEFAULT_PREFETCH_AT) < 3000) return;
      window.__WB_DEFAULT_PREFETCH_AT = now;
      if (!Number.isFinite(initialFetchLat) || !Number.isFinite(initialFetchLng)) return;
      // Do not overwrite location input on load; user types address without it being cleared
      setTimeout(function () {
        fetchEventsByLatLng(booking, initialFetchLat, initialFetchLng, initialFetchRadiusKm, { label: 'Configured default', source: 'default' });
      }, 0);
    }
    (function defaultPrefetch() {
      triggerDefaultPrefetch({ force: false });
    })();

    (function startDefaultInputGuard() {
      // Guard disabled: was overwriting location input while user typed. No automatic "restore default" on empty.
    })();

    async function handlePlaceSelect(ev) {
      if (window.__WB_DIAG__) console.log('[WB_DIAG] place_select fired', ev && ev.type, ev && ev.detail ? Object.keys(ev.detail) : null);
      var placeEl = document.querySelector('#workshop-location-place-autocomplete,[data-workshop-place-autocomplete]');
      var detail = ev && (ev.detail || ev);
      
      // 1. PRIORITY: Get text from placePrediction first (most reliable source)
      var predictionText = '';
      if (detail && detail.placePrediction) {
        predictionText = (detail.placePrediction.text && detail.placePrediction.text.text) 
          || detail.placePrediction.text 
          || detail.placePrediction.displayName 
          || '';
        if (typeof predictionText === 'object' && predictionText.text) predictionText = predictionText.text;
        predictionText = String(predictionText || '').trim();
        if (window.__WB_DIAG__) console.log('[WB_DIAG] placePrediction.text', predictionText);
      }

      // ── 2. Get Place with coordinates ──
      // RULE: place starts null. NEVER assign a non-Place object (like ev.detail).
      var place = null;

      // 2a. placePrediction.toPlace() + fetchFields (gmp-select event)
      if (detail && detail.placePrediction && typeof detail.placePrediction.toPlace === 'function') {
        if (window.__WB_DIAG__) console.log('[WB_DIAG] placePrediction detected');
        try {
          place = detail.placePrediction.toPlace();
          if (place && typeof place.then === 'function') place = await place;
          if (window.__WB_DIAG__) console.log('[WB_DIAG] toPlace resolved', !!place);
          if (place && typeof place.fetchFields === 'function' && !place.__wbFieldsFetched) {
            await place.fetchFields({ fields: ['location'] });
            place.__wbFieldsFetched = true;
            if (window.__WB_DIAG__) {
              var fLat = null, fLng = null;
              if (place.location) { fLat = typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat; fLng = typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng; }
              console.log('[WB_DIAG] fetchFields resolved: lat=' + fLat + ', lng=' + fLng);
            }
          }
        } catch (e) {
          if (window.__WB_DIAG__) console.warn('[WB_DIAG] placePrediction.toPlace()/fetchFields error', e);
          place = null;
        }
      }

      // 2b. detail.place (gmp-placeselect event) — NEVER use bare detail
      if (!place && detail && detail.place) {
        place = detail.place;
        if (window.__WB_DIAG__) console.log('[WB_DIAG] using detail.place');
      }

      // 2c. placeEl.getPlace() fallback
      if (!place && placeEl && typeof placeEl.getPlace === 'function') {
        try {
          var gp = placeEl.getPlace();
          if (gp && typeof gp.then === 'function') gp = await gp;
          if (gp) { place = gp; if (window.__WB_DIAG__) console.log('[WB_DIAG] using placeEl.getPlace()'); }
        } catch (e) {}
      }

      // 2d. target.getPlace() fallback
      if (!place) {
        var target = ev && ev.target;
        if (target && typeof target.getPlace === 'function') {
          try {
            var tp = target.getPlace();
            if (tp && typeof tp.then === 'function') tp = await tp;
            if (tp) { place = tp; if (window.__WB_DIAG__) console.log('[WB_DIAG] using target.getPlace()'); }
          } catch (e) {}
        }
      }

      // 2e. Ensure place has coordinates — fetchFields for 2b/2c/2d paths
      if (place && typeof place.fetchFields === 'function' && !place.__wbFieldsFetched && !(place.geometry && place.geometry.location) && !place.location) {
        try {
          if (window.__WB_DIAG__) console.log('[WB_DIAG] place missing coords, calling fetchFields');
          await place.fetchFields({ fields: ['location'] });
          place.__wbFieldsFetched = true;
          if (window.__WB_DIAG__) {
            var fLat2 = null, fLng2 = null;
            if (place.location) { fLat2 = typeof place.location.lat === 'function' ? place.location.lat() : place.location.lat; fLng2 = typeof place.location.lng === 'function' ? place.location.lng() : place.location.lng; }
            console.log('[WB_DIAG] fetchFields resolved: lat=' + fLat2 + ', lng=' + fLng2);
          }
        } catch (e) {
          if (window.__WB_DIAG__) console.warn('[WB_DIAG] fetchFields fallback error', e);
        }
      }
      if (place && typeof place.then === 'function') place = await place;
      
      // Get lat/lng early so we can allow new location through even if place_id/text duplicates
      var ll = (place && typeof getLatLngFromPlace === 'function') ? await getLatLngFromPlace(place) : null;
      var prevLoc = window.__WORKSHOP_LOCATION__;
      var hasNewCoords = ll && ll.lat != null && ll.lng != null && (!prevLoc || prevLoc.lat == null || prevLoc.lng == null || Math.abs(prevLoc.lat - ll.lat) > 1e-5 || Math.abs(prevLoc.lng - ll.lng) > 1e-5);
      // Duplicate check: only skip when same place_id AND not a new location (different lat/lng)
      var fp = String((place && (place.placeId || place.place_id || place.id)) || (detail && detail.placePrediction && (detail.placePrediction.placeId || detail.placePrediction.place_id)) || predictionText || '');
      if (fp && fp === (window.__WORKSHOP_PLACE_LAST_ID__ || __WB_lastHandledPlaceFp) && !hasNewCoords) return;
      __WB_lastHandledPlaceFp = fp;
      window.__WORKSHOP_PLACE_LAST_ID__ = fp;

      // 4. Label fallbacks only (parsed / getCityStateFromPlace): improve displayLabel when coords exist, or set label when they do not.
      // fetchEventsByLatLng is only called with ll from getLatLngFromPlace — never with parsed.city / parsed.state_code (those are not API parameters).
      // getCityStateFromPlace runs when comma-parsing predictionText did not yield city+state; it is not on the events-fetch critical path.
      // Priority: predictionText → getCityStateFromPlace(place) → place.formattedAddress
      var label = '';
      var parsed = null;
      var Flow = window.WorkshopLocationFlow;
      
      // Try predictionText first (most reliable)
      if (predictionText && predictionText.indexOf(',') >= 0) {
        parsed = parseCityStateFromFormattedAddress(predictionText);
        label = predictionText;
        if (window.__WB_DIAG__) console.log('[WB_DIAG] parsed from predictionText', parsed);
      }
      
      // If predictionText didn't work, try place object
      if ((!parsed || (!parsed.city && !parsed.state_code)) && place) {
        var placeResult = await getCityStateFromPlace(place);
        if (placeResult && (placeResult.city || placeResult.state_code)) {
          parsed = placeResult;
          label = label || [placeResult.city, placeResult.state || placeResult.state_code].filter(Boolean).join(', ') || placeResult.formattedAddress || '';
          if (window.__WB_DIAG__) console.log('[WB_DIAG] parsed from getCityStateFromPlace', parsed);
        }
      }
      
      // Final fallback: try formattedAddress
      if ((!parsed || (!parsed.city && !parsed.state_code)) && place) {
        var fa = (place.formattedAddress || place.formatted_address || '').toString().trim();
        if (fa && fa.indexOf(',') >= 0) {
          parsed = parseCityStateFromFormattedAddress(fa);
          label = label || fa;
          if (window.__WB_DIAG__) console.log('[WB_DIAG] parsed from formattedAddress', parsed);
        }
      }
      
      // Ensure we have a label
      label = label || predictionText || 'Location selected';

      // Prefer lat/lng for venue-centric fetch (single source for API)
      if (ll && ll.lat != null && ll.lng != null && Number.isFinite(Number(ll.lat)) && Number.isFinite(Number(ll.lng))) {
        var fallback = (ll && (ll.formattedAddress || '').trim()) ? ll.formattedAddress.trim() : label;
        var displayLabel = getPlaceDisplayLabel(place, fallback) || fallback;
        wbSetLocationUi({ label: displayLabel });
        if (window.__WB_DIAG__) console.log('[WB_DIAG] triggering fetchEventsByLatLng lat=' + ll.lat + ', lng=' + ll.lng + ', radius=' + getRadiusKm(booking));
        fetchEventsByLatLng(booking, Number(ll.lat), Number(ll.lng), getRadiusKm(booking), { label: displayLabel, source: 'places' });
        return;
      }

      // 5. No coordinates available — update UI label but do NOT fetch.
      //    Only valid lat/lng from Place + fetchFields triggers event fetches.
      wbSetLocationUi({ label: label });
      if (window.__WB_DIAG__) console.warn('[WB_DIAG] handlePlaceSelect: no coordinates from Place, skipping fetch', { label: label, place: !!place });
    }

    /** When place/parsed unavailable, widget may set input value async. After delay, read it and trigger fetch if parseable. */
    function scheduleFetchFromInputAfterPlaceSelect(placeEl) {
      var delay = 200;
      setTimeout(function () {
        var displayText = '';
        try {
          if (placeEl && placeEl.shadowRoot) {
            var inner = placeEl.shadowRoot.querySelector('input');
            if (inner && inner.value) displayText = inner.value.trim();
          }
          if (!displayText) {
            var hidden = document.getElementById('workshop-location-input');
            if (hidden && hidden.value) displayText = hidden.value.trim();
          }
          if (!displayText && locSummary && locSummary.textContent) displayText = locSummary.textContent.trim();
        } catch (_) {}
        if (!displayText || displayText === 'Location selected' || displayText.indexOf(',') < 0) return;
        var fallbackParsed = parseCityStateFromFormattedAddress(displayText);
        if (!fallbackParsed || (!fallbackParsed.city && !fallbackParsed.state_code)) return;
        var flowRef = window.WorkshopLocationFlow;
        var cityKey = (flowRef && typeof flowRef.deriveEventCityKey === 'function')
          ? flowRef.deriveEventCityKey(fallbackParsed.state_code, fallbackParsed.city)
          : '';
        var resolved = {
          lat: undefined,
          lng: undefined,
          city: fallbackParsed.city,
          state: fallbackParsed.state,
          state_code: (fallbackParsed.state_code || fallbackParsed.state || '').toString().trim().toUpperCase(),
          country: fallbackParsed.country || 'US',
          source: 'places',
          city_key: cityKey ? String(cityKey).trim() : undefined
        };
        if (window.__WB_DIAG__) console.log('[WB_DIAG] scheduleFetchFromInput: text parsed, no lat/lng — skipping (coordinates required for fetch)', { state_code: resolved.state_code, displayText: displayText });
        // Do NOT call onLocationResolved without lat/lng — it would clear UI and show error.
      }, delay);
    }

    /**
     * Updates location input text. Delegates to wbSetLocationUi only (no direct .value elsewhere).
     * Empty/falsy label → resets to default.
     */
    function setLocationInputTextOnly(label) {
      wbSetLocationUi(label ? { label: label } : { isDefault: true });
    }

    /** @deprecated Use setLocationInputTextOnly or wbSetLocationUi. Kept for backward compatibility. */
    function setPlaceSelectionInInputs(placeEl, label) {
      wbSetLocationUi(label ? { label: label } : { isDefault: true });
    }
    window.__WB_onPlaceSelect = handlePlaceSelect;

    (function startPlaceSelectFallbackPoll() {
      var pollInterval = 1200;
      var lastPlaceId = '';
      setInterval(function () {
        if (typeof window.__WB_onPlaceSelect !== 'function') return;
        var placeEl = document.querySelector('#workshop-location-place-autocomplete,[data-workshop-place-autocomplete]');
        if (!placeEl || typeof placeEl.getPlace !== 'function') return;
        try {
          var p = placeEl.getPlace();
          var place = p && typeof p.then === 'function' ? null : p;
          var nextPlaceId = place ? String(place.id || place.placeId || place.place_id || '') : '';
          if (nextPlaceId && nextPlaceId !== lastPlaceId && nextPlaceId !== (window.__WORKSHOP_PLACE_LAST_ID__ || '')) {
            lastPlaceId = nextPlaceId;
            if (window.__WB_DIAG__) console.log('[WB_DIAG] place select fallback poll triggered', { placeId: nextPlaceId });
            window.__WB_onPlaceSelect({ target: placeEl });
          }
          if (place) lastPlaceId = nextPlaceId;
          if (!place && p && typeof p.then === 'function') {
            p.then(function (resolved) {
              if (!resolved) return;
              var id = String(resolved.id || resolved.placeId || resolved.place_id || '');
              if (id && id !== lastPlaceId && id !== (window.__WORKSHOP_PLACE_LAST_ID__ || '')) {
                lastPlaceId = id;
                if (window.__WB_DIAG__) console.log('[WB_DIAG] place select fallback poll (async) triggered', { placeId: id });
                window.__WB_onPlaceSelect({ target: placeEl });
              }
            }).catch(function () {});
          }
        } catch (_) {}
      }, pollInterval);
    })();

    function fetchCity(cityKey) {
      // Theme: city_key fetch disabled. Only lat/lng + radius_km (events-v) is used.
      if (typeof window !== 'undefined' && (window.__WB_DEV__ === true || window.__WB_DEBUG__ === true)) {
        console.log('[WB_EVENTS] fetchCity(city_key) disabled; not sending request');
      }
      return Promise.reject(new Error('city_key fetch disabled'));

      if (!cityKey || !eventsRoot) return Promise.reject(new Error('Missing city_key or events root'));
      var cacheKey = workshopCacheShop + '|' + (productGid || '') + '|city:' + cityKey;
      var cached = workshopEventsCache[cacheKey];
      if (cached && (Date.now() - cached.ts) < WORKSHOP_CACHE_TTL_MS) {
        var requestId = ++workshopFetchRequestId;
        if (window.__WB_DIAG__) {
          console.log('[WB_DIAG] response meta.count (cache)', cached.data.meta && cached.data.meta.count);
          console.log('[WB_DIAG] events render source', window.__WB_LAST_RENDER_SOURCE__ || 'CITY');
        }
        var events = cached.data.events || [];
        var cityList = getCityList();
        var cityRec = cityList.filter(function (c) { return (c.city_key || '').toString().trim().toLowerCase() === (cityKey || '').toString().trim().toLowerCase(); })[0];
        var clat = cityRec && cityRec.lat != null ? Number(cityRec.lat) : null;
        var clng = cityRec && cityRec.lng != null ? Number(cityRec.lng) : null;
        var venueList = buildVenueListFromEvents(events, clat, clng);
        window.__WORKSHOP_VENUE_LIST__ = venueList;
        if (eventsPlaceholder) eventsPlaceholder.classList.add('pdp-wb-hidden');
        renderVenueCentricList(venueList);
        trySelectEventFromUrl();
        if (window.__WB_DIAG__) console.log('[workshop] cache hit city', cityKey);
        return Promise.resolve(cached.data);
      }
      if (cityAbortController) cityAbortController.abort();
      cityAbortController = new AbortController();
      var requestId = ++workshopFetchRequestId;
      var signal = cityAbortController.signal;
      var qs = 'city_key=' + encodeURIComponent(cityKey) + (productGid ? '&product_gid=' + encodeURIComponent(productGid) : '');
      if (window.__WB_DIAG__) {
        window.__WB_LAST_FETCH_ENDPOINT__ = 'city_key';
        console.log('[WB_DIAG] endpoint', 'city_key', 'url', joinProxyPath(PROXY_BASE, EVENTS_PATH) + '?' + qs);
      }
      return fetchWorkshopEvents(qs, { method: 'GET', credentials: 'same-origin', signal: signal, requestId: requestId })
        .then(function (res) {
          if (!res.ok) throw new Error('proxy_http_error');
          return res.text();
        })
        .then(function (text) {
          var data;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            showWorkshopFetchError();
            throw new Error('Invalid response');
          }
          if (!data || data.ok !== true) {
            console.warn('[workshop-events]', data && data.error_code, data && data.message);
            showWorkshopFetchError(data && data.message ? data.message : undefined);
            throw new Error('Invalid response');
          }
          logBackendRidIfPresent(requestId, data);
          return data;
        })
        .then(function (data) {
          if (requestId !== workshopFetchRequestId) return data;
          var events = data.events || [];
          if (events.length > 0) workshopEventsCache[cacheKey] = { data: data, ts: Date.now() };
          if (window.__WB_DIAG__) {
            console.log('[WB_DIAG] response meta.count', data.meta && data.meta.count);
            console.log('[WB_DIAG] events render source', window.__WB_LAST_RENDER_SOURCE__ || 'CITY');
          }
          var cityList = getCityList();
          var cityRec = cityList.filter(function (c) { return (c.city_key || '').toString().trim().toLowerCase() === (cityKey || '').toString().trim().toLowerCase(); })[0];
          var clat = cityRec && cityRec.lat != null ? Number(cityRec.lat) : null;
          var clng = cityRec && cityRec.lng != null ? Number(cityRec.lng) : null;
          var venueList = buildVenueListFromEvents(events, clat, clng);
          window.__WORKSHOP_VENUE_LIST__ = venueList;
          if (eventsPlaceholder) eventsPlaceholder.classList.add('pdp-wb-hidden');
          renderVenueCentricList(venueList);
          trySelectEventFromUrl();
          return data;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return Promise.reject(err);
          throw err;
        });
    }

    /**
     * Legacy: fetchState is no longer used in the venue-centric flow.
     * All fetch paths now go through lat/lng via fetchEventsByLatLng.
     * This function is kept for backward compatibility but updated to use venue-centric rendering.
     */
    function fetchState(stateCode, isPrimary) {
      if (!stateCode || !eventsRoot) return Promise.reject(new Error('Missing state_code or events root'));
      var primary = isPrimary !== false;
      var cacheKey = workshopCacheShop + '|' + (productGid || '') + '|state:' + stateCode.toUpperCase();
      var cached = workshopEventsCache[cacheKey];
      if (cached && (Date.now() - cached.ts) < WORKSHOP_CACHE_TTL_MS) {
        if (primary) {
          if (window.__WB_DIAG__) console.log('[WB_DIAG] fetchState cache hit', stateCode);
          // Venue-centric rendering for cache hit
          var venueList = buildVenueListFromEvents(cached.data.events || [], null, null);
          window.__WORKSHOP_VENUE_LIST__ = venueList;
          if (eventsPlaceholder) eventsPlaceholder.classList.add('pdp-wb-hidden');
          renderVenueCentricList(venueList);
          trySelectEventFromUrl();
        }
        return Promise.resolve(cached.data);
      }
      if (primary && stateAbortController) stateAbortController.abort();
      if (primary) stateAbortController = new AbortController();
      var requestId = ++workshopFetchRequestId;
      var signal = primary && stateAbortController ? stateAbortController.signal : undefined;
      var qs = 'state_code=' + encodeURIComponent(stateCode) + (productGid ? '&product_gid=' + encodeURIComponent(productGid) : '');
      if (window.__WB_DIAG__) console.log('[WB_DIAG] endpoint state_code', stateCode, 'primary', primary);
      var fetchOpts = { method: 'GET', credentials: 'same-origin', requestId: requestId };
      if (signal) fetchOpts.signal = signal;
      return fetchWorkshopEvents(qs, fetchOpts)
        .then(function (res) {
          if (!res.ok) throw new Error('proxy_http_error');
          return res.text();
        })
        .then(function (text) {
          var data;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            showWorkshopFetchError();
            throw new Error('Invalid response');
          }
          if (!data || data.ok !== true) {
            console.warn('[workshop-events]', data && data.error_code, data && data.message);
            showWorkshopFetchError(data && data.message ? data.message : undefined);
            throw new Error('Invalid response');
          }
          logBackendRidIfPresent(requestId, data);
          return data;
        })
        .then(function (data) {
          if (requestId !== workshopFetchRequestId) return data;
          var events = data.events || [];
          if (events.length > 0) workshopEventsCache[cacheKey] = { data: data, ts: Date.now() };
          if (primary) {
            // Venue-centric rendering
            var venueList = buildVenueListFromEvents(events, null, null);
            window.__WORKSHOP_VENUE_LIST__ = venueList;
            if (eventsPlaceholder) eventsPlaceholder.classList.add('pdp-wb-hidden');
            renderVenueCentricList(venueList);
            trySelectEventFromUrl();
          }
          return data;
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return Promise.reject(err);
          throw err;
        });
    }

    /** Date from starts_at: wall-clock only. Extract YYYY-MM-DD from string; never use Date/toISOString (no timezone conversion). */
    function dayFromStartsAt(ev) {
      try {
        var s = (ev && ev.starts_at != null) ? String(ev.starts_at) : '';
        if (!s) return '';
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
        return '';
      } catch (e) { return ''; }
    }

    /** Extract HH:mm or HH:mm:ss from starts_at string (handles T, space, or regex). */
    function extractTimeFromStartsAt(st) {
      if (st == null) return '';
      var s = typeof st === 'string' ? st.trim() : '';
      if (!s) return '';
      var sep = s.length > 10 ? s.charAt(10) : '';
      if (sep === 'T' || sep === ' ') {
        if (s.length >= 19) return s.slice(11, 8);
        if (s.length >= 16) return s.slice(11, 5);
      }
      var match = s.match(/\d{1,2}:\d{2}(?::\d{2})?/);
      return match ? match[0] : '';
    }
    /** From a range time_label like "10:00-12:30" or "10-12.30", return start part as HH:mm for display. */
    function extractStartTimeFromRange(timeLabel) {
      if (!timeLabel || typeof timeLabel !== 'string') return '';
      var t = timeLabel.trim();
      var i = t.indexOf('-');
      if (i <= 0) return '';
      var startPart = t.slice(0, i).trim();
      if (!startPart) return '';
      var match = startPart.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (match) return match[1] + ':' + match[2] + (match[3] != null ? ':' + match[3] : '');
      var hourOnly = startPart.match(/^(\d{1,2})$/);
      if (hourOnly) {
        var h = parseInt(hourOnly[1], 10);
        if (h >= 0 && h <= 23) return (h < 10 ? '0' : '') + h + ':00';
      }
      return '';
    }
    /** Convert 24h string (HH:mm or HH:mm:ss) to 12h (e.g. 02:00 PM). */
    function time24hTo12h(s) {
      if (!s || typeof s !== 'string') return '';
      var parts = s.trim().split(':');
      var h = parseInt(parts[0], 10);
      var m = (parts.length >= 2 && parts[1]) ? parseInt(parts[1], 10) : 0;
      if (isNaN(h) || h < 0 || h > 23 || isNaN(m) || m < 0 || m > 59) return '';
      var hour12 = h % 12;
      if (hour12 === 0) hour12 = 12;
      var ampm = h < 12 ? 'AM' : 'PM';
      var mm = (m < 10 ? '0' : '') + m;
      return (hour12 < 10 ? '0' : '') + hour12 + ':' + mm + ' ' + ampm;
    }

    /** Event time display: ONLY ev.time_label (pure string). Wall-clock: never convert to local; no Date/Intl/toLocale*, no parsing of starts_at. */
    var _workshopTimeLabelWarned = (typeof Set !== 'undefined') ? new Set() : {};
    function getEventDisplayTime(ev) {
      if (!ev || typeof ev !== 'object') return '';
      var t = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
      if (!t) {
        var key = (ev.id != null ? String(ev.id) : '') || (ev.handle != null ? String(ev.handle) : '') || '';
        if (key && !_workshopTimeLabelWarned.has(key)) {
          _workshopTimeLabelWarned.add(key);
          console.warn('[workshop] time_label missing for event', ev.id || ev.handle || '');
        }
      }
      return t;
    }

    /** Date label from date_key (YYYY-MM-DD). Wall-clock: no timezone conversion; display the calendar day as stored. */
    function formatDateLabelFromKey(dateKey) {
      if (!dateKey || typeof dateKey !== 'string') return dateKey || '';
      var parts = dateKey.trim().split('-').map(Number);
      if (parts.length < 3) return dateKey;
      var y = parts[0], m = parts[1], d = parts[2];
      if (!y || !m || !d) return dateKey;
      try {
        return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(y, m - 1, d));
      } catch (_) { return dateKey; }
    }

    function wbCoerceImageUrl(img) {
      if (img == null || img === '') return '';
      if (typeof img === 'object') {
        if (img.url) return String(img.url).trim();
        if (img.src) return String(img.src).trim();
      }
      return String(img).trim();
    }

    /** Venue image from fetch event or nested venue object (includes metaobject-backed fields). */
    function wbVenueImageFromApiEvent(ev) {
      if (!ev || typeof ev !== 'object') return '';
      var venue = ev.venue && typeof ev.venue === 'object' ? ev.venue : {};
      var raw =
        ev.venue_image ||
        ev.venueImage ||
        ev.venue_image_url ||
        venue.venue_image ||
        venue.venueImage ||
        venue.image ||
        ev.cover_image ||
        venue.cover_image ||
        '';
      var u = wbCoerceImageUrl(raw);
      return u;
    }

    function wbVenueImageFromStub(v) {
      if (!v || typeof v !== 'object') return '';
      return wbCoerceImageUrl(
        v.venue_image ||
        v.venueImage ||
        v.image ||
        ''
      );
    }

    function wbNormalizeVenueNameKey(name) {
      return String(name || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[^\w\s\u0100-\u024f-]/g, '');
    }

    /** Cached venue_ref_id / venue_name → image URL from shop metaobject catalog JSON. */
    function wbGetVenueImageCatalogIndex(bookingRoot) {
      if (!bookingRoot) return null;
      if (bookingRoot._wbVenueImageCatalogIndex) return bookingRoot._wbVenueImageCatalogIndex;
      var el = bookingRoot.querySelector('script[type="application/json"][data-wb-venues-waitlist-catalog]');
      if (!el || !el.textContent) return null;
      var rows;
      try {
        rows = JSON.parse(el.textContent);
      } catch (_) {
        return null;
      }
      if (!Array.isArray(rows)) return null;
      var byRef = Object.create(null);
      var byName = Object.create(null);
      for (var i = 0; i < rows.length; i++) {
        var row = rows[i];
        if (!row || typeof row !== 'object') continue;
        var img = wbCoerceImageUrl(row.venue_image);
        if (!img) continue;
        var ref = row.venue_ref_id != null ? String(row.venue_ref_id).trim() : '';
        if (ref) byRef[ref] = img;
        var nk = wbNormalizeVenueNameKey(row.venue_name);
        if (nk) byName[nk] = img;
      }
      bookingRoot._wbVenueImageCatalogIndex = { byRef: byRef, byName: byName };
      return bookingRoot._wbVenueImageCatalogIndex;
    }

    function wbLookupVenueImageFromCatalog(venue, displayName, bookingRoot) {
      var idx = wbGetVenueImageCatalogIndex(bookingRoot);
      if (!idx) return '';
      venue = venue && typeof venue === 'object' ? venue : {};
      var ref = (venue.venue_ref_id || venue.id || '').toString().trim();
      if (ref && idx.byRef[ref]) return idx.byRef[ref];
      var names = [displayName, venue.venue_name, venue.name, venue.city_name, venue.city];
      for (var ni = 0; ni < names.length; ni++) {
        var nk = wbNormalizeVenueNameKey(names[ni]);
        if (nk && idx.byName[nk]) return idx.byName[nk];
      }
      return '';
    }

    /** Best image for venue grid: stub, any event, then shop catalog (fixes Auckland etc.). */
    function wbResolveVenueCardImage(venue, displayName, bookingRoot) {
      if (!venue || typeof venue !== 'object') return '';
      var u = wbVenueImageFromStub(venue);
      if (u) return u;
      var evs = Array.isArray(venue.events) ? venue.events : [];
      for (var ei = 0; ei < evs.length; ei++) {
        u = wbVenueImageFromApiEvent(evs[ei]);
        if (u) {
          venue.venue_image = u;
          return u;
        }
      }
      u = wbLookupVenueImageFromCatalog(venue, displayName, bookingRoot);
      if (u) venue.venue_image = u;
      return u;
    }

    function wbEnrichVenueListImagesFromCatalog(venueList, bookingRoot) {
      if (!venueList || !bookingRoot || !Array.isArray(venueList.groups)) return;
      venueList.groups.forEach(function (g) {
        (g.venues || []).forEach(function (v) {
          if (!v || typeof v !== 'object') return;
          if (wbVenueImageFromStub(v)) return;
          var dn = (v.venue_name || v.name || v.city_name || '').toString().trim();
          wbResolveVenueCardImage(v, dn, bookingRoot);
        });
      });
    }

    function wbWizardVenueCardMediaHtml(imgUrl) {
      return imgUrl
        ? '<span class="wbw-venue-card__media"><img src="' +
            escapeHtml(imgUrl) +
            '" alt="" loading="lazy" decoding="async" /></span>'
        : '<span class="wbw-venue-card__media wbw-venue-card__media--empty" aria-hidden="true"></span>';
    }

    function wbWizardVenueCardCaptionHtml(lineCity, kmLine) {
      return (
        '<span class="wbw-venue-card__caption">' +
        '<span class="wbw-venue-card__name">' +
        escapeHtml(lineCity) +
        '</span>' +
        (kmLine ? '<span class="wbw-venue-card__km">' + escapeHtml(kmLine) + '</span>' : '') +
        '</span>'
      );
    }

    function wbWizardVenueGridCardInnerHtml(lineCity, kmLine, imgUrl, flatIsWaitlist) {
      var front = wbWizardVenueCardMediaHtml(imgUrl) + wbWizardVenueCardCaptionHtml(lineCity, kmLine);
      if (!flatIsWaitlist) return front;
      return (
        '<span class="wbw-venue-flip__inner">' +
        '<span class="wbw-venue-flip__face wbw-venue-flip__face--front">' +
        front +
        '</span>' +
        '<span class="wbw-venue-flip__face wbw-venue-flip__face--back">' +
        '<span class="wbw-venue-flip__back-name">' +
        escapeHtml(lineCity) +
        '</span>' +
        '<span class="wbw-venue-flip__back-cta-wrap">' +
        '<button type="button" class="wbw-venue-wl-cta" data-wbw-wl-signup>Sign up for Waitlist</button>' +
        '</span>' +
        '</span>' +
        '</span>'
      );
    }

    function normalizeApiEvent(ev) {
      if (!ev || typeof ev !== 'object') return null;
      var statusEarly = wbResolveEventStatus(ev);
      if (statusEarly === 'draft' || statusEarly === 'completed') return null;
      const venue = ev.venue && typeof ev.venue === 'object' ? ev.venue : {};
      /** date_key: use ev.date_key when present; else derive from starts_at (wall-clock string). When both date_key and time_label are set, starts_at is ignored. */
      let dateKey = (ev.date_key || '').toString().trim();
      if (!dateKey && ev.starts_at) {
        try {
          var st = (ev.starts_at || '').toString();
          if (/^\d{4}-\d{2}-\d{2}/.test(st)) dateKey = st.slice(0, 10);
        } catch (_) {}
      }
      /** time_label: use only if "11:00 AM" or single 24h (e.g. 14:00). Ranges (10:00-12:30) are ignored; fallback to starts_at. If starts_at empty → show "-". */
      function isTimeLabelAmPmFormat(s) {
        if (!s || typeof s !== 'string') return false;
        return /^\s*\d{1,2}:\d{2}(:\d{2})?\s*(AM|PM)\s*$/i.test(s.trim());
      }
      function isTimeLabel24h(s) {
        if (!s || typeof s !== 'string') return false;
        var t = s.trim();
        return /^\d{1,2}:\d{2}(:\d{2})?$/.test(t) && t.indexOf('-') === -1;
      }
      var timeLabel = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
      if (isTimeLabelAmPmFormat(timeLabel)) {
        /* use as-is */
      } else if (isTimeLabel24h(timeLabel)) {
        timeLabel = time24hTo12h(timeLabel);
      } else {
        timeLabel = '';
        if (ev.starts_at) {
          try {
            var rawTime = extractTimeFromStartsAt(ev.starts_at);
            if (rawTime) timeLabel = time24hTo12h(rawTime);
          } catch (_) {}
        }
        if (!timeLabel && ev.time_label) {
          var rangeStart = extractStartTimeFromRange(ev.time_label);
          if (rangeStart) timeLabel = time24hTo12h(rangeStart);
        }
      }
      const statusVal = wbResolveEventStatus(ev);
      const capRem = ev.capacity_remaining != null && ev.capacity_remaining !== '' ? Number(ev.capacity_remaining) : null;
      const capTot = ev.capacity_total != null && ev.capacity_total !== '' ? Number(ev.capacity_total) : null;
      const availVariants = Array.isArray(ev.available_variants) ? ev.available_variants : (Array.isArray(ev.availableVariants) ? ev.availableVariants : []);
      const variantId = ev.variant_id != null && ev.variant_id !== '' ? ev.variant_id : (availVariants[0] && (availVariants[0].id != null || availVariants[0].variant_id != null)) ? (availVariants[0].id || availVariants[0].variant_id) : null;
      const productHandle = (ev.product_handle || ev.productHandle || (ev.product && ev.product.handle) || '').toString().trim() || null;
      const linkEnabled = ev.link_enabled !== false && !!productHandle;
      var venueLat = wbFirstCoord(ev.venue_lat, ev.venue_latitude, ev.lat, ev.latitude, venue.lat, venue.latitude);
      var venueLng = wbFirstCoord(ev.venue_lng, ev.venue_longitude, ev.lng, ev.longitude, venue.lng, venue.longitude);
      var cityName = (ev.city_name || venue.city_name || ev.city || venue.city || '').toString().trim();
      if (window.__WB_DEBUG__) console.debug('[WB NORM]', {
        name: ev.venue_name || ev.name || venue.name || '',
        city_key: ev.city_key || venue.city_key || '',
        resolvedLat: venueLat, resolvedLng: venueLng,
        raw: { ev_venue_lat: ev.venue_lat, ev_lat: ev.lat, ev_latitude: ev.latitude, v_lat: venue.lat, v_latitude: venue.latitude,
               ev_venue_lng: ev.venue_lng, ev_lng: ev.lng, ev_longitude: ev.longitude, v_lng: venue.lng, v_longitude: venue.longitude }
      });
      return {
        id: ev.id,
        handle: ev.handle,
        variant_id: variantId,
        available_variants: availVariants,
        event_metaobject_gid: String(ev.event_metaobject_gid || ev.id || ''),
        product_handle: productHandle,
        link_enabled: linkEnabled,
        state: ev.state || venue.state || '',
        state_code: ev.state_code || venue.state_code || '',
        city: ev.city || venue.city || '',
        city_name: cityName || null,
        city_key: (ev.city_key || venue.city_key || '').toString().trim(),
        status: statusVal,
        event_name: ev.event_name || ev.name || '',
        venue_name: ev.venue_name || venue.name || venue.title || '',
        venue_handle: (ev.venue_handle || (venue && venue.handle) || '').toString().trim(),
        venue_address: ev.venue_address || venue.address || '',
        venue_phone: ev.venue_phone || venue.phone || '',
        venue_work_hours: ev.venue_work_hours || venue.work_hours || '',
        venue_tags: (function () {
          var t = ev.venue_tags != null ? ev.venue_tags : (venue && venue.tags);
          if (Array.isArray(t)) return t.map(function (x) { return (x != null && x !== '') ? String(x).trim() : ''; }).filter(Boolean);
          if (t != null && t !== '') return [String(t).trim()];
          return [];
        })(),
        venue_ref_id: ev.venue_ref_id || venue.id || '',
        date_key: dateKey,
        starts_at: ev.starts_at || '',
        capacity_remaining: capRem,
        capacity_total: capTot,
        venue_image: wbVenueImageFromApiEvent(ev),
        time_label: timeLabel,
        venue_lat: (venueLat != null && Number.isFinite(venueLat)) ? venueLat : null,
        venue_lng: (venueLng != null && Number.isFinite(venueLng)) ? venueLng : null,
        also_available_products: Array.isArray(ev.also_available_products) ? ev.also_available_products : []
      };
    }

    function productUrlFromEvent(event) {
      const enabled = event && event.link_enabled !== false;
      const handle = (event && (event.product_handle || event.productHandle || (event.product && event.product.handle) || '')) || null;
      const h = (handle && String(handle).trim()) || null;
      if (!enabled || !h) return null;
      return '/products/' + h;
    }

    /** Weekday from date_key (YYYY-MM-DD) only; no timezone. */
    /** Get weekday name from date_key (YYYY-MM-DD). UTC-safe: uses Date.UTC to avoid timezone shifts. */
    function getWeekdayFromDateKey(dateKey) {
      if (!dateKey || typeof dateKey !== 'string') return '';
      var parts = dateKey.trim().split('-').map(Number);
      if (parts.length < 3) return '';
      var y = parts[0], m = parts[1], d = parts[2];
      if (!y || !m || !d) return '';
      try {
        // Use Date.UTC to create a UTC timestamp, then getUTCDay() for timezone-safe weekday
        var utcDate = new Date(Date.UTC(y, m - 1, d));
        var day = utcDate.getUTCDay();
        var names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        return names[day] || '';
      } catch (_) { return ''; }
    }

    /** Venue-centric date label: WEEKDAY • YYYY-MM-DD (from date_key only). Wall-clock: no timezone conversion. */
    function formatDateLabelVenueCentric(dateKey) {
      if (!dateKey || typeof dateKey !== 'string') return dateKey || '';
      var weekday = getWeekdayFromDateKey(dateKey);
      return (weekday ? weekday + ' • ' : '') + dateKey.trim();
    }

    // near_cities rendering functions removed: venue-centric UI does not use them

    function ensureCrossProductStyles() {
      if (document.getElementById('wb-cross-product-styles')) return;
      var style = document.createElement('style');
      style.id = 'wb-cross-product-styles';
      style.textContent = '.wb-cross-product{font-size:11px;color:rgba(0,0,0,.6);margin-top:6px;padding-top:6px;border-top:1px solid rgba(0,0,0,.08);}.wb-cross-product__label{display:block;margin-bottom:4px;}.wb-cross-product__list{display:flex;flex-wrap:wrap;gap:8px;align-items:center;}.wb-cross-product__item{display:inline-flex;align-items:center;gap:6px;text-decoration:none;color:inherit;border-radius:6px;padding:2px 6px;transition:background .15s,text-decoration .15s;}.wb-cross-product__item:hover{text-decoration:underline;background:rgba(0,0,0,.04);}.wb-cross-product__img{width:20px;height:20px;border-radius:4px;object-fit:cover;background:rgba(0,0,0,.06);}.wb-cross-product--booking{margin-top:12px;padding-top:10px;}.wb-cross-product--booking .wb-cross-product__img{width:22px;height:22px;}';
      document.head.appendChild(style);
    }

    function ensureWaitlistStyles() {
      if (document.getElementById('wb-waitlist-styles-v10')) return;
      ['wb-waitlist-styles-v9', 'wb-waitlist-styles-v8', 'wb-waitlist-styles-v7', 'wb-waitlist-styles-v6', 'wb-waitlist-styles-v5', 'wb-waitlist-styles-v4', 'wb-waitlist-styles-v3', 'wb-waitlist-styles-v2', 'wb-waitlist-styles'].forEach(function (id) {
        var o = document.getElementById(id);
        if (o && o.parentNode) o.parentNode.removeChild(o);
      });
      var st = document.createElement('style');
      st.id = 'wb-waitlist-styles-v10';
      st.textContent =
        '.wb-waitlist-actions{display:flex;flex-direction:column;gap:10px;align-items:flex-start;max-width:100%;}' +
        '.wb-waitlist-btn{margin-top:4px;padding:10px 16px;border-radius:6px;border:1px solid rgba(var(--color-foreground),0.2);background:rgb(var(--color-background));cursor:pointer;font:inherit;}' +
        '.wb-waitlist-btn:disabled{opacity:0.6;cursor:not-allowed;}' +
        '.wb-waitlist-form{display:none;flex-direction:column;gap:8px;width:100%;margin-top:8px;}' +
        '.wb-waitlist-form.is-open{display:flex;}' +
        '.wb-waitlist-form input{padding:8px 10px;border:1px solid rgba(var(--color-foreground),0.15);border-radius:4px;font:inherit;width:100%;max-width:280px;box-sizing:border-box;}' +
        '.wb-waitlist-modal{position:fixed;inset:0;z-index:2147483010;display:none;align-items:center;justify-content:center;padding:clamp(16px,4vw,28px);box-sizing:border-box;-webkit-tap-highlight-color:transparent;}' +
        '.wb-waitlist-modal.is-open{display:flex;animation:wb-wl-modal-in 0.24s ease-out;}' +
        '@keyframes wb-wl-modal-in{from{opacity:0.88}to{opacity:1}}' +
        '.wb-waitlist-modal-backdrop{position:absolute;inset:0;background:rgba(15,18,28,0.62);cursor:pointer;-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);}' +
        '.wb-waitlist-modal-dialog{position:relative;z-index:1;width:min(100%,560px);max-width:560px;max-height:none;display:flex;flex-direction:column;overflow:hidden;background:var(--color-base-background-1,rgb(var(--color-background)));color:rgb(var(--color-foreground));border-radius:22px;padding:0;box-shadow:0 4px 6px rgba(15,23,42,0.04),0 24px 48px rgba(15,23,42,0.14),0 1px 0 rgba(255,255,255,0.6) inset;box-sizing:border-box;border:1px solid rgba(var(--color-foreground),0.08);}' +
        '.wb-waitlist-modal-dialog--has-photo{max-width:min(100%,720px);width:min(100%,720px);}' +
        '.wb-waitlist-modal-inner{display:flex;flex-direction:column;flex:1 1 auto;min-width:0;width:100%;align-self:stretch;background:inherit;}' +
        '@media (min-width:700px){.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-inner{flex-direction:row;align-items:stretch;min-height:0;width:100%;}}' +
        '.wb-waitlist-modal-figure{margin:0;padding:0;flex:0 0 auto;display:block;overflow:hidden;background:rgba(var(--color-foreground),0.06);}' +
        '.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-figure{min-height:0;height:auto;align-self:stretch;}' +
        '@media (max-width:699px){.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-figure{max-height:min(32vh,200px);flex-shrink:1;}}' +
        '@media (min-width:700px){.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-figure{flex:0 0 38%;max-width:320px;min-height:100%;min-width:200px;}}' +
        '.wb-waitlist-modal-photo{display:block;width:100%;height:auto;min-height:0;object-fit:cover;aspect-ratio:16/9;}' +
        '@media (max-width:699px){.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-photo{max-height:min(32vh,200px);aspect-ratio:16/9;}}' +
        '@media (min-width:700px){.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-photo{min-height:280px;aspect-ratio:auto;height:100%;}}' +
        '.wb-waitlist-modal-body{background:#fdfbf7;color:#1c1917;padding:clamp(1.35rem,3vw,2rem) clamp(1.25rem,3vw,1.85rem) clamp(1.5rem,2.5vw,1.85rem);flex:1 1 auto;min-width:0;display:flex;flex-direction:column;overflow-x:hidden;overflow-y:visible;align-self:stretch;box-sizing:border-box;}' +
        '@media (min-width:700px){.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-body{flex:1 1 0%;width:auto;}}' +
        '.wb-waitlist-modal-dialog--has-photo .wb-waitlist-modal-body{padding-top:clamp(1.75rem,3.5vw,2.35rem);}' +
        '.wb-waitlist-modal-x{position:absolute;top:12px;right:12px;z-index:4;width:44px;height:44px;border:0;border-radius:999px;background:rgba(15,23,42,0.08);color:#1e293b;font-size:1.35rem;line-height:1;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;transition:background .18s ease,transform .12s ease,box-shadow .18s ease;box-shadow:0 1px 2px rgba(15,23,42,0.06);}' +
        '.wb-waitlist-modal-x:hover{background:rgba(15,23,42,0.12);transform:scale(1.04);}' +
        '.wb-waitlist-modal-x:active{transform:scale(0.96);}' +
        '.wb-waitlist-modal-title{margin:0 2.75rem 1.1rem 0;font-size:clamp(1.2rem,2.6vw,1.45rem);font-weight:700;line-height:1.28;letter-spacing:-0.02em;color:#0f172a;}' +
        '.wb-waitlist-modal-fields{display:flex;flex-direction:column;gap:1rem;margin-bottom:0.25rem;}' +
        '.wb-waitlist-field{display:flex;flex-direction:column;gap:6px;}' +
        '.wb-waitlist-label{font-size:11px;font-weight:700;letter-spacing:0.06em;text-transform:uppercase;color:#475569;margin:0;}' +
        '.wb-waitlist-input{width:100%;box-sizing:border-box;padding:14px 16px;border:1.5px solid rgba(30,41,59,0.18);border-radius:12px;font:inherit;font-size:16px;line-height:1.35;background:#fff;color:#0f172a;box-shadow:0 1px 0 rgba(255,255,255,0.7) inset,0 1px 3px rgba(15,23,42,0.05);transition:border-color .18s ease,box-shadow .18s ease,background .18s ease,transform .1s ease;}' +
        '.wb-waitlist-input::placeholder{color:rgba(var(--color-foreground),0.38);}' +
        '.wb-waitlist-input:hover{border-color:rgba(var(--color-foreground),0.28);background:rgba(255,255,255,0.95);}' +
        '.wb-waitlist-input:focus{outline:none;border-color:rgba(45,41,38,0.32);box-shadow:0 0 0 3px rgba(45,41,38,0.1),0 1px 0 rgba(255,255,255,0.8) inset;}' +
        '.wb-waitlist-input:active{transform:translateY(1px);}' +
        '.wb-waitlist-modal-actions{display:flex;flex-wrap:wrap;gap:10px;align-items:stretch;margin-top:1.1rem;}' +
        '.wb-waitlist-modal .wb-waitlist-btn{position:relative;padding:13px 24px;border-radius:999px;font-weight:600;font-size:15px;min-height:50px;cursor:pointer;border-width:1.5px;border-style:solid;transition:transform .12s ease,box-shadow .2s ease,background .2s ease,border-color .2s ease,color .2s ease,filter .2s ease;}' +
        '.wb-waitlist-modal .wb-waitlist-btn:active:not(:disabled){transform:translateY(2px) scale(0.98);}' +
        '.wb-waitlist-modal .wb-waitlist-btn--primary{border-color:#c9c4bd!important;background:#ebe8e4!important;background-color:#ebe8e4!important;color:#2d2926!important;-webkit-text-fill-color:#2d2926!important;text-shadow:none!important;box-shadow:0 1px 2px rgba(45,41,38,0.08),0 1px 0 rgba(255,255,255,0.65) inset!important;}' +
        '.wb-waitlist-modal .wb-waitlist-btn--primary:hover:not(:disabled){filter:none!important;background:#e3dfd9!important;border-color:#bfb9b2!important;box-shadow:0 2px 10px rgba(45,41,38,0.1),0 1px 0 rgba(255,255,255,0.78) inset!important;}' +
        '.wb-waitlist-modal .wb-waitlist-btn--primary:active:not(:disabled){box-shadow:0 1px 4px rgba(45,41,38,0.08)!important;}' +
        '.wb-waitlist-modal .wb-waitlist-btn--primary:disabled{opacity:0.55;filter:none;box-shadow:none!important;background:#eceae8!important;border-color:#d1cec9!important;color:#78716c!important;-webkit-text-fill-color:#78716c!important;text-shadow:none!important;}' +
        '.wb-waitlist-modal .wb-waitlist-btn--secondary{background:#faf9f8;border-color:#dcd8d2;color:#2d2926;font-weight:600;box-shadow:none;}' +
        '.wb-waitlist-modal .wb-waitlist-btn--secondary:hover:not(:disabled){background:#f0eeeb;border-color:#cfc9c2;color:#2d2926;}' +
        '.wb-waitlist-modal .wb-waitlist-btn--secondary:active:not(:disabled){background:#e8e6e3;}' +
        '.wb-waitlist-msg{margin-top:14px;font-size:15px;line-height:1.5;text-align:left;color:rgba(var(--color-foreground),0.88);padding:12px 14px;border-radius:12px;background:rgba(45,41,38,0.05);border:1px solid rgba(45,41,38,0.1);flex-shrink:0;max-width:100%;box-sizing:border-box;word-wrap:break-word;}' +
        '.wb-waitlist-err{margin-top:12px;color:#b00020;font-size:14px;line-height:1.45;text-align:left;padding:10px 12px;border-radius:10px;background:rgba(176,0,32,0.06);border:1px solid rgba(176,0,32,0.18);flex-shrink:0;max-width:100%;box-sizing:border-box;word-wrap:break-word;}' +
        '.wb-venue-slots-empty-msg{flex:1 1 100%;width:100%;max-width:100%;box-sizing:border-box;margin:12px 0 4px;font-size:14px;line-height:1.5;color:rgba(var(--color-foreground),0.62);text-align:center;padding:14px 12px;border-radius:12px;background:rgba(var(--color-foreground),0.04);border:1px dashed rgba(var(--color-foreground),0.12);}' +
        '.wb-venue-card--waitlist-banner .wb-waitlist-btn.wb-waitlist-btn--card-cta{background:#ebe8e4!important;color:#2d2926!important;border-color:#c9c4bd!important;-webkit-text-fill-color:#2d2926!important;text-shadow:none!important;box-shadow:0 1px 2px rgba(45,41,38,0.08),0 1px 0 rgba(255,255,255,0.55) inset!important;}' +
        '.wb-waitlist-modal .wb-waitlist-btn{flex:1 1 auto;min-width:min(160px,100%);}';
      document.head.appendChild(st);
    }

    function wbSyncWaitlistModalMedia(bookingHost, modalRoot) {
      if (!bookingHost || !modalRoot) return;
      var dlg = modalRoot.querySelector('.wb-waitlist-modal-dialog');
      var fig = modalRoot.querySelector('[data-wb-waitlist-modal-figure]');
      var img = modalRoot.querySelector('[data-wb-waitlist-modal-img]');
      if (!dlg || !fig || !img) return;
      var url = (bookingHost.getAttribute('data-wb-waitlist-modal-image-url') || '').trim();
      var alt = (bookingHost.getAttribute('data-wb-waitlist-modal-image-alt') || '').trim() || 'Workshop';
      if (url) {
        img.src = url;
        img.alt = alt;
        fig.removeAttribute('hidden');
        dlg.classList.add('wb-waitlist-modal-dialog--has-photo');
      } else {
        img.removeAttribute('src');
        img.alt = '';
        fig.setAttribute('hidden', '');
        dlg.classList.remove('wb-waitlist-modal-dialog--has-photo');
      }
    }

    /** Customer Location string for admin (e.g. "Orewa, New Zealand") from the venue card and its group. */
    function wbFormatWaitlistCustomerLocation(venue, group) {
      if (!venue || typeof venue !== 'object') venue = {};
      var addr = (venue.venue_address || '').toString().trim();
      if (addr && /\bnew zealand\b/i.test(addr)) return addr;
      var glabel = group && (group.city_label || '').toString().trim();
      if (glabel && !/^us-[a-z]{2}-/i.test(glabel)) {
        if (/\bnew zealand\b/i.test(glabel)) return glabel;
        return glabel + ', New Zealand';
      }
      var place = (venue.city_name || venue.city || venue.venue_name || '').toString().trim();
      if (!place) return '';
      if (/\bnew zealand\b/i.test(place)) return place;
      return place + ', New Zealand';
    }

    /** Open waitlist modal for an empty venue (has_events false or no events). Shared by Join Waitlist and card-delegation. */
    function wbOpenWaitlistModalForVenue(bookingHost, venueRefId, venueNameForApi, customerLocation) {
      if (
        bookingHost &&
        bookingHost.classList &&
        (bookingHost.classList.contains('wb-wizard') ||
          (bookingHost.closest && bookingHost.closest('[data-workshop-booking-wizard]')))
      ) {
        try {
          document.dispatchEvent(
            new CustomEvent('wbw:open-waitlist-modal', {
              detail: {
                venueRefId: venueRefId,
                venueName: venueNameForApi,
                customerLocation: customerLocation,
                bookingRoot: bookingHost,
              },
            })
          );
        } catch (_) {}
        return;
      }
      var modal = ensureWaitlistModal(bookingHost);
      if (!modal) return;
      wbSyncWaitlistModalMedia(bookingHost, modal);
      var titleEl = modal.querySelector('[data-wb-waitlist-modal-title]');
      var displayName = (venueNameForApi != null ? String(venueNameForApi) : '').trim() || 'this venue';
      if (titleEl) titleEl.textContent = 'Join the waitlist for ' + displayName;
      modal._wbVenueId = venueRefId != null ? String(venueRefId).trim() : '';
      modal._wbVenueTitle = (venueNameForApi != null ? String(venueNameForApi) : '').trim();
      modal._wbCustomerLocation = (customerLocation != null ? String(customerLocation) : '').trim();
      var firstIn = modal.querySelector('[data-wb-waitlist-modal-first-name]');
      var lastIn = modal.querySelector('[data-wb-waitlist-modal-last-name]');
      var emailIn = modal.querySelector('[data-wb-waitlist-modal-email]');
      var subM = modal.querySelector('[data-wb-waitlist-modal-submit]');
      var errM = modal.querySelector('[data-wb-waitlist-modal-error]');
      var okM = modal.querySelector('[data-wb-waitlist-modal-success]');
      if (firstIn) {
        firstIn.value = '';
        firstIn.style.display = '';
      }
      if (lastIn) {
        lastIn.value = '';
        lastIn.style.display = '';
      }
      if (emailIn) {
        emailIn.value = '';
        emailIn.style.display = '';
      }
      if (subM) subM.style.display = '';
      if (errM) {
        errM.textContent = '';
        errM.hidden = true;
      }
      if (okM) okM.hidden = true;
      modal.removeAttribute('hidden');
      modal.classList.add('is-open');
      try {
        if (firstIn) firstIn.focus();
      } catch (e) {}
    }

    /** One centered waitlist modal per booking root; opened from each empty-venue card. */
    function ensureWaitlistModal(bookingHost) {
      if (!bookingHost) return null;
      if (bookingHost._wbWaitlistModal) return bookingHost._wbWaitlistModal;
      ensureWaitlistStyles();
      var wlId = 'wl';
      try {
        var sid =
          bookingHost.getAttribute('data-wb-section-id') ||
          bookingHost.getAttribute('data-product-id') ||
          'wl';
        wlId = String(sid).replace(/[^a-zA-Z0-9_-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '').slice(0, 72) || 'wl';
      } catch (e) {
        wlId = 'wl';
      }
      var idFirst = 'wb-waitlist-first-' + wlId;
      var idLast = 'wb-waitlist-last-' + wlId;
      var idEmail = 'wb-waitlist-email-' + wlId;
      var wrap = document.createElement('div');
      wrap.className = 'wb-waitlist-modal';
      wrap.setAttribute('hidden', '');
      wrap.innerHTML =
        '<div class="wb-waitlist-modal-backdrop" data-wb-waitlist-modal-dismiss></div>' +
        '<div class="wb-waitlist-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="wb-wl-modal-title">' +
        '<button type="button" class="wb-waitlist-modal-x" data-wb-waitlist-modal-dismiss aria-label="Close">\u00d7</button>' +
        '<div class="wb-waitlist-modal-inner">' +
        '<figure class="wb-waitlist-modal-figure" data-wb-waitlist-modal-figure hidden>' +
        '<img class="wb-waitlist-modal-photo" data-wb-waitlist-modal-img alt="" width="640" height="480" loading="lazy" decoding="async" />' +
        '</figure>' +
        '<div class="wb-waitlist-modal-body">' +
        '<h3 id="wb-wl-modal-title" class="wb-waitlist-modal-title" data-wb-waitlist-modal-title>Join the waitlist</h3>' +
        '<div class="wb-waitlist-modal-fields">' +
        '<div class="wb-waitlist-field">' +
        '<label class="wb-waitlist-label" for="' +
        idFirst +
        '">First name</label>' +
        '<input id="' +
        idFirst +
        '" class="wb-waitlist-input" type="text" data-wb-waitlist-modal-first-name autocomplete="given-name" placeholder="e.g. Jane" required />' +
        '</div>' +
        '<div class="wb-waitlist-field">' +
        '<label class="wb-waitlist-label" for="' +
        idLast +
        '">Last name</label>' +
        '<input id="' +
        idLast +
        '" class="wb-waitlist-input" type="text" data-wb-waitlist-modal-last-name autocomplete="family-name" placeholder="e.g. Doe" required />' +
        '</div>' +
        '<div class="wb-waitlist-field">' +
        '<label class="wb-waitlist-label" for="' +
        idEmail +
        '">Email</label>' +
        '<input id="' +
        idEmail +
        '" class="wb-waitlist-input" type="email" data-wb-waitlist-modal-email autocomplete="email" placeholder="you@example.com" required />' +
        '</div>' +
        '</div>' +
        '<div class="wb-waitlist-modal-actions">' +
        '<button type="button" class="wb-waitlist-btn wb-waitlist-btn--primary" data-wb-waitlist-modal-submit>Submit</button>' +
        '<button type="button" class="wb-waitlist-btn wb-waitlist-btn--secondary" data-wb-waitlist-modal-dismiss>Cancel</button>' +
        '</div>' +
        '<div class="wb-waitlist-err" data-wb-waitlist-modal-error hidden></div>' +
        '<div class="wb-waitlist-msg" data-wb-waitlist-modal-success hidden>You\'re on the waitlist!</div>' +
        '</div></div></div>';
      document.body.appendChild(wrap);
      bookingHost._wbWaitlistModal = wrap;
      wbSyncWaitlistModalMedia(bookingHost, wrap);

      function closeWaitlistModal() {
        wrap.classList.remove('is-open');
        wrap.setAttribute('hidden', '');
        wrap._wbVenueId = null;
        wrap._wbVenueTitle = '';
        wrap._wbCustomerLocation = '';
        var tReset = wrap.querySelector('[data-wb-waitlist-modal-title]');
        if (tReset) tReset.textContent = 'Join the waitlist';
      }
      wrap.querySelectorAll('[data-wb-waitlist-modal-dismiss]').forEach(function (el) {
        el.addEventListener('click', closeWaitlistModal);
      });
      var firstIn = wrap.querySelector('[data-wb-waitlist-modal-first-name]');
      var lastIn = wrap.querySelector('[data-wb-waitlist-modal-last-name]');
      var emailIn = wrap.querySelector('[data-wb-waitlist-modal-email]');
      var subBtn = wrap.querySelector('[data-wb-waitlist-modal-submit]');
      var errEl = wrap.querySelector('[data-wb-waitlist-modal-error]');
      var okEl = wrap.querySelector('[data-wb-waitlist-modal-success]');
      if (subBtn) {
        subBtn.addEventListener('click', function () {
          var firstV = firstIn ? String(firstIn.value || '').trim() : '';
          var lastV = lastIn ? String(lastIn.value || '').trim() : '';
          var emailV = emailIn ? String(emailIn.value || '').trim() : '';
          var venueIdVal = wrap._wbVenueId;
          var venueTitle = (wrap._wbVenueTitle != null ? String(wrap._wbVenueTitle) : '').trim();
          var locationV = (wrap._wbCustomerLocation != null ? String(wrap._wbCustomerLocation) : '').trim();
          var fullNameV = (firstV && lastV ? firstV + ' ' + lastV : firstV || lastV).trim();
          if (errEl) {
            errEl.textContent = '';
            errEl.hidden = true;
          }
          if (!firstV) {
            if (errEl) {
              errEl.textContent = 'Please enter your first name.';
              errEl.hidden = false;
            }
            return;
          }
          if (!lastV) {
            if (errEl) {
              errEl.textContent = 'Please enter your last name.';
              errEl.hidden = false;
            }
            return;
          }
          if (!emailV || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailV)) {
            if (errEl) {
              errEl.textContent = 'Please enter a valid email.';
              errEl.hidden = false;
            }
            return;
          }
          subBtn.disabled = true;
          var workshopType = '';
          try {
            if (bookingHost && bookingHost.getAttribute) {
              workshopType = bookingHost.getAttribute('data-workshop-type') || '';
            }
            if (!String(workshopType || '').trim()) {
              var _wbTypeEl = document.querySelector('[data-workshop-type]');
              workshopType = _wbTypeEl ? _wbTypeEl.getAttribute('data-workshop-type') || '' : '';
            }
          } catch (e) {
            workshopType = '';
          }
          if (typeof workshopType !== 'string') workshopType = '';
          workshopType = workshopType.trim();
          var cityForAddress = '';
          if (locationV) {
            var _addrParts = locationV.split(',');
            if (_addrParts[0]) cityForAddress = String(_addrParts[0]).trim();
          }
          if (!cityForAddress && venueTitle) cityForAddress = String(venueTitle).trim();
          var waitlistPayload = {
            email: emailV,
            first_name: firstV,
            last_name: lastV,
            name: fullNameV,
            location: locationV,
            venue_id: venueIdVal,
            venue_name: venueTitle,
            shop_domain: window.Shopify?.shop ? String(window.Shopify.shop).trim() : '',
            workshop_type: workshopType
          };
          if (cityForAddress) {
            waitlistPayload.city = cityForAddress;
            waitlistPayload.country = 'New Zealand';
            waitlistPayload.country_code = 'NZ';
            waitlistPayload.default_address = {
              city: cityForAddress,
              country: 'New Zealand',
              country_code: 'NZ',
              address1: cityForAddress + ', New Zealand'
            };
          }
          fetch(__WB_WAITLIST_API_URL__, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(waitlistPayload),
            mode: 'cors'
          })
            .then(function (res) {
              if (res && res.ok) return;
              if (!res) throw new Error('Something went wrong. Please try again.');
              return res.text().then(function (txt) {
                var msg = 'Something went wrong. Please try again.';
                try {
                  var j = JSON.parse(txt);
                  if (j && (j.message || j.error)) msg = String(j.message || j.error).trim() || msg;
                } catch (_) {
                  if (txt && String(txt).trim()) msg = String(txt).trim().slice(0, 280);
                }
                throw new Error(msg);
              });
            })
            .then(function () {
              if (okEl) okEl.hidden = false;
              setTimeout(function () {
                closeWaitlistModal();
                if (okEl) okEl.hidden = true;
                if (firstIn) {
                  firstIn.value = '';
                  firstIn.style.display = '';
                }
                if (lastIn) {
                  lastIn.value = '';
                  lastIn.style.display = '';
                }
                if (emailIn) {
                  emailIn.value = '';
                  emailIn.style.display = '';
                }
                if (subBtn) subBtn.style.display = '';
              }, 2000);
            })
            .catch(function (err) {
              if (errEl) {
                errEl.textContent = err && err.message ? String(err.message) : 'Something went wrong. Please try again.';
                errEl.hidden = false;
              }
            })
            .finally(function () {
              subBtn.disabled = false;
            });
        });
      }
      return wrap;
    }

    function ensureCrossProductBookingContainer(booking) {
      if (!booking) return null;
      var id = 'wb-cross-product-booking';
      var el = booking.querySelector('#' + id);
      if (el) return el;
      var anchor = booking.querySelector('.wb-variant-grid') ||
        booking.querySelector('.wb-variant-list') ||
        booking.querySelector('.wb-variant-options') ||
        booking.querySelector('.workshop-variant-selector') ||
        booking.querySelector('.wb-variant-selector') ||
        (booking.querySelector('.wb-variant-btn') && booking.querySelector('.wb-variant-btn').closest('div'));
      if (!anchor) return null;
      el = document.createElement('div');
      el.id = id;
      el.className = 'wb-cross-product wb-cross-product--booking';
      el.style.display = 'none';
      anchor.insertAdjacentElement('afterend', el);
      return el;
    }

    function findEventById(eventId) {
      var list = window.__WORKSHOP_VENUE_LIST__;
      if (!list || !Array.isArray(list.groups) || !eventId) return null;
      for (var gi = 0; gi < list.groups.length; gi++) {
        var g = list.groups[gi];
        var venues = g && g.venues;
        if (!Array.isArray(venues)) continue;
        for (var vi = 0; vi < venues.length; vi++) {
          var v = venues[vi];
          var evs = v && v.events;
          if (!Array.isArray(evs)) continue;
          for (var ei = 0; ei < evs.length; ei++) {
            var ev = evs[ei];
            if (String(ev.id) === String(eventId) || String(ev.event_metaobject_gid || '') === String(eventId)) return ev;
          }
        }
      }
      return null;
    }

    function renderAlsoAvailableProducts(selectedEvent, bookingRoot) {
      var existing = bookingRoot && bookingRoot.querySelector('[data-wb-also-available]');
      if (!selectedEvent) {
        if (existing) existing.remove();
        return;
      }
      var arr = selectedEvent.also_available_products;
      if (!Array.isArray(arr) || arr.length === 0) {
        if (existing) existing.remove();
        return;
      }
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/);
      var currentHandle = m && m[1] ? m[1].toLowerCase() : '';
      var map = new Map();
      for (var i = 0; i < arr.length; i++) {
        var p = arr[i];
        if (!p || !p.handle) continue;
        var h = p.handle.toLowerCase();
        if (h === currentHandle) continue;
        if (!map.has(h)) map.set(h, p);
      }
      if (map.size === 0) {
        if (existing) existing.remove();
        return;
      }
      var anchor = bookingRoot.querySelector('.wb-variant-cards') || bookingRoot.querySelector('[data-workshop-variant-wrap]');
      if (!anchor) return;
      var keys = Array.from(map.keys()).slice(0, 2);
      var block = bookingRoot.querySelector('[data-wb-also-available]');
      if (!block) {
        block = document.createElement('div');
        block.setAttribute('data-wb-also-available', '');
        block.style.cssText = 'margin-top:10px;display:flex;gap:8px;flex-wrap:wrap';
        anchor.insertAdjacentElement('afterend', block);
      }
      var esc = function (s) { var d = document.createElement('div'); d.textContent = s; return d.innerHTML; };
      var html = 'Also available as ';
      for (var k = 0; k < keys.length; k++) {
        var prod = map.get(keys[k]);
        html += '<a class="button button--secondary button--small" href="' + esc(prod.url || '') + '">' + esc(prod.title || '') + '</a>';
      }
      block.innerHTML = html;
    }

    function renderCrossProductBooking(booking, eventId) {
      ensureCrossProductStyles();
      var host = ensureCrossProductBookingContainer(booking);
      if (!host) return;
      var currentHandle = ((booking && booking.dataset && booking.dataset.productHandle) || '').toString().trim().toLowerCase();
      var ev = findEventById(eventId);
      var av = (ev && Array.isArray(ev.available_variants)) ? ev.available_variants : [];
      var byHandle = Object.create(null);
      for (var i = 0; i < av.length; i++) {
        var it = av[i] || {};
        var h = (it.product_handle || it.productHandle || '').toString().trim();
        if (!h) continue;
        if (h.toLowerCase() === currentHandle) continue;
        if (byHandle[h.toLowerCase()]) continue;
        byHandle[h.toLowerCase()] = {
          handle: h,
          title: (it.product_title || it.productTitle || it.title || h).toString().trim(),
          url: (it.product_url || it.productUrl || '').toString().trim() || ('/products/' + encodeURIComponent(h)),
          image: (it.product_image || it.productImage || it.image || '').toString().trim() || null
        };
      }
      var keys = Object.keys(byHandle);
      if (!keys.length) {
        host.innerHTML = '';
        host.style.display = 'none';
        return;
      }
      host.style.display = '';
      var html = '<span class="wb-cross-product__label">Other variant options are available for this time slot:</span><div class="wb-cross-product__list">';
      for (var k = 0; k < keys.length; k++) {
        var op = byHandle[keys[k]];
        html += '<a class="wb-cross-product__item" href="' + escapeHtml(op.url) + '" data-product-handle="' + escapeHtml(op.handle) + '">';
        if (op.image) html += '<img class="wb-cross-product__img" src="' + escapeHtml(op.image) + '" alt="" loading="lazy" width="22" height="22">';
        html += '<span>' + escapeHtml(op.title) + '</span></a>';
      }
      html += '</div>';
      host.innerHTML = html;
    }

    /**
     * Build calendar model for one month. date_key format YYYY-MM-DD.
     * @param {number} year
     * @param {number} month 1-12
     * @param {Set<string>} enabledDatesSet set of 'YYYY-MM-DD' that are clickable
     * @returns {{ year: number, month: number, weekDayNames: string[], cells: Array<{ dateKey: string|null, dayNum: number, isEnabled: boolean }> }}
     */
    function wbBuildCalendarModel(year, month, enabledDatesSet) {
      var weekDayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      var firstDay = (new Date(year, month - 1, 1).getDay() + 6) % 7;
      var daysInMonth = new Date(year, month, 0).getDate();
      var cells = [];
      var i, d, dateKey, pad = function (n) { return (n < 10 ? '0' : '') + n; };
      for (i = 0; i < firstDay; i++) cells.push({ dateKey: null, dayNum: 0, isEnabled: false });
      for (d = 1; d <= daysInMonth; d++) {
        dateKey = year + '-' + pad(month) + '-' + pad(d);
        cells.push({ dateKey: dateKey, dayNum: d, isEnabled: enabledDatesSet && enabledDatesSet.has(dateKey) });
      }
      var remainder = cells.length % 7;
      if (remainder) for (i = 0; i < 7 - remainder; i++) cells.push({ dateKey: null, dayNum: 0, isEnabled: false });
      return { year: year, month: month, weekDayNames: weekDayNames, cells: cells };
    }

    /**
     * Render mini calendar in venue card. Only enabled (event) dates are clickable.
     * Syncs with hidden select: calendar selection updates select and dispatches change.
     * @param {Element} cardEl .wb-venue-card
     * @param {Set<string>} datesSet set of 'YYYY-MM-DD' for this venue
     * @param {string} currentSelectedDate current select value (YYYY-MM-DD)
     * @param {function(string)} onSelectDate called when user picks a date (we then set select + dispatch)
     */
    function wbRenderVenueDateCalendar(cardEl, datesSet, currentSelectedDate, onSelectDate) {
      var container = cardEl.querySelector('[data-wb-date-calendar]');
      if (!container) return;
      var dateSelect = cardEl.querySelector('[data-workshop-venue-date]');
      if (!dateSelect) return;

      container.innerHTML = '';
      container.classList.remove('wb-date-calendar--empty');

      if (!datesSet || datesSet.size === 0) {
        container.classList.add('wb-date-calendar--empty');
        container.innerHTML = '<p class="wb-date-calendar-empty-msg">No dates available</p>';
        return;
      }

      var sortedDates = Array.from(datesSet).sort();
      var monthKeys = [];
      var seen = {};
      sortedDates.forEach(function (dk) {
        var ym = (dk || '').slice(0, 7);
        if (ym && !seen[ym]) { seen[ym] = true; monthKeys.push(ym); }
      });
      var currentMonthIndex = 0;
      if (currentSelectedDate) {
        var selYm = currentSelectedDate.slice(0, 7);
        var idx = monthKeys.indexOf(selYm);
        if (idx >= 0) currentMonthIndex = idx;
      }
      var curYm = monthKeys[currentMonthIndex] || '';
      var parts = curYm.split('-').map(Number);
      var year = parts[0] || new Date().getFullYear();
      var month = parts[1] || 1;

      function renderMonth(yr, mo) {
        container.innerHTML = '';
        var model = wbBuildCalendarModel(yr, mo, datesSet);
        var header = document.createElement('div');
        header.className = 'wb-cal-header';
        var monthLabel = document.createElement('span');
        monthLabel.className = 'wb-cal-month-label';
        try {
          monthLabel.textContent = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(new Date(yr, mo - 1, 1));
        } catch (_) { monthLabel.textContent = yr + '-' + (mo < 10 ? '0' : '') + mo; }
        header.appendChild(monthLabel);
        var nav = document.createElement('div');
        nav.className = 'wb-cal-nav';
        if (monthKeys.length > 1) {
          var prevBtn = document.createElement('button');
          prevBtn.type = 'button';
          prevBtn.className = 'wb-cal-nav-prev';
          prevBtn.setAttribute('aria-label', 'Previous month');
          prevBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>';
          var nextBtn = document.createElement('button');
          nextBtn.type = 'button';
          nextBtn.className = 'wb-cal-nav-next';
          nextBtn.setAttribute('aria-label', 'Next month');
          nextBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>';
          prevBtn.addEventListener('click', function () {
            var idx = monthKeys.indexOf(yr + '-' + (mo < 10 ? '0' : '') + mo);
            if (idx > 0) {
              var prevYm = monthKeys[idx - 1].split('-').map(Number);
              renderMonth(prevYm[0], prevYm[1]);
            }
          });
          nextBtn.addEventListener('click', function () {
            var idx = monthKeys.indexOf(yr + '-' + (mo < 10 ? '0' : '') + mo);
            if (idx >= 0 && idx < monthKeys.length - 1) {
              var nextYm = monthKeys[idx + 1].split('-').map(Number);
              renderMonth(nextYm[0], nextYm[1]);
            }
          });
          nav.appendChild(prevBtn);
          nav.appendChild(nextBtn);
        }
        header.appendChild(nav);
        container.appendChild(header);

        var weekRow = document.createElement('div');
        weekRow.className = 'wb-cal-weekdays';
        model.weekDayNames.forEach(function (name) {
          var th = document.createElement('span');
          th.className = 'wb-cal-weekday';
          th.textContent = name;
          weekRow.appendChild(th);
        });
        container.appendChild(weekRow);

        var grid = document.createElement('div');
        grid.className = 'wb-cal-grid';
        model.cells.forEach(function (cell) {
          var dayEl = document.createElement('button');
          dayEl.type = 'button';
          dayEl.className = 'wb-cal-day';
          if (cell.dayNum) {
            dayEl.textContent = cell.dayNum;
            dayEl.setAttribute('data-date-key', cell.dateKey || '');
            if (cell.isEnabled) {
              dayEl.classList.add('wb-cal-day--enabled');
              dayEl.setAttribute('aria-label', 'Select ' + (cell.dateKey || ''));
              dayEl.addEventListener('click', function () {
                if (dateSelect.value !== cell.dateKey) {
                  dateSelect.value = cell.dateKey;
                  dateSelect.dispatchEvent(new Event('change', { bubbles: true }));
                }
                onSelectDate(cell.dateKey);
                wbSyncCalendarFromSelect(cardEl);
              });
            } else {
              dayEl.classList.add('wb-cal-day--disabled');
              dayEl.setAttribute('disabled', '');
            }
            if (cell.dateKey === (dateSelect.value || '')) { dayEl.classList.add('wb-cal-day--selected'); dayEl.classList.add('is-selected'); }
          } else {
            dayEl.classList.add('wb-cal-day--empty');
            dayEl.setAttribute('aria-hidden', 'true');
          }
          grid.appendChild(dayEl);
        });
        container.appendChild(grid);
      }

      renderMonth(year, month);
    }

    /**
     * Sync calendar selected state from the venue card's date select value.
     * @param {Element} cardEl .wb-venue-card
     */
    function wbSyncCalendarFromSelect(cardEl) {
      var container = cardEl.querySelector('[data-wb-date-calendar]');
      var dateSelect = cardEl.querySelector('[data-workshop-venue-date]');
      if (!container || !dateSelect) return;
      var val = (dateSelect.value || '').trim();
      container.querySelectorAll('.wb-cal-day--selected, .wb-cal-day.is-selected').forEach(function (el) { el.classList.remove('wb-cal-day--selected', 'is-selected'); });
      if (val) {
        var cell = container.querySelector('.wb-cal-day[data-date-key="' + CSS.escape(val) + '"]');
        if (cell) { cell.classList.add('wb-cal-day--selected'); cell.classList.add('is-selected'); }
      }
    }

    /**
     * Accordion: close all venue groups in the container except the one to keep open (animated).
     * @param {Element} containerEl - Root containing .wb-venue-group elements (e.g. eventsRoot)
     * @param {Element|null} keepGroupSectionEl - The .wb-venue-group to leave expanded, or null to close all
     */
    function wbCloseAllVenueCardsExcept(containerEl, keepGroupSectionEl) {
      if (!containerEl) return;
      var groups = containerEl.querySelectorAll('.wb-venue-group');
      for (var i = 0; i < groups.length; i++) {
        var group = groups[i];
        if (group === keepGroupSectionEl) continue;
        var toggle = group.querySelector('.wb-group-toggle');
        var body = group.querySelector('.wb-group-body');
        if (document.activeElement && group.contains(document.activeElement) && toggle) {
          try { toggle.focus(); } catch (e) {}
        }
        if (body && toggle) wbCollapseBody(body, toggle);
      }
    }

    /**
     * Collapse accordion body with height + opacity transition. Cleans up previous transitionend.
     */
    function wbCollapseBody(bodyEl, toggleEl) {
      if (!bodyEl) return;
      if (bodyEl._wbTransitionEnd) {
        bodyEl.removeEventListener('transitionend', bodyEl._wbTransitionEnd);
        bodyEl._wbTransitionEnd = null;
      }
      var curHeight = bodyEl.style.height;
      if (curHeight === 'auto' || curHeight === '') {
        bodyEl.style.height = bodyEl.scrollHeight + 'px';
        bodyEl.offsetHeight;
      }
      bodyEl.style.height = '0px';
      bodyEl.style.opacity = '0';
      bodyEl.setAttribute('aria-hidden', 'true');
      if (toggleEl) toggleEl.setAttribute('aria-expanded', 'false');
      var handler = function (e) {
        if (e.propertyName !== 'height') return;
        bodyEl.removeEventListener('transitionend', handler);
        bodyEl._wbTransitionEnd = null;
        bodyEl.style.height = '0';
      };
      bodyEl._wbTransitionEnd = handler;
      bodyEl.addEventListener('transitionend', handler);
    }

    /**
     * Expand accordion body with height + opacity transition. On transitionend sets height to auto.
     */
    function wbExpandBody(bodyEl, toggleEl) {
      if (!bodyEl) return;
      if (bodyEl._wbTransitionEnd) {
        bodyEl.removeEventListener('transitionend', bodyEl._wbTransitionEnd);
        bodyEl._wbTransitionEnd = null;
      }
      bodyEl.setAttribute('aria-hidden', 'false');
      if (toggleEl) toggleEl.setAttribute('aria-expanded', 'true');
      bodyEl.style.opacity = '1';
      bodyEl.style.height = bodyEl.scrollHeight + 'px';
      bodyEl.offsetHeight;
      var handler = function (e) {
        if (e.propertyName !== 'height') return;
        bodyEl.removeEventListener('transitionend', handler);
        bodyEl._wbTransitionEnd = null;
        bodyEl.style.height = 'auto';
      };
      bodyEl._wbTransitionEnd = handler;
      bodyEl.addEventListener('transitionend', handler);
    }

    /**
     * Venue-centric: render location groups (collapsible), venue cards, date dropdown, hour slots.
     * Empty / waitlist venues: same loop over group.venues; venue._wbWaitlistVenue === true → card with Join Waitlist only (no date/slots).
     */
    function renderVenueCentricList(venueList) {
      if (!eventsRoot) return;
      var prevSelectedSlot = eventsRoot.querySelector('.workshop-event-card.is-selected');
      if (prevSelectedSlot) {
        var prevVenueCard = prevSelectedSlot.closest ? prevSelectedSlot.closest('.wb-venue-card') : null;
        if (prevVenueCard) wbLockVenueContextFromCard(prevVenueCard);
      } else {
        var prevOpenHeader = eventsRoot.querySelector('[data-workshop-group-toggle][aria-expanded="true"]');
        if (prevOpenHeader) {
          var prevOpenGroup = prevOpenHeader.closest ? prevOpenHeader.closest('.wb-venue-group') : null;
          if (prevOpenGroup) {
            var prevGroupKey = String(prevOpenGroup.getAttribute('data-workshop-venue-group') || '').trim();
            if (prevGroupKey) wbLockedVenueGroupKey = prevGroupKey;
            var firstVenueInOpen = prevOpenGroup.querySelector('.wb-venue-card[data-venue-ref-id]');
            if (firstVenueInOpen) {
              var prevRef = String(firstVenueInOpen.getAttribute('data-venue-ref-id') || '').trim();
              if (prevRef) wbLockedVenueRefId = prevRef;
            }
          }
        }
      }
      ensureCrossProductStyles();
      ensureWaitlistStyles();
      var groups = (venueList && venueList.groups) ? venueList.groups : [];
      if (window.__WB_DEBUG__) console.debug('[WB] render venues start', { count: groups.length, sample: groups[0] ? { city_label: groups[0].city_label, distance_km: groups[0].distance_km, userLoc: window.__wbUserLoc } : null });
      eventsRoot.innerHTML = '';
      if (eventsPlaceholder) eventsPlaceholder.classList.add('pdp-wb-hidden');
      var fragment = document.createDocumentFragment();
      var wbEmptyVenueSlotsMsg =
        'Unfortunately, no events are scheduled at this venue in the near future.';

      var isWizardFlatVenuePicker =
        booking && booking.classList && booking.classList.contains('wb-wizard');
      if (isWizardFlatVenuePicker) {
        wbEnrichVenueListImagesFromCatalog(venueList, booking);
        var wizVenueLayout = '';
        try {
          wizVenueLayout = booking.getAttribute
            ? String(booking.getAttribute('data-wbw-venue-layout') || '')
                .trim()
                .toLowerCase()
            : '';
        } catch (_) {}
        var wizUseCardGrid = wizVenueLayout !== 'list';

        var flatList = document.createElement('div');
        flatList.className =
          'wbw-venue-flat-list' +
          (wizUseCardGrid ? ' wbw-venue-flat-list--grid' : ' wbw-venue-flat-list--text');
        flatList.setAttribute('data-wbw-venue-flat-list', wizUseCardGrid ? 'grid' : 'list');

        for (var gfi = 0; gfi < groups.length; gfi++) {
          var grp = groups[gfi];
          var grpDist = toNum(grp.distance_km);
          var grpLabel = (grp.city_label || '').toString().trim();
          var flatVenues = grp.venues || [];
          for (var vfi = 0; vfi < flatVenues.length; vfi++) {
            var flatVenue = flatVenues[vfi];
            var flatTitle = (flatVenue.venue_name || flatVenue.name || flatVenue.city_name || 'Venue')
              .toString()
              .trim();
            var flatHasBookable = wbVenueHasBookableEvents(flatVenue);
            var flatIsWaitlist = flatVenue._wbWaitlistVenue === true || !flatHasBookable;
            var lineCity =
              grpLabel && !/^us-[a-z]{2}-/i.test(grpLabel) ? grpLabel : flatTitle;
            var distText =
              grpDist != null && Number.isFinite(grpDist)
                ? ' - ' + Number(grpDist).toFixed(1) + ' KM AWAY'
                : '';
            var kmLine =
              grpDist != null && Number.isFinite(grpDist) ? Number(grpDist).toFixed(1) + ' km' : '';

            var pick;
            if (wizUseCardGrid && flatIsWaitlist) {
              pick = document.createElement('div');
              pick.setAttribute('role', 'button');
              pick.setAttribute('tabindex', '0');
            } else {
              pick = document.createElement('button');
              pick.type = 'button';
            }
            pick.className =
              'wb-venue-pick-card wb-venue-card' +
              (flatIsWaitlist ? ' wb-venue-pick-card--waitlist' : ' wb-venue-pick-card--sessions') +
              (wizUseCardGrid ? ' wbw-venue-pick-card--card' : '') +
              (wizUseCardGrid && flatIsWaitlist ? ' wbw-venue-flip-card' : '');
            pick.setAttribute('data-venue-ref-id', flatVenue.venue_ref_id || '');
            pick.setAttribute('data-wbw-city-label', lineCity);
            pick.setAttribute(
              'data-venue-address',
              String(flatVenue.venue_address || flatVenue.address || '').trim()
            );
            pick.setAttribute('aria-pressed', 'false');
            if (flatIsWaitlist) pick.setAttribute('data-wb-waitlist-venue', '1');

            if (wizUseCardGrid) {
              var imgUrl = wbResolveVenueCardImage(flatVenue, lineCity, booking);
              pick.innerHTML = wbWizardVenueGridCardInnerHtml(lineCity, kmLine, imgUrl, flatIsWaitlist);
            } else {
              pick.innerHTML =
                '<span class="wb-venue-pick-card__line">' +
                escapeHtml(lineCity) +
                escapeHtml(distText) +
                '</span>';
            }
            pick._wbVenue = flatVenue;
            flatList.appendChild(pick);
          }
        }

        fragment.appendChild(flatList);
        eventsRoot.appendChild(fragment);
        wbRestoreVenueContext();

        if (groups.length === 0 && eventsPlaceholder) {
          wbwDispatchVenueReset();
          eventsPlaceholder.classList.remove('pdp-wb-hidden');
          eventsPlaceholder.textContent =
            'No venues found for this location. Try another location.';
        }

        try {
          wbRefreshBookingGuideUi();
        } catch (_) {}

        var flatCards = eventsRoot.querySelectorAll('.wb-venue-pick-card');
        try {
          document.dispatchEvent(
            new CustomEvent('wbw:venue-list-rendered', { detail: { bookingRoot: booking } })
          );
        } catch (_) {}
        if (typeof window.__wbwApplyAllDeepLinks === 'function') {
          try {
            window.__wbwApplyAllDeepLinks();
          } catch (_) {}
        }
        return {
          groupCards: groups.length,
          venueCards: flatCards.length,
          slotButtons: 0,
        };
      }

      function buildHourSlotsForDate(venueCard, venue, selectedDateKey, timesContainer) {
        if (window.WB_DEBUG_FETCH) console.log('[WB_SLOTS] container', {
          exists: !!timesContainer,
          className: timesContainer && timesContainer.className,
          parentEl: timesContainer && timesContainer.parentElement && timesContainer.parentElement.className,
          childCountBefore: timesContainer && timesContainer.children && timesContainer.children.length
        });
        timesContainer.innerHTML = '';
        var vEventsAll = Array.isArray(venue.events) ? venue.events : [];
        var eventsOnDate = vEventsAll.filter(function (ev) { return (ev.date_key || '') === selectedDateKey; });
        function wbSlotSortKey(ev) {
          try {
            var t = (ev && ev.time_label != null) ? String(ev.time_label).trim() : '';
            if (!t && ev && ev.starts_at) t = String(extractTimeFromStartsAt(ev.starts_at) || '').trim();
            var m = t.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
            if (!m) return Number.MAX_SAFE_INTEGER;
            var hh = Number(m[1]);
            var mm = Number(m[2]);
            var mer = (m[3] || '').toUpperCase();
            if (mer) {
              if (mer === 'AM' && hh === 12) hh = 0;
              if (mer === 'PM' && hh < 12) hh += 12;
            }
            return (hh * 60) + mm;
          } catch (_) {
            return Number.MAX_SAFE_INTEGER;
          }
        }
        eventsOnDate.sort(function (a, b) { return wbSlotSortKey(a) - wbSlotSortKey(b); });
        if (window.WB_DEBUG_FETCH) console.log('[WB_SLOTS] eventsOnDate', {
          dateKey: selectedDateKey,
          count: eventsOnDate.length,
          sample: eventsOnDate.slice(0, 3).map(function (e) { return { id: e.id, time: e.time_label, status: e.status }; })
        });
        for (var i = 0; i < eventsOnDate.length; i++) {
          var ev = eventsOnDate[i];
          if (wbShouldHideEvent(ev)) continue;
          var timeLabel = (ev.time_label != null && ev.time_label !== '') ? String(ev.time_label).trim() : '';
          if (!timeLabel && ev.starts_at) {
            try {
              var raw = extractTimeFromStartsAt(ev.starts_at);
              if (raw) timeLabel = time24hTo12h(raw);
            } catch (_) {}
          }
          if (!timeLabel) timeLabel = '—';
          var cap = ev.capacity_remaining;
          var status = wbResolveEventStatus(ev);
          var isSoldOut = wbIsEventSoldOut({ status: status, capacity_remaining: cap });
          var hasVariant = ev.variant_id != null && ev.variant_id !== '';
          var notBookable = !hasVariant && (!ev.available_variants || ev.available_variants.length === 0);
          var title = (ev.event_name || ev.venue_name || '(Event)').trim();
          var dateLabel = formatDateLabelVenueCentric(selectedDateKey);
          var variantIdNumeric = hasVariant ? parseVariantIdFromGid(ev.variant_id) : null;
          var variantIdRaw = hasVariant ? String(ev.variant_id).trim() : '';

          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'workshop-event-card wb-time-slot' + (isSoldOut ? ' wb-time-slot--sold' : '');
          btn.setAttribute('data-event-id', String(ev.id || ''));
          btn.setAttribute('data-workshop-event-id', String(ev.id || ''));
          btn.setAttribute('data-event-metaobject-gid', String(ev.event_metaobject_gid || ev.id || ''));
          btn.setAttribute('data-workshop-event-card', '1');
          btn.setAttribute('data-event-handle', String(ev.handle || ''));
          btn.setAttribute('data-variant-id', variantIdNumeric ? String(variantIdNumeric) : (hasVariant ? variantIdRaw : 'none'));
          if (variantIdRaw && /^gid:\/\/shopify\/ProductVariant\/\d+$/i.test(variantIdRaw)) btn.setAttribute('data-variant-gid', variantIdRaw);
          if (notBookable) btn.setAttribute('data-not-bookable', '1');
          btn.setAttribute('data-state', String(ev.state || ''));
          btn.setAttribute('data-city', String(ev.city || ''));
          btn.setAttribute('data-city-key', String(ev.city_key || ''));
          btn.setAttribute('data-status', isSoldOut ? 'sold_out' : status);
          btn.setAttribute('data-event-name', title);
          btn.setAttribute('data-venue-name', String(ev.venue_name || ''));
          btn.setAttribute('data-venue-handle', String(ev.venue_handle || ''));
          btn.setAttribute('data-venue-address', String(ev.venue_address || ''));
          btn.setAttribute('data-venue-phone', String(ev.venue_phone || ''));
          btn.setAttribute('data-venue-work-hours', String(ev.venue_work_hours || ''));
          btn.setAttribute('data-date-key', selectedDateKey);
          btn.setAttribute('data-date-label', dateLabel);
          btn.setAttribute('data-time-label', timeLabel);
          btn.setAttribute('data-capacity-remaining', cap != null ? String(cap) : '');
          btn.setAttribute('data-capacity-total', (ev.capacity_total != null && ev.capacity_total !== '') ? String(ev.capacity_total) : '');
          btn.setAttribute('data-starts-at', '0');
          btn._wbEv = ev;
          btn.setAttribute('data-product-handle', String(ev.product_handle || ''));
          btn.setAttribute('data-link-enabled', ev.link_enabled !== false && !!(ev.product_handle || '').toString().trim() ? '1' : '0');
          var capLabel = '';
          if (typeof cap === 'number' && cap > 0 && cap <= 5) capLabel = ' · ' + cap + ' left';
          btn.innerHTML = '<span class="wb-slot-time">' + escapeHtml(timeLabel) + '</span>';
          if (isSoldOut) {
            btn.innerHTML += '<span class="wb-slot-sold-badge">Sold out</span>';
          } else if (capLabel) {
            btn.innerHTML += '<span class="wb-slot-cap">' + escapeHtml(capLabel) + '</span>';
          }
          if (notBookable) {
            btn.innerHTML += '<span class="wb-slot-cap">Not bookable</span>';
          }
          timesContainer.appendChild(btn);
        }
        if (eventsOnDate.length === 0 && vEventsAll.length > 0) {
          var emptyP = document.createElement('p');
          emptyP.className = 'wb-venue-slots-empty-msg';
          emptyP.setAttribute('role', 'status');
          emptyP.textContent = wbEmptyVenueSlotsMsg;
          timesContainer.appendChild(emptyP);
        }
        if (window.WB_DEBUG_FETCH) console.log('[WB_SLOTS] appended', {
          childCountAfter: timesContainer.children.length,
          containerInDOM: !!timesContainer.closest('.wb-venue-card'),
          containerVisible: timesContainer.offsetHeight > 0
        });
      }

      for (var gi = 0; gi < groups.length; gi++) {
        var group = groups[gi];
        if (window.__WB_DEBUG__) {
          var firstVenue = (group.venues && group.venues[0]) || {};
          console.debug('[WB] group', { label: group.city_label, group_key: group.group_key, city_key: group.city_key, vlat: firstVenue.venue_lat, vlng: firstVenue.venue_lng, userLoc: window.__wbUserLoc, distKm: group.distance_km });
        }
        var groupSection = document.createElement('div');
        groupSection.className = 'wb-venue-group';
        groupSection.setAttribute('data-workshop-venue-group', group.group_key || group.city_key || '');

        var distKm = toNum(group.distance_km);
        var rawLabel = (group.city_label || '').toString().trim();
        var displayLabel = (rawLabel && !/^us-[a-z]{2}-/i.test(rawLabel)) ? rawLabel : '';
        var hasDistance = (distKm != null && Number.isFinite(distKm));
        var distText = hasDistance ? (' \u2014 ' + Number(distKm).toFixed(1) + ' km away') : '';
        var distClass = hasDistance ? 'wb-venue-distance' : 'wb-venue-distance is-hidden';

        var groupHeader = document.createElement('button');
        groupHeader.type = 'button';
        groupHeader.className = 'wb-group-toggle';
        groupHeader.setAttribute('data-workshop-group-toggle', '1');
        groupHeader.setAttribute('aria-expanded', 'false');
        groupHeader.innerHTML =
          '<span class="wb-group-label-wrap">' +
            '<span class="wb-group-label">' + escapeHtml(displayLabel) + '</span>' +
            '<span class="' + distClass + '" data-wb-venue-distance>' + escapeHtml(distText) + '</span>' +
          '</span>' +
          '<svg class="wb-group-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/></svg>';
        groupSection.appendChild(groupHeader);

        if (window.__WB_DEBUG__) {
          console.debug('[WB] distance check', {
            label: displayLabel,
            distKm: distKm,
            hasDistance: hasDistance,
            userLoc: window.__wbUserLoc,
            group_distance_km: group.distance_km
          });
        }

        var groupBody = document.createElement('div');
        groupBody.className = 'wb-group-body';
        groupBody.setAttribute('aria-hidden', 'true');
        groupBody.style.height = '0px';
        groupBody.style.opacity = '0';
        groupSection.appendChild(groupBody);

        for (var vi = 0; vi < group.venues.length; vi++) {
          (function (venue, venueGroup) {
            var venueCard = document.createElement('div');
            venueCard.className = 'workshop-venue-card wb-venue-card';
            venueCard.setAttribute('data-venue-ref-id', venue.venue_ref_id || '');

            var venueTitle = (venue.venue_name || venue.name || venue.city_name || 'Venue').toString().trim();
            var phoneEsc = venue.venue_phone ? escapeHtml(venue.venue_phone).replace(/"/g, '&quot;') : '';
            var tagsHtml = '';
            if (Array.isArray(venue.venue_tags) && venue.venue_tags.length > 0) {
              tagsHtml = '<div class="wb-venue-tags">' + venue.venue_tags.map(function (t) { return '<span class="wb-venue-tag">' + escapeHtml(String(t)) + '</span>'; }).join('') + '</div>';
            }
            var isWaitlistOnly = venue._wbWaitlistVenue === true;
            if (isWaitlistOnly) venueCard.setAttribute('data-wb-waitlist-venue', '1');

            var vEventsForVenue = Array.isArray(venue.events) ? venue.events : [];
            var hasAnyVenueEvents = vEventsForVenue.length > 0;

            var venueHeadlineHtml;
            var useWaitlistBanner = isWaitlistOnly && !hasAnyVenueEvents;
            if (isWaitlistOnly) {
              if (useWaitlistBanner) {
                venueHeadlineHtml = '';
              } else {
                venueHeadlineHtml = '<h4 class="wb-venue-name">' + escapeHtml(venueTitle) + '</h4>';
              }
            } else if (!hasAnyVenueEvents) {
              venueHeadlineHtml =
                '<p class="wb-venue-no-events-banner wb-venue-no-events-banner--solo" role="status">' +
                escapeHtml(wbEmptyVenueSlotsMsg) +
                '</p>';
            } else {
              venueHeadlineHtml = '<h4 class="wb-venue-name">' + escapeHtml(venueTitle) + '</h4>';
            }

            var venueAddressInlineHtml = venue.venue_address
              ? '<span class="wb-venue-addr-inline">' + escapeHtml(venue.venue_address) + '</span>'
              : '';
            var venueSummaryRowHtml =
              '<div class="wb-venue-summary-row">' +
                venueAddressInlineHtml +
              '</div>';

            var leftColHtml =
              '<div class="wb-venue-left">' +
                (hasAnyVenueEvents && !isWaitlistOnly ? venueSummaryRowHtml : venueHeadlineHtml) +
                (hasAnyVenueEvents && !isWaitlistOnly ? '' :
                  '<div class="wb-venue-details">' +
                    (venue.venue_address ? '<div class="wb-venue-field"><div class="wb-venue-label">Address</div><div class="wb-venue-value wb-venue-addr">' + escapeHtml(venue.venue_address) + '</div></div>' : '') +
                    (venue.venue_phone ? '<div class="wb-venue-field"><div class="wb-venue-label">Phone</div><div class="wb-venue-value wb-venue-phone">' + '<a class="wb-venue-phone-link" href="tel:' + phoneEsc + '">' + escapeHtml(venue.venue_phone) + '</a></div></div>' : '') +
                    (venue.venue_work_hours ? '<div class="wb-venue-field"><div class="wb-venue-label">Work Hours</div><div class="wb-venue-value wb-venue-work-hours">' + escapeHtml(venue.venue_work_hours) + '</div></div>' : '') +
                    tagsHtml +
                  '</div>' +
                  '<div class="wb-venue-meta" data-wb-venue-meta></div>'
                ) +
              '</div>';

            var waitlistCtaHtml = isWaitlistOnly
              ? '<div class="wb-venue-right">' +
                  '<div class="wb-waitlist-actions">' +
                    '<button type="button" class="wb-waitlist-btn wb-waitlist-btn--card-cta" data-wb-waitlist-open aria-haspopup="dialog">Sign up for waitlist \u2192</button>' +
                  '</div>' +
                '</div>'
              : '';

            var calendarHtml =
              '<div class="wb-venue-date-wrap">' +
                '<div class="wb-date-calendar" data-wb-date-calendar data-wst-anchor="date"></div>' +
                '<select class="wb-venue-date-select wb-venue-date-select-fallback" data-workshop-venue-date="1" aria-label="Select date"></select>' +
              '</div>';

            var timeSlotsHtml =
              '<div class="wb-venue-slots" data-workshop-venue-times data-wst-anchor="time"></div>';

            var venueCardBodyHtml =
              '<div class="wb-venue-card-body">' +
                venueSummaryRowHtml +
                calendarHtml +
                timeSlotsHtml +
              '</div>';

            if (useWaitlistBanner) {
              venueCard.innerHTML =
                '<p class="wb-venue-no-events-banner" role="status">' +
                escapeHtml(wbEmptyVenueSlotsMsg) +
                '</p>' +
                '<div class="wb-venue-waitlist-footer">' +
                leftColHtml +
                waitlistCtaHtml +
                '</div>';
              venueCard.classList.add('wb-venue-card--waitlist-banner');
            } else if (hasAnyVenueEvents && !isWaitlistOnly) {
              venueCard.innerHTML =
                venueCardBodyHtml;
            } else {
              venueCard.innerHTML =
                leftColHtml + (isWaitlistOnly ? waitlistCtaHtml : '');
            }
            if (!hasAnyVenueEvents && !isWaitlistOnly) {
              venueCard.setAttribute('data-wb-no-scheduled-events', '1');
            }

            if (!isWaitlistOnly && hasAnyVenueEvents) {
              if (window.WB_DEBUG_FETCH) console.log('[WB_VENUE_DOM]', {
                hasDate: !!venueCard.querySelector('[data-workshop-venue-date]'),
                hasSlots: !!venueCard.querySelector('[data-workshop-venue-times]')
              });

              var dateSelect = venueCard.querySelector('[data-workshop-venue-date]');
              var timesContainer = venueCard.querySelector('[data-workshop-venue-times]');

              var dateKeys = [];
              var seen = {};
              vEventsForVenue.forEach(function (ev) {
                var dk = (ev.date_key || '').toString().trim();
                if (dk && !seen[dk]) { seen[dk] = true; dateKeys.push(dk); }
              });
              dateKeys.sort();

              dateKeys.forEach(function (dk) {
                var opt = document.createElement('option');
                opt.value = dk;
                opt.textContent = formatDateLabelVenueCentric(dk);
                if (dk === (dateKeys[0] || '')) opt.selected = true;
                dateSelect.appendChild(opt);
              });
              if (dateKeys.length === 0) dateSelect.setAttribute('disabled', '');

              var selectedDateKey = dateKeys[0] || '';
              buildHourSlotsForDate(venueCard, venue, selectedDateKey, timesContainer);
              function wbTrackerVenueNameFromDom() {
                var name = '';
                var groupEl = venueCard.closest ? venueCard.closest('.wb-venue-group') : null;
                var groupHeader = groupEl ? (groupEl.querySelector('[data-workshop-group-toggle]') || groupEl.querySelector('.wb-group-toggle')) : null;
                if (groupHeader && groupHeader.getAttribute('aria-expanded') === 'true') {
                  var groupLabel = groupHeader.querySelector('.wb-group-label');
                  name = groupLabel ? String(groupLabel.textContent || '').trim() : '';
                }
                if (!name) {
                  var venueNameEl = venueCard.querySelector('.wb-venue-name');
                  name = venueNameEl ? String(venueNameEl.textContent || '').trim() : '';
                }
                if (!name) name = String(venueTitle || '').trim();
                return name;
              }

              dateSelect.addEventListener('change', function () {
                wbLockVenueContextFromCard(venueCard);
                venueCard.setAttribute('data-wb-date-touched', '1');
                var newDateKey = dateSelect.value || '';
                if (!newDateKey) return;
                var trackerVenueName = wbTrackerVenueNameFromDom();
                wbSyncCalendarFromSelect(venueCard);
                // Check BEFORE rebuild if this venue card has the global selection
                // 1) .is-selected is set by setEventSelectionFromCard on hour click
                // 2) Fallback: match by __WORKSHOP_SELECTION__.eventId in case DOM is out of sync
                var sel = window.__WORKSHOP_SELECTION__;
                var selId = sel && (sel.eventId != null && sel.eventId !== '') ? String(sel.eventId) : '';
                var hasSelectedSlot = venueCard.querySelector('.workshop-event-card.is-selected') != null;
                var hasMatchingEventId = selId && venueCard.querySelector('.workshop-event-card[data-event-id="' + CSS.escape(selId) + '"]');
                var hadSelectionInThisCard = hasSelectedSlot || !!hasMatchingEventId;
                // Rebuild hour slots for the new date (removes old buttons)
                buildHourSlotsForDate(venueCard, venue, newDateKey, timesContainer);
                // If this venue card had the selection, clear it (the old slot is gone)
                // User must click an hour slot to re-select. NO auto-select.
                if (hadSelectionInThisCard) {
                  clearEventSelection();
                  wstUpdate(
                    3,
                    wstValues(trackerVenueName, wstFormatDateKey(newDateKey), '', '', '')
                  );
                }
                wbRestoreVenueContext();
                syncInCardSummaryForVenueCard(venueCard);
                try {
                  wbRefreshBookingGuideUi();
                } catch (e) {}
              });

              wbRenderVenueDateCalendar(venueCard, new Set(dateKeys), selectedDateKey, function (pickedDateKey) {
                var trackerVenueName = wbTrackerVenueNameFromDom();
                wstUpdate(
                  3,
                  wstValues(trackerVenueName, wstFormatDateKey(pickedDateKey || ''), '', '', '')
                );
              });

              // No auto-select: user must click an hour slot to set selection.
              // Add to Cart stays disabled until user explicitly clicks a time slot.
            } else {
              var openBtn = venueCard.querySelector('[data-wb-waitlist-open]');
              var venueRefId = (venue.venue_ref_id != null && String(venue.venue_ref_id).trim() !== '') ? String(venue.venue_ref_id).trim() : '';
              if (openBtn) {
                openBtn.addEventListener('click', function (ev) {
                  if (ev && ev.stopPropagation) ev.stopPropagation();
                  wbOpenWaitlistModalForVenue(
                    booking,
                    venueRefId,
                    (venue.venue_name != null ? String(venue.venue_name) : '').trim(),
                    wbFormatWaitlistCustomerLocation(venue, venueGroup)
                  );
                });
              }
            }

            venueCard._wbVenue = venue;

            groupBody.appendChild(venueCard);
          })(group.venues[vi], group);
        }

        // IIFE to capture groupHeader/groupBody/groupSection per iteration (closure fix). Accordion: only one group open at a time; animated open/close.
        (function (gHeader, gBody, gSection) {
          gHeader.addEventListener('click', function () {
            var expanded = gHeader.getAttribute('aria-expanded') === 'true';
            if (expanded) {
              if (document.activeElement && gSection.contains(document.activeElement)) {
                try { gHeader.focus(); } catch (e) {}
              }
              wbCollapseBody(gBody, gHeader);
              var anyOpen = eventsRoot && eventsRoot.querySelector('[data-workshop-group-toggle][aria-expanded="true"]');
              if (!anyOpen) {
                wbLockedVenueRefId = '';
                wbLockedVenueGroupKey = '';
                wstUpdate(1, {});
              }
            } else {
              wbCloseAllVenueCardsExcept(eventsRoot, gSection);
              var resetCards = gBody.querySelectorAll('.wb-venue-card');
              for (var ri = 0; ri < resetCards.length; ri++) {
                resetCards[ri].removeAttribute('data-wb-date-touched');
              }
              wbExpandBody(gBody, gHeader);
              var venueNameOnOpen = '';
              var venueLabelEl = gHeader.querySelector('.wb-group-label');
              if (venueLabelEl) venueNameOnOpen = String(venueLabelEl.textContent || '').trim();
              wstUpdate(2, wstValues(venueNameOnOpen, '', '', '', ''));
            }
            try {
              wbRefreshBookingGuideUi();
            } catch (e) {}
          });
        })(groupHeader, groupBody, groupSection);

        fragment.appendChild(groupSection);
      }

      eventsRoot.appendChild(fragment);
      wbRestoreVenueContext();

      if (groups.length === 0 && eventsPlaceholder) {
        eventsPlaceholder.classList.remove('pdp-wb-hidden');
        eventsPlaceholder.textContent = 'No events found for this location. Try another location or date range.';
      }

      var venueCardsAll = eventsRoot.querySelectorAll('.workshop-venue-card');
      for (var j = 0; j < venueCardsAll.length; j++) syncInCardSummaryForVenueCard(venueCardsAll[j]);
      var slotButtons = eventsRoot.querySelectorAll('.workshop-event-card[data-workshop-event-card]').length;
      try {
        wbRefreshBookingGuideUi();
      } catch (e) {}
      return { groupCards: groups.length, venueCards: venueCardsAll.length, slotButtons: slotButtons };
    }

    /**
     * After event list renders: find the event matching URL event_id and select it.
     * Two-pass: DOM search first, then venue data search for non-first dates.
     */
    function trySelectEventFromUrl() {
      var urlEventId = getEventIdFromUrl();
      if (window.__WB_DIAG__) console.log('[WB_RESTORE] trySelectEventFromUrl called, event_id:', urlEventId);
      if (!urlEventId || !eventsRoot) {
        if (window.__WB_DIAG__) console.log('[WB_RESTORE] abort — no event_id or no eventsRoot');
        return;
      }
      var urlNumeric = extractNumericEventIdForUrl(urlEventId) || urlEventId;
      if (window.__WB_DIAG__) console.log('[WB_RESTORE] normalized numeric id:', urlNumeric);

      /** Expand the group containing this card instantly (no animation wait). */
      function _expandGroupOf(card) {
        var groupSection = card.closest('.wb-venue-group');
        if (!groupSection) return;
        var groupHeader = groupSection.querySelector('[data-workshop-group-toggle]') ||
                          groupSection.querySelector('.wb-group-toggle');
        var groupBody = groupSection.querySelector('.wb-group-body');
        if (groupHeader && groupBody && groupHeader.getAttribute('aria-expanded') !== 'true') {
          groupBody.style.height  = 'auto';
          groupBody.style.opacity = '1';
          groupBody.setAttribute('aria-hidden', 'false');
          groupHeader.setAttribute('aria-expanded', 'true');
        }
      }

      /** Expand group, select card, update state, scroll to booking panel. */
      function _finishSelection(card) {
        if (window.__WB_DIAG__) console.log('[WB_RESTORE] _finishSelection called, card:', card ? card.dataset.eventId : 'null');
        if (!card) return;
        if (!wbIsEventBookable(card)) {
          if (window.__WB_DIAG__) console.log('[WB_RESTORE] not bookable — abort');
          return;
        }

        _expandGroupOf(card);
        setEventSelectionFromCard(card);
        if (window.__WB_DIAG__) console.log('[WB_RESTORE] setEventSelectionFromCard completed');

        // Clear so subsequent location changes don't re-trigger
        window.__WB_INITIAL_EVENT_ID__ = null;

        // Sync all venue card summaries
        var vcAll = eventsRoot.querySelectorAll('.workshop-venue-card');
        for (var v = 0; v < vcAll.length; v++) syncInCardSummaryForVenueCard(vcAll[v]);

        // Advance state so variant selector and buttons become active
        if (useLocationFirst && typeof setWbState === 'function') setWbState(2);

        validateSeatSelection();

        // Scroll booking panel into view (account for sticky header height)
        requestAnimationFrame(function () {
          var panel = booking.querySelector('[data-wb-panel]') || document.querySelector('[data-wb-panel]');
          if (!panel) return;
          function computeHeaderOffset() {
            var offset = 0;
            var headerCandidates = document.querySelectorAll('#shopify-section-header, .site-header, .header-wrapper, header');
            for (var h = 0; h < headerCandidates.length; h++) {
              var headerEl = headerCandidates[h];
              if (!headerEl) continue;
              var style = window.getComputedStyle(headerEl);
              if (style.position !== 'fixed' && style.position !== 'sticky') continue;
              var rect = headerEl.getBoundingClientRect();
              if (rect.height <= 0) continue;
              if (rect.bottom <= 0 || rect.top >= window.innerHeight) continue;
              offset = Math.max(offset, rect.height);
            }
            return offset;
          }
          function scrollWithOffset(behavior) {
            var headerOffset = computeHeaderOffset();
            var panelTop = panel.getBoundingClientRect().top + window.pageYOffset;
            var targetTop = Math.max(0, panelTop - headerOffset - 2);
            window.scrollTo({ top: targetTop, behavior: behavior });
          }
          scrollWithOffset('smooth');
          // Re-align once after layout settles (images/accordion height updates).
          setTimeout(function () { scrollWithOffset('auto'); }, 220);
        });
      }

      // ── Pass 1: DOM search (first date's slots already rendered) ──────────────
      var domCards = eventsRoot.querySelectorAll('.workshop-event-card');
      for (var i = 0; i < domCards.length; i++) {
        var dc = domCards[i];
        var dcId  = (dc.dataset.eventId            || '').toString().trim();
        var dcGid = (dc.dataset.eventMetaobjectGid || '').toString().trim();
        var dcNumeric = normalizeEventIdForMatch(dcId) || normalizeEventIdForMatch(dcGid);
        if (dcNumeric && dcNumeric === urlNumeric) {
          if (window.__WB_DIAG__) console.log('[WB_RESTORE] Pass 1 MATCH — calling _finishSelection for', dcId || dcGid);
          _finishSelection(dc);
          return;
        }
      }

      if (window.__WB_DIAG__) console.log('[WB_RESTORE] Pass 1 missed — searching venue data');

      // ── Pass 2: _wbVenue data search (handles non-first dates) ───────────────
      // Each .wb-venue-card stores _wbVenue with all events across all dates.
      // If target event is on a different date, switch the date dropdown,
      // let the existing 'change' listener rebuild slots, then select.
      var venueCardEls = eventsRoot.querySelectorAll('.wb-venue-card');
      for (var vc = 0; vc < venueCardEls.length; vc++) {
        var vcEl    = venueCardEls[vc];
        var vcVenue = vcEl._wbVenue;
        if (!vcVenue || !Array.isArray(vcVenue.events)) continue;

        for (var ei = 0; ei < vcVenue.events.length; ei++) {
          var ev = vcVenue.events[ei];
          var evNumeric = normalizeEventIdForMatch(String(ev.id                   || '')) ||
                          normalizeEventIdForMatch(String(ev.event_metaobject_gid || ''));
          if (!evNumeric || evNumeric !== urlNumeric) continue;

          if (wbIsEventSoldOut(ev) || wbShouldHideEvent(ev)) return;

          var targetDate   = String(ev.date_key || '').trim();
          var dateSelectEl = vcEl.querySelector('[data-workshop-venue-date]');

          if (dateSelectEl && targetDate && dateSelectEl.value !== targetDate) {
            dateSelectEl.value = targetDate;
            dateSelectEl.dispatchEvent(new Event('change', { bubbles: true }));
          }

          (function (_vcEl, _urlNumeric) {
            setTimeout(function () {
              var slotCards = _vcEl.querySelectorAll('.workshop-event-card');
              var matchCard = null;
              for (var s = 0; s < slotCards.length; s++) {
                var sNum = normalizeEventIdForMatch(slotCards[s].dataset.eventId            || '') ||
                           normalizeEventIdForMatch(slotCards[s].dataset.eventMetaobjectGid || '');
                if (sNum === _urlNumeric) { matchCard = slotCards[s]; break; }
              }
              _finishSelection(matchCard);
            }, 0);
          })(vcEl, urlNumeric);

          return;
        }
      }
    }

    if (typeof window !== 'undefined') window.__WB_trySelectEventFromUrl = trySelectEventFromUrl;

    // Legacy renderEventsListFromApi removed: venue-centric UI uses renderVenueCentricList

    /**
     * Venue-centric: all fetch flows must resolve to lat/lng and call fetchEventsByLatLng.
     * city_key and state_code are for display/grouping only; API is always lat/lng.
     */
    async function onLocationResolved(resolved) {
      var resolvedCityKey = (resolved.city_key != null && String(resolved.city_key).trim()) ? String(resolved.city_key).trim() : null;
      var resolvedStateCode = (resolved.state_code || resolved.state || '').toString().trim().toUpperCase();
      if (window.__WB_DIAG__) console.log('[workshop] onLocationResolved', { source: resolved.source, city_key: resolvedCityKey, state_code: resolvedStateCode, lat: resolved.lat, lng: resolved.lng });

      // 1) Only lat/lng triggers fetch. No city_key or state_code fallback.
      if (resolved.lat != null && resolved.lng != null && Number.isFinite(Number(resolved.lat)) && Number.isFinite(Number(resolved.lng))) {
        var label = [resolved.city, resolved.state || resolved.state_code].filter(Boolean).join(', ') || (resolved.lat + ', ' + resolved.lng);
        fetchEventsByLatLng(booking, Number(resolved.lat), Number(resolved.lng), getRadiusKm(booking), { label: label, source: resolved.source || 'places' });
        return;
      }

      // 2) No lat/lng: silently return. Do NOT clear UI or show error.
      //    Only handlePlaceSelect with Place + fetchFields triggers fetches.
      if (window.__WB_DIAG__) console.log('[WB_DIAG] onLocationResolved: no valid lat/lng, skipping (no UI change)');
      return;
    }

    function onLocationClear(opts) {
      window.__WORKSHOP_LOCATION__ = null;
      wbSetUserLoc(null, null);
      window.__WORKSHOP_VENUE_LIST__ = null;
      window.__WORKSHOP_PLACE_LAST_ID__ = '';
      window.__WB_LAST_CACHE_KEY__ = '';
      if (typeof window !== 'undefined') window.__WB_USER_CHOSEN_LOCATION__ = false;
      wbSetLocationUi({ clear: true });
      if (opts && opts.refetchDefault) {
        triggerDefaultPrefetch({ force: true });
      }
    }

    // Radius selector is intentionally disabled. Radius is controlled from Theme Editor.

    /** IP fallback when GPS/Wi‑Fi geolocation is unavailable (common on desktop). */
    function wbFetchApproxLocationFromIp() {
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, 8000) : null;
      return fetch('https://ipapi.co/json/', controller ? { signal: controller.signal } : undefined)
        .then(function (res) {
          if (!res.ok) throw new Error('ip geolocation http ' + res.status);
          return res.json();
        })
        .then(function (data) {
          var lat = Number(data.latitude);
          var lng = Number(data.longitude);
          if (!Number.isFinite(lat) || !Number.isFinite(lng)) throw new Error('ip geolocation missing coords');
          var label = [data.city, data.region_code || data.region].filter(Boolean).join(', ') || 'Your area';
          return { lat: lat, lng: lng, label: label, source: 'ip_geolocation' };
        })
        .finally(function () {
          if (timer) clearTimeout(timer);
        });
    }

    /**
     * Browser geolocation with staged accuracy + IP fallback. Wizard and PDP share this path.
     */
    function wbRunGeolocationWithFallback(onCoords, onError) {
      if (!navigator.geolocation) {
        wbFetchApproxLocationFromIp()
          .then(function (ip) {
            onCoords(ip.lat, ip.lng, ip.label, ip.source);
          })
          .catch(function () {
            onError({ code: 2 });
          });
        return;
      }
      function tryGeo(opts, onRecoverable) {
        navigator.geolocation.getCurrentPosition(
          function (pos) {
            onCoords(pos.coords.latitude, pos.coords.longitude, null, 'geolocation');
          },
          function (err) {
            var code = err && err.code;
            if (code === 1) {
              onError(err);
              return;
            }
            if (onRecoverable && (code === 2 || code === 3)) {
              onRecoverable();
              return;
            }
            onError(err);
          },
          opts
        );
      }
      tryGeo({ enableHighAccuracy: false, timeout: 22000, maximumAge: 300000 }, function () {
        tryGeo({ enableHighAccuracy: true, timeout: 22000, maximumAge: 0 }, function () {
          tryGeo({ enableHighAccuracy: false, timeout: 30000, maximumAge: 0 }, function () {
            wbFetchApproxLocationFromIp()
              .then(function (ip) {
                onCoords(ip.lat, ip.lng, ip.label, ip.source);
              })
              .catch(function () {
                onError({ code: 2 });
              });
          });
        });
      });
    }

    if (useLocBtn && !useLocBtn.dataset.wbUseLocBound) {
      useLocBtn.dataset.wbUseLocBound = '1';
      useLocBtn.addEventListener('click', function () {
        if (DEV_MODE) console.log('[WB_RADIUS] "Use my location" click fired');
        if (typeof window !== 'undefined') window.__WB_USER_CHOSEN_LOCATION__ = true;
        if (eventsRoot) eventsRoot.innerHTML = '';
        window.__WORKSHOP_VENUE_LIST__ = null;
        setLoading(booking, true);

        function wbUseLocationFetch(lat, lng, presetLabel, fetchSource) {
          var locationLabel = presetLabel || 'Current location';
          var source = fetchSource || 'geolocation';
          if (DEV_MODE) {
            console.log(
              '[WB_RADIUS] location resolved lat=' + lat + ' lng=' + lng + ' source=' + source + ' radius=' + getRadiusKm(booking)
            );
          }
          if (window.__WB_DIAG__) console.log('[WB_DIAG] Use my location → lat/lng', { lat: lat, lng: lng, source: source });
          var REVERSE_GEOCODE_TIMEOUT_MS = 2500;
          var done = false;
          function doFetch() {
            if (done) return;
            done = true;
            wbSetLocationUi({ label: locationLabel });
            fetchEventsByLatLng(booking, lat, lng, getRadiusKm(booking), { source: source, label: locationLabel });
          }
          if (presetLabel || source === 'ip_geolocation') {
            doFetch();
            return;
          }
          if (window.google && window.google.maps && window.google.maps.Geocoder) {
            var geocoder = new google.maps.Geocoder();
            geocoder.geocode({ location: { lat: lat, lng: lng } }, function (results, status) {
              if (done) return;
              if (status === 'OK' && results && results[0]) {
                var city = '';
                var state = '';
                (results[0].address_components || []).forEach(function (comp) {
                  if (comp.types.includes('locality')) city = comp.long_name;
                  else if (!city && comp.types.includes('sublocality_level_1')) city = comp.long_name;
                  else if (!city && comp.types.includes('postal_town')) city = comp.long_name;
                  if (comp.types.includes('administrative_area_level_1')) state = comp.short_name;
                });
                var resolvedLabel = [city, state].filter(Boolean).join(', ');
                if (resolvedLabel) locationLabel = resolvedLabel;
                if (window.__WB_DIAG__) console.log('[WB_DIAG] Reverse geocode resolved:', locationLabel);
              }
              doFetch();
            });
            setTimeout(function () {
              doFetch();
            }, REVERSE_GEOCODE_TIMEOUT_MS);
          } else {
            doFetch();
          }
        }

        wbRunGeolocationWithFallback(wbUseLocationFetch, function (err) {
          if (typeof window !== 'undefined') window.__WB_USER_CHOSEN_LOCATION__ = false;
          setLoading(booking, false);
          var code = err && err.code;
          var msg =
            code === 1
              ? 'Location permission denied. Allow location access in your browser, then try again.'
              : code === 2
                ? 'Could not determine your location. Search for a city instead.'
                : code === 3
                  ? 'Location request timed out. Try again.'
                  : 'Could not access your location. Try again or search for a city.';
          showWorkshopFetchError(msg);
          if (DEV_MODE) console.warn('[WB_RADIUS] geolocation error', err);
        });
      });
    }
    if (retryBtn) retryBtn.addEventListener('click', function () { onLocationClear({ refetchDefault: true }); });

    // Near-city-link handler removed: venue-centric UI does not use near locations.
    // All fetch flows use lat/lng only.

    function bindGooglePlacesPlaceElement() {
      if (!googleKey || !locInput) return;
      if (locInput.dataset.googleBound) {
        // Already bound, but ensure listeners are active
        var existing = document.querySelector('#workshop-location-place-autocomplete,[data-workshop-place-autocomplete]');
        if (existing) {
          ensureDocPlaceListeners();
          ensurePlaceListeners(existing);
          startPlaceInputWatcher(existing);
        }
        return;
      }
      var existing = document.querySelector('#workshop-location-place-autocomplete,[data-workshop-place-autocomplete]');
      if (existing) {
        ensureDocPlaceListeners();
        ensurePlaceListeners(existing);
        startPlaceInputWatcher(existing);
        return;
      }
      const lib = window.google && window.google.maps && window.google.maps.places;
      const PlaceAutocompleteElement = lib && lib.PlaceAutocompleteElement;
      if (!PlaceAutocompleteElement) return;
      try {
        const parent = locInput.parentNode;
        if (!parent) return;
        var isWizardBooking = !!(booking.classList && booking.classList.contains('wb-wizard'));
        var placeOpts = {
          placeholder: locInput.placeholder || 'Search city or area'
        };
        if (isWizardBooking) placeOpts.noInputIcon = true;
        var placeEl = new PlaceAutocompleteElement(placeOpts);
        placeEl.className = 'wbw-search-place-host wbw-search-field pdp-wb-location-input';
        placeEl.id = 'workshop-location-place-autocomplete';
        placeEl.setAttribute('data-workshop-place-autocomplete', '1');
        if (isWizardBooking) placeEl.setAttribute('no-input-icon', '');
        placeEl.dataset.googleBound = '1';
        placeEl.dataset.workshopLocationInput = '1';
        placeEl.setAttribute('aria-label', locInput.getAttribute('aria-label') || 'Location');
        locInput.style.display = 'none';
        locInput.dataset.googleBound = '1';
        parent.insertBefore(placeEl, locInput);
        applyWizardPlaceAutocompleteChrome(placeEl);
        ensureDocPlaceListeners();
        ensurePlaceListeners(placeEl);
        startPlaceInputWatcher(placeEl);
        if (booking.classList && booking.classList.contains('wb-wizard')) {
          var wrap = locInput.closest('.wb-search-input-wrap');
          if (wrap) wrap.classList.add('wbw-search-has-place');
          var notifyPlacesMounted = function () {
            try {
              booking.dispatchEvent(new CustomEvent('wbw:places-mounted', { bubbles: true }));
            } catch (_) {}
          };
          notifyPlacesMounted();
          setTimeout(notifyPlacesMounted, 80);
          setTimeout(notifyPlacesMounted, 400);
          setTimeout(notifyPlacesMounted, 1200);
        }
      } catch (e) {
        gmapsLastError = (e && e.message) || '[WB] PlaceAutocompleteElement failed';
        if (window.__WB_DIAG__) console.warn('[WB]', gmapsLastError);
      }
    }

    /** Continuously watch widget input for value changes and trigger fetch when a comma-separated address appears. */
    function startPlaceInputWatcher(placeEl) {
      if (!placeEl || placeEl.dataset.wbInputWatcherStarted) return;
      placeEl.dataset.wbInputWatcherStarted = '1';
      var lastValue = '';
      var lastProcessedKey = '';
      var checkInterval = setInterval(function () {
        if (!placeEl.parentNode) {
          clearInterval(checkInterval);
          return;
        }
        var currentValue = '';
        try {
          var inner = placeEl.shadowRoot && placeEl.shadowRoot.querySelector('input');
          if (inner && inner.value) currentValue = inner.value.trim();
          if (!currentValue) {
            var hidden = document.getElementById('workshop-location-input');
            if (hidden && hidden.value) currentValue = hidden.value.trim();
          }
          if (!currentValue && locSummary && locSummary.textContent) currentValue = locSummary.textContent.trim();
        } catch (_) {}
        if (currentValue && currentValue !== lastValue && currentValue.indexOf(',') >= 0 && currentValue !== 'Location selected') {
          lastValue = currentValue;
          var fallbackParsed = parseCityStateFromFormattedAddress(currentValue);
          if (fallbackParsed && (fallbackParsed.city || fallbackParsed.state_code)) {
            var prev = window.__WORKSHOP_LOCATION__ || null;
            var prevKey = prev && prev.city_key ? String(prev.city_key).trim() : '';
            var prevState = prev && prev.state_code ? String(prev.state_code).trim().toUpperCase() : '';
            var flowRef = window.WorkshopLocationFlow;
            var cityKey = (flowRef && typeof flowRef.deriveEventCityKey === 'function')
              ? flowRef.deriveEventCityKey(fallbackParsed.state_code, fallbackParsed.city)
              : '';
            var newState = (fallbackParsed.state_code || fallbackParsed.state || '').toString().trim().toUpperCase();
            var processKey = (cityKey || '') + '|' + newState;
            if (prevKey !== cityKey || prevState !== newState) {
              if (lastProcessedKey === processKey) {
                if (window.__WB_DIAG__) console.log('[WB_DIAG] place input watcher: skipping duplicate', { city_key: cityKey, state_code: newState });
                return;
              }
              lastProcessedKey = processKey;
              var resolved = {
                lat: undefined,
                lng: undefined,
                city: fallbackParsed.city,
                state: fallbackParsed.state,
                state_code: newState,
                country: fallbackParsed.country || 'US',
                source: 'places',
                city_key: cityKey ? String(cityKey).trim() : undefined
              };
              if (window.__WB_DIAG__) console.log('[WB_DIAG] place input watcher: text changed, no lat/lng — skipping (only handlePlaceSelect triggers fetch)', { state_code: resolved.state_code, currentValue: currentValue });
              // Do NOT call onLocationResolved without lat/lng — it would clear UI and show error.
              // Only handlePlaceSelect (with Place + fetchFields) triggers event fetches.
            }
          }
        }
      }, 200);
      
      // Also use MutationObserver for faster detection
      try {
        var observer = new MutationObserver(function(mutations) {
          var currentValue = '';
          try {
            var inner = placeEl.shadowRoot && placeEl.shadowRoot.querySelector('input');
            if (inner && inner.value) currentValue = inner.value.trim();
            if (!currentValue) {
              var hidden = document.getElementById('workshop-location-input');
              if (hidden && hidden.value) currentValue = hidden.value.trim();
            }
            if (!currentValue && locSummary && locSummary.textContent) currentValue = locSummary.textContent.trim();
          } catch (_) {}
          if (currentValue && currentValue.indexOf(',') >= 0 && currentValue !== 'Location selected') {
            // Trigger immediate check
            setTimeout(function() {
              var checkValue = '';
              try {
                var inner = placeEl.shadowRoot && placeEl.shadowRoot.querySelector('input');
                if (inner && inner.value) checkValue = inner.value.trim();
                if (!checkValue) {
                  var hidden = document.getElementById('workshop-location-input');
                  if (hidden && hidden.value) checkValue = hidden.value.trim();
                }
                if (!checkValue && locSummary && locSummary.textContent) checkValue = locSummary.textContent.trim();
              } catch (_) {}
              if (checkValue && checkValue.indexOf(',') >= 0) {
                var parsed = parseCityStateFromFormattedAddress(checkValue);
                if (parsed && (parsed.city || parsed.state_code)) {
                  var flowRef = window.WorkshopLocationFlow;
                  var cityKey = (flowRef && typeof flowRef.deriveEventCityKey === 'function')
                    ? flowRef.deriveEventCityKey(parsed.state_code, parsed.city)
                    : '';
                  var newState = (parsed.state_code || parsed.state || '').toString().trim().toUpperCase();
                  var prev = window.__WORKSHOP_LOCATION__ || null;
                  var prevKey = prev && prev.city_key ? String(prev.city_key).trim() : '';
                  var prevState = prev && prev.state_code ? String(prev.state_code).trim().toUpperCase() : '';
                  if (prevKey !== cityKey || prevState !== newState) {
                    var resolved = {
                      lat: undefined,
                      lng: undefined,
                      city: parsed.city,
                      state: parsed.state,
                      state_code: newState,
                      country: parsed.country || 'US',
                      source: 'places',
                      city_key: cityKey ? String(cityKey).trim() : undefined
                    };
                    if (window.__WB_DIAG__) console.log('[WB_DIAG] MutationObserver: text changed, no lat/lng — skipping (only handlePlaceSelect triggers fetch)', { state_code: resolved.state_code, checkValue: checkValue });
                    // Do NOT call onLocationResolved without lat/lng — it would clear UI and show error.
                  }
                }
              }
            }, 100);
          }
        });
        // Observe the hidden input and locSummary
        var hiddenInput = document.getElementById('workshop-location-input');
        if (hiddenInput) observer.observe(hiddenInput, { attributes: true, attributeFilter: ['value'] });
        if (locSummary) observer.observe(locSummary, { childList: true, characterData: true, subtree: true });
      } catch (e) {
        if (window.__WB_DIAG__) console.warn('[WB_DIAG] MutationObserver failed', e);
      }
    }

    function attachPlaceAutocompleteFocusHandler(inputEl) {
      if (!inputEl || inputEl.dataset.googleBound || !googleKey) return;
      inputEl.addEventListener('focus', function onLocFocus() {
        ensureGooglePlacesLoaded(googleKey).then(() => bindGooglePlacesPlaceElement()).catch((err) => {
          gmapsLastError = (err && err.message) || '[WB] Google Places load failed';
          if (window.__WB_DIAG__) console.warn('[WB]', gmapsLastError);
        });
        inputEl.removeEventListener('focus', onLocFocus);
      }, { once: true });
    }
    if (googleKey && locInput) {
      if (booking.classList && booking.classList.contains('wb-wizard')) {
        ensureGooglePlacesLoaded(googleKey)
          .then(function () {
            bindGooglePlacesPlaceElement();
          })
          .catch(function (err) {
            gmapsLastError = (err && err.message) || '[WB] Google Places load failed';
            if (window.__WB_DIAG__) console.warn('[WB]', gmapsLastError);
          });
      } else {
        attachPlaceAutocompleteFocusHandler(locInput);
      }
    }

    document.addEventListener('shopify:section:load', () => {
      if (!useLocationFirst || !googleKey) return;
      requestAnimationFrame(() => {
        const newInput = document.getElementById('workshop-location-input');
        const root = document.querySelector('.pdp-workshop-booking');
        if (root && newInput && newInput.closest('.pdp-workshop-booking') === root && !newInput.dataset.googleBound) {
          attachPlaceAutocompleteFocusHandler(newInput);
        }
      });
    });
    window.__WORKSHOP_DIAG_GMAPS = function () {
      const kEl =
        (booking && booking.querySelector && booking.querySelector('[data-google-maps-key]')) ||
        (booking && booking.hasAttribute && booking.hasAttribute('data-google-maps-key') ? booking : null);
      const k = kEl ? (kEl.getAttribute('data-key') || (kEl.dataset && kEl.dataset.key) || '') : '';
      const scripts = Array.from(document.querySelectorAll('script[src*="maps.googleapis.com"]')).map((s) => s.src);
      const placeElementMounted = !!document.getElementById('workshop-location-place-autocomplete');
      return {
        hasKey: !!k && k.length > 0,
        keyLen: (k || '').length,
        hasGoogle: !!(typeof window !== 'undefined' && window.google),
        hasPlaces: !!(typeof window !== 'undefined' && window.google && window.google.maps && window.google.maps.places),
        scripts,
        placeElementMounted,
        lastError: gmapsLastError || null
      };
    };

    /* Waitlist modal: only [data-wb-waitlist-open] opens it — no whole-card click (see renderVenueCentricList openBtn listener). */
  }

  // Sync selection from workshop-events-ui (App Proxy flow) when event-selected is dispatched
  document.addEventListener('workshop:event-selected', (e) => {
    const d = e && e.detail;
    if (!d || !d.eventId) return;
    window.__WORKSHOP_SELECTION__ = {
      eventId: d.eventId,
      eventHandle: d.eventHandle || '',
      city: '',
      state: '',
      venue: '',
      datetime: ''
    };
    if (selectedEventIdInput) selectedEventIdInput.value = d.eventId;
    var numericEventIdForUrl = extractNumericEventIdForUrl(d.eventId);
    window.__WB_URL_EVENT_ID_NUMERIC__ = numericEventIdForUrl || '';
    setTimeout(function () {
      updateUrlForEventId(numericEventIdForUrl);
    }, 0);
    if (useLocationFirst) {
      setWbState(2);
      if (bookNowBtn) { bookNowBtn.disabled = true; bookNowBtn.style.cursor = 'not-allowed'; }
      if (addToCartBtn) { addToCartBtn.disabled = true; addToCartBtn.style.cursor = 'not-allowed'; }
    }
    validateSeatSelection();
  });

  // Event selection (Date/Time selection)
  if (eventsRoot) {
    eventsRoot.addEventListener('click', (e) => {
      const card = e.target.closest('.workshop-event-card');
      if (!card) return;

      if (wbIsEventSoldOut(card)) {
        showSoldOutItemWarning();
        return;
      }
      if (!wbIsEventBookable(card)) return;

      setEventSelectionFromCard(card);
      wstUpdate(
        4,
        wstValuesWithQty(
          String(card.getAttribute('data-venue-name') || '').trim(),
          wstFormatDateKey(card.getAttribute('data-date-key') || ''),
          String(card.getAttribute('data-time-label') || '').trim(),
          '',
          String(Math.round(Number((quantityInput && quantityInput.value) || 1) || 1)),
          ''
        )
      );
      var venueCards = eventsRoot.querySelectorAll('.workshop-venue-card');
      for (var i = 0; i < venueCards.length; i++) syncInCardSummaryForVenueCard(venueCards[i]);
      if (useLocationFirst) {
        setWbState(2);
        if (bookNowBtn) { bookNowBtn.disabled = true; bookNowBtn.style.cursor = 'not-allowed'; }
        if (addToCartBtn) { addToCartBtn.disabled = true; addToCartBtn.style.cursor = 'not-allowed'; }
      }
      validateSeatSelection();

      // Force full expanded state on time-slot click. wbOpenStickyPanel can
      // no-op when bookingBox is null or no [data-ai-wb-sticky] root resolves,
      // leaving the panel neutral (no wb-panel--* class) and bypassing all
      // .wb-panel--expanded-scoped styles. Idempotent.
      var panel = booking.querySelector('[data-wb-panel]') || document.querySelector('[data-wb-panel]');
      if (panel) {
        panel.classList.remove('wb-panel--collapsed');
        panel.classList.add('wb-panel--expanded');
        panel.classList.add('wb-sticky-open');
        var expandedToggle = panel.querySelector('[data-ai-wb-sticky-toggle]');
        if (expandedToggle) expandedToggle.setAttribute('aria-expanded', 'true');
        var expandedBody = panel.querySelector('[data-ai-wb-sticky-body]');
        if (expandedBody) {
          expandedBody.style.maxHeight = '';
          expandedBody.style.overflow = '';
          expandedBody.classList.remove('pdp-wb-hidden');
        }
      }
      requestAnimationFrame(function () {
        var kitAnchor = document.querySelector('[data-wst-anchor="kit"]');
        if (kitAnchor) {
          var header = document.querySelector('header, .site-header, #shopify-section-header');
          var headerH = header ? Math.ceil(header.getBoundingClientRect().height) + 8 : 80;
          var rect = kitAnchor.getBoundingClientRect();
          var viewH = window.innerHeight - headerH;
          var targetH = rect.height;
          var offset = window.scrollY + rect.top - headerH - Math.max(0, (viewH - targetH) / 2);
          window.scrollTo({ top: Math.round(Math.max(0, offset)), behavior: 'smooth' });
        }
      });
    });
  }

  // Quantity change handler for capacity validation (metaobject-based)
  const quantityInput = bookingBox ? bookingBox.querySelector('[data-workshop-quantity]') : null;
  var qtyMinusBtn = bookingBox ? bookingBox.querySelector('[data-wb-qty-minus]') : null;
  var qtyPlusBtn = bookingBox ? bookingBox.querySelector('[data-wb-qty-plus]') : null;
  function wbClampAndValidateQty(nextValue) {
    if (!quantityInput) return;
    var min = Math.max(1, Number(quantityInput.getAttribute('min') || 1));
    var maxRaw = Number(quantityInput.getAttribute('max'));
    var hasMax = Number.isFinite(maxRaw) && maxRaw > 0;
    var n = Number(nextValue);
    if (!Number.isFinite(n)) n = min;
    n = Math.round(n);
    if (n < min) n = min;
    if (hasMax && n > maxRaw) n = maxRaw;
    quantityInput.value = String(n);
    validateSeatSelection();
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
  if (quantityInput) {
    quantityInput.addEventListener('input', (e) => {
      validateSeatSelection();
      if (typeof wbUpdateSelectedItemsSummary === 'function') wbUpdateSelectedItemsSummary();
      var factsOnQtyInput = wstGetCurrentBookingFacts();
      if (factsOnQtyInput && factsOnQtyInput.venueName && factsOnQtyInput.dateLabel && factsOnQtyInput.timeLabel && factsOnQtyInput.variantName) {
        var addonForStep = typeof addonSelection === 'function' ? addonSelection() : null;
        var wstStepForQty = addonForStep && addonForStep.selected ? 6 : 5;
        wstUpdate(
          wstStepForQty,
          wstValuesWithQty(
            factsOnQtyInput.venueName,
            factsOnQtyInput.dateLabel,
            factsOnQtyInput.timeLabel,
            factsOnQtyInput.variantName,
            factsOnQtyInput.qtyLabel || '',
            factsOnQtyInput.extrasName || 'None'
          )
        );
      }
    });
    
    quantityInput.addEventListener('change', (e) => {
      validateSeatSelection();
      if (typeof wbUpdateSelectedItemsSummary === 'function') wbUpdateSelectedItemsSummary();
      var factsOnQtyChange = wstGetCurrentBookingFacts();
      if (factsOnQtyChange && factsOnQtyChange.venueName && factsOnQtyChange.dateLabel && factsOnQtyChange.timeLabel && factsOnQtyChange.variantName) {
        var addonForStepCh = typeof addonSelection === 'function' ? addonSelection() : null;
        var wstStepForQtyCh = addonForStepCh && addonForStepCh.selected ? 6 : 5;
        wstUpdate(
          wstStepForQtyCh,
          wstValuesWithQty(
            factsOnQtyChange.venueName,
            factsOnQtyChange.dateLabel,
            factsOnQtyChange.timeLabel,
            factsOnQtyChange.variantName,
            factsOnQtyChange.qtyLabel || '',
            factsOnQtyChange.extrasName || 'None'
          )
        );
      }
    });
  }
  if (qtyMinusBtn && !qtyMinusBtn.dataset.wbBound) {
    qtyMinusBtn.dataset.wbBound = '1';
    qtyMinusBtn.addEventListener('click', function () {
      var current = Number((quantityInput && quantityInput.value) || 1);
      wbClampAndValidateQty(current - 1);
    });
  }
  if (qtyPlusBtn && !qtyPlusBtn.dataset.wbBound) {
    qtyPlusBtn.dataset.wbBound = '1';
    qtyPlusBtn.addEventListener('click', function () {
      var current = Number((quantityInput && quantityInput.value) || 1);
      wbClampAndValidateQty(current + 1);
    });
  }

  // Bind validation to event selection
  document.addEventListener('click', (e) => {
    const card = e.target.closest('.workshop-event-card');
    if (card) {
      // Existing selection logic is handled by setEventSelectionFromCard
      // Just validate capacity after selection
      setTimeout(() => {
        validateSeatSelection();
      }, 0);
    }
  });

  // Listen to selector changes
  window.addEventListener('workshop:filter-changed', applyFilters);
  if (!useLocationFirst) {
    if (stateInput) stateInput.addEventListener('input', applyFilters);
    if (cityInput) cityInput.addEventListener('input', applyFilters);
  }
  function handleVariantChange() {
    variantFilterActive = true;
    if (useLocationFirst && wbState === 2) setWbState(3);
    applyFilters();
    var selectedCard = eventsRoot ? eventsRoot.querySelector('.workshop-event-card.is-selected') : null;
    if (!selectedCard) {
      var sheetRoot1 = document.getElementById('wb-sheet-body');
      var hasSheetBookingUi1 = sheetRoot1 && sheetRoot1.querySelector('.pdp-workshop-booking');
      var inSheetContext1 = !!(booking && booking.closest && booking.closest('#wb-sheet-body'));
      if (hasSheetBookingUi1 && inSheetContext1) {
        selectedCard =
          document.querySelector('#wb-event-list .wb-all__event-card.is-selected') ||
          document.querySelector('.wb-all__event-card.is-selected');
      }
    }
    if (selectedCard) {
      showPreviewPrice(selectedCard);
      var detail = buildSelectionDetailFromCard(selectedCard);
      if (detail) wbDispatchSelection(detail);
    } else {
      setLastPriceFromVariant();
    }
    updateAllVariantCardPrices();
  }

  booking.querySelectorAll('.wb-variant-btn').forEach(function (btn) {
    btn.addEventListener('click', function () {
      booking.querySelectorAll('.wb-variant-btn').forEach(function (b) { b.classList.remove('is-active'); });
      btn.classList.add('is-active');
      var vid = btn.dataset.variantId || '';
      if (variantSelect) variantSelect.value = vid;
      motionTriggerVariantById(vid);
      handleVariantChange();
      var selectedCard = eventsRoot ? eventsRoot.querySelector('.workshop-event-card.is-selected') : null;
      if (selectedCard) {
        var variantNameClick = String(btn.getAttribute('data-variant-title') || btn.dataset.variantTitle || '').trim();
        if (!variantNameClick) {
          var variantTitleElClick = btn.querySelector('.wb-variant-title');
          variantNameClick = variantTitleElClick ? String(variantTitleElClick.textContent || '').trim() : '';
        }
        wstUpdate(
          5,
          wstValuesWithQty(
            String(selectedCard.getAttribute('data-venue-name') || '').trim(),
            wstFormatDateKey(selectedCard.getAttribute('data-date-key') || ''),
            String(selectedCard.getAttribute('data-time-label') || '').trim(),
            variantNameClick,
            String(Math.round(Number((quantityInput && quantityInput.value) || 1) || 1)),
            ''
          )
        );
      }
      wbKeepStandaloneSheetExpanded();
    });
  });

  if (variantSelect) variantSelect.addEventListener('change', function () {
    motionTriggerVariantById(variantSelect.value);
    handleVariantChange();
    var selectedCard = eventsRoot ? eventsRoot.querySelector('.workshop-event-card.is-selected') : null;
    if (selectedCard) {
      var activeBtnFromChange = booking ? booking.querySelector('.wb-variant-btn.is-active') : null;
      var variantNameChange = activeBtnFromChange ? String(activeBtnFromChange.getAttribute('data-variant-title') || activeBtnFromChange.dataset.variantTitle || '').trim() : '';
      if (!variantNameChange && activeBtnFromChange) {
        var variantTitleElChange = activeBtnFromChange.querySelector('.wb-variant-title');
        variantNameChange = variantTitleElChange ? String(variantTitleElChange.textContent || '').trim() : '';
      }
      wstUpdate(
        5,
        wstValuesWithQty(
          String(selectedCard.getAttribute('data-venue-name') || '').trim(),
          wstFormatDateKey(selectedCard.getAttribute('data-date-key') || ''),
          String(selectedCard.getAttribute('data-time-label') || '').trim(),
          variantNameChange,
          String(Math.round(Number((quantityInput && quantityInput.value) || 1) || 1)),
          ''
        )
      );
    }
  });

  // Initial price on load: show selected variant base price before any event selection
  setLastPriceFromVariant();
  updateAllVariantCardPrices();
  wstUpdate(1, {});

  function wbIsStandaloneMobileSheetContext() {
    return !!(
      booking &&
      booking.classList &&
      booking.classList.contains('wb-standalone') &&
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 1024px)').matches
    );
  }

  function wbKeepStandaloneSheetExpanded() {
    if (!wbIsStandaloneMobileSheetContext()) return;
    var panel = (booking && booking.closest && booking.closest('[data-wb-panel]')) ||
      (booking && booking.querySelector && booking.querySelector('[data-wb-panel]')) ||
      document.querySelector('[data-wb-panel]');
    if (!panel) return;
    var toggle = panel.querySelector('[data-ai-wb-sticky-toggle]');
    var body = panel.querySelector('[data-ai-wb-sticky-body]');
    var alreadyExpanded =
      panel.classList.contains('wb-panel--expanded') &&
      !panel.classList.contains('wb-panel--collapsed') &&
      panel.classList.contains('wb-sticky-open') &&
      (!toggle || toggle.getAttribute('aria-expanded') === 'true') &&
      (!body || !body.classList.contains('pdp-wb-hidden'));
    if (alreadyExpanded) return;

    panel.classList.remove('wb-panel--collapsed');
    panel.classList.add('wb-panel--expanded');
    panel.classList.add('wb-sticky-open');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    if (body) {
      body.style.maxHeight = '';
      body.style.overflow = '';
      body.classList.remove('pdp-wb-hidden');
    }
  }

  function wbCloseStandaloneMobilePanel() {
    if (!wbIsStandaloneMobileSheetContext()) return;
    var panel = (booking && booking.closest && booking.closest('[data-ai-wb-sticky]')) ||
      (booking && booking.closest && booking.closest('[data-wb-panel]')) ||
      (booking && booking.querySelector && booking.querySelector('[data-ai-wb-sticky]')) ||
      (booking && booking.querySelector && booking.querySelector('[data-wb-panel]')) ||
      document.querySelector('[data-ai-wb-sticky]') ||
      document.querySelector('[data-wb-panel]');
    if (!panel) return;
    panel.classList.remove('wb-panel--expanded');
    panel.classList.remove('wb-sticky-open');
    panel.classList.add('wb-panel--collapsed');
    var toggle = panel.querySelector('[data-ai-wb-sticky-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    var body = panel.querySelector('[data-ai-wb-sticky-body]');
    if (body) {
      body.style.maxHeight = '0px';
      body.style.overflow = 'hidden';
      body.classList.add('pdp-wb-hidden');
    }
    wbCloseMobilePreview();
  }

  // Optional add-on card (mobile sheet): selectable state for Turkish Coffee.
  var addonCard = null;
  var addonCards = [];
  var addonQtyInputs = [];
  var addonQtyMinusBtns = [];
  var addonQtyPlusBtns = [];
  if (bookingBox) {
    var addonCandidates = bookingBox.querySelectorAll('[data-wb-addon-card]');
    addonCards = Array.prototype.slice.call(addonCandidates || []);
    addonQtyInputs = Array.prototype.slice.call(bookingBox.querySelectorAll('[data-wb-addon-qty-input]') || []);
    addonQtyMinusBtns = Array.prototype.slice.call(bookingBox.querySelectorAll('[data-wb-addon-qty-minus]') || []);
    addonQtyPlusBtns = Array.prototype.slice.call(bookingBox.querySelectorAll('[data-wb-addon-qty-plus]') || []);
    for (var ai = 0; ai < addonCandidates.length; ai++) {
      var cand = addonCandidates[ai];
      if (!cand) continue;
      var candStyle = window.getComputedStyle ? window.getComputedStyle(cand) : null;
      var hiddenByCss = candStyle && (candStyle.display === 'none' || candStyle.visibility === 'hidden');
      if (hiddenByCss) continue;
      addonCard = cand;
      break;
    }
    if (!addonCard) addonCard = addonCandidates[0] || null;
    addonCards.forEach(function (card) {
      card.removeAttribute('data-wst-anchor');
    });
    if (addonCard) addonCard.setAttribute('data-wst-anchor', 'extras');
  }
  function getWorkshopQtyValue() {
    var n = Number((quantityInput && quantityInput.value) || 1);
    if (!Number.isFinite(n) || n < 1) n = 1;
    return Math.round(n);
  }
  function clampAddonQty(nextValue) {
    var n = Number(nextValue);
    if (!Number.isFinite(n)) n = 0;
    n = Math.round(n);
    if (n < 0) n = 0;
    return n;
  }
  function setAddonQty(nextValue) {
    var n = clampAddonQty(nextValue);
    addonCards.forEach(function (card) {
      if (!card) return;
      card.setAttribute('data-addon-quantity', String(n));
    });
    addonQtyInputs.forEach(function (input) {
      if (!input) return;
      input.value = String(n);
    });
    if (typeof wbUpdateSelectedItemsSummary === 'function') wbUpdateSelectedItemsSummary();
  }
  function getAddonQty() {
    var primaryCard = addonCard || (addonCards.length ? addonCards[0] : null);
    var raw = primaryCard ? primaryCard.getAttribute('data-addon-quantity') : '';
    if ((!raw || !raw.trim()) && addonQtyInputs.length) raw = addonQtyInputs[0].value;
    return clampAddonQty(raw);
  }
  var mobilePreviewEnabled = ((booking && booking.dataset && booking.dataset.wbMobilePreviewEnabled) || 'true') !== 'false';
  var mobilePreviewSubtitle = ((booking && booking.dataset && booking.dataset.wbMobilePreviewSubtitle) || 'Variant details').trim() || 'Variant details';
  var mobilePreviewOffset = Number((booking && booking.dataset && booking.dataset.wbMobilePreviewOffset) || 14);
  if (!Number.isFinite(mobilePreviewOffset)) mobilePreviewOffset = 14;
  function addonSelection() {
    var primaryCard = addonCard || (addonCards.length ? addonCards[0] : null);
    if (!primaryCard) return { selected: false, variantId: '', title: '', quantity: 0 };
    var selected = addonCards.some(function (card) { return card.classList.contains('is-active'); });
    var vid = (primaryCard.getAttribute('data-addon-variant-id') || '').trim();
    var title = (primaryCard.getAttribute('data-addon-title') || 'Addon').trim();
    var qty = getAddonQty();
    return { selected: selected, variantId: vid, title: title, quantity: qty };
  }
  function wbGetSelectedVariantTitleForSummary() {
    var activeBtn = booking ? booking.querySelector('.wb-variant-btn.is-active') : null;
    if (activeBtn) {
      var t = (activeBtn.getAttribute('data-variant-title') || '').trim();
      if (t) return t;
    }
    var selectedId = String((variantSelect && variantSelect.value) || '').trim();
    if (selectedId && booking && booking.querySelector) {
      var fallbackBtn = booking.querySelector('.wb-variant-btn[data-variant-id="' + CSS.escape(selectedId) + '"]');
      if (fallbackBtn) {
        var ft = (fallbackBtn.getAttribute('data-variant-title') || '').trim();
        if (ft) return ft;
      }
    }
    return 'Workshop';
  }
  function wbUpdateSelectedItemsSummary() {
    if (!selectedItemsWrap || !selectedItemsList) return;
    var workshopQty = getWorkshopQtyValue();
    var variantTitle = wbGetSelectedVariantTitleForSummary();
    var addon = addonSelection();
    var chips = [];
    if (workshopQty > 0) {
      chips.push('<span class="wb-selected-items__chip"><span class="wb-selected-items__chip-qty">' + workshopQty + '</span> ' + variantTitle + '</span>');
    }
    if (addon && addon.selected && addon.quantity > 0) {
      chips.push('<span class="wb-selected-items__chip"><span class="wb-selected-items__chip-qty">' + addon.quantity + '</span> ' + (addon.title || 'Turkish Coffee') + '</span>');
    }
    if (!chips.length) {
      selectedItemsList.innerHTML = '';
      if (selectedItemsTotal) selectedItemsTotal.textContent = 'Total: —';
      selectedItemsWrap.classList.add('pdp-wb-hidden');
      return;
    }
    selectedItemsList.innerHTML = chips.join('');
    var activeVariantBtn = booking ? booking.querySelector('.wb-variant-btn.is-active') : null;
    var baseCents = activeVariantBtn && activeVariantBtn.dataset && activeVariantBtn.dataset.basePrice
      ? parseInt(activeVariantBtn.dataset.basePrice, 10)
      : null;
    if (!Number.isFinite(baseCents) || baseCents <= 0) {
      var fallbackBase = getSelectedVariantPriceCents();
      baseCents = (Number.isFinite(fallbackBase) && fallbackBase > 0) ? fallbackBase : 0;
    }
    var addonPriceRaw = addonCard ? (addonCard.getAttribute('data-addon-price') || '') : '';
    var addonPriceCents = 0;
    if (addonPriceRaw) {
      try {
        var cleaned = String(addonPriceRaw).replace(/[^0-9.,]/g, '').replace(/,/g, '');
        var parsed = Number(cleaned);
        if (Number.isFinite(parsed) && parsed >= 0) addonPriceCents = Math.round(parsed * 100);
      } catch (_) {}
    }
    var totalCents = Math.max(0, workshopQty) * Math.max(0, baseCents || 0);
    if (addon && addon.selected && addon.quantity > 0) {
      totalCents += Math.max(0, addon.quantity) * Math.max(0, addonPriceCents || 0);
    }
    if (selectedItemsTotal) {
      selectedItemsTotal.textContent = totalCents > 0 ? ('Total: ' + formatMoney(totalCents)) : 'Total: —';
    }
    selectedItemsWrap.classList.remove('pdp-wb-hidden');
  }
  function setAddonActive(next) {
    if (!addonCards || !addonCards.length) return;
    addonCards.forEach(function (card) {
      card.classList.toggle('is-active', !!next);
      card.setAttribute('aria-pressed', next ? 'true' : 'false');
    });
    if (typeof wbUpdateSelectedItemsSummary === 'function') wbUpdateSelectedItemsSummary();
  }
  setAddonQty(0);
  addonCards.forEach(function (card) {
    if (!card || card.dataset.wbBound) return;
    card.dataset.wbBound = '1';
    if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '0');
    if (!card.hasAttribute('role')) card.setAttribute('role', 'button');
    card.addEventListener('click', function (e) {
      if (e && e.target && e.target.closest && e.target.closest('[data-wb-addon-qty-control]')) return;
      if (e) { e.preventDefault(); e.stopPropagation(); }
      var next = !card.classList.contains('is-active');
      if (next) setAddonQty(getAddonQty());
      setAddonActive(next);
      var factsOnAddon = wstGetCurrentBookingFacts();
      if (factsOnAddon && factsOnAddon.venueName && factsOnAddon.dateLabel && factsOnAddon.timeLabel && factsOnAddon.variantName) {
        wstUpdate(
          6,
          wstValuesWithQty(
            factsOnAddon.venueName,
            factsOnAddon.dateLabel,
            factsOnAddon.timeLabel,
            factsOnAddon.variantName,
            factsOnAddon.qtyLabel || '',
            next ? (String(card.getAttribute('data-addon-title') || 'Extras').trim() || 'Extras') : 'None'
          )
        );
      }
      wbKeepStandaloneSheetExpanded();
    });
    card.addEventListener('keydown', function (e) {
      if (!e) return;
      var key = e.key || '';
      if (key !== 'Enter' && key !== ' ') return;
      e.preventDefault();
      card.click();
    });
  });
  addonQtyMinusBtns.forEach(function (btn) {
    if (!btn || btn.dataset.wbBound) return;
    btn.dataset.wbBound = '1';
    btn.addEventListener('click', function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      setAddonQty(getAddonQty() - 1);
      setAddonActive(true);
      wbKeepStandaloneSheetExpanded();
    });
  });
  addonQtyPlusBtns.forEach(function (btn) {
    if (!btn || btn.dataset.wbBound) return;
    btn.dataset.wbBound = '1';
    btn.addEventListener('click', function (e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      setAddonQty(getAddonQty() + 1);
      setAddonActive(true);
      wbKeepStandaloneSheetExpanded();
    });
  });
  addonQtyInputs.forEach(function (input) {
    if (!input || input.dataset.wbBound) return;
    input.dataset.wbBound = '1';
    input.addEventListener('click', function (e) {
      if (e) e.stopPropagation();
    });
    input.addEventListener('input', function (e) {
      if (e) e.stopPropagation();
      setAddonQty(input.value);
      setAddonActive(true);
    });
    input.addEventListener('change', function (e) {
      if (e) e.stopPropagation();
      setAddonQty(input.value);
      setAddonActive(true);
    });
  });
  if (booking && !booking.dataset.wbSelectedItemsBound) {
    booking.dataset.wbSelectedItemsBound = '1';
    booking.addEventListener('click', function (e) {
      if (!e || !e.target || !e.target.closest) return;
      if (e.target.closest('.wb-variant-btn')) {
        setTimeout(function () { wbUpdateSelectedItemsSummary(); }, 0);
      }
    });
  }
  if (variantSelect && !variantSelect.dataset.wbSelectedItemsBound) {
    variantSelect.dataset.wbSelectedItemsBound = '1';
    variantSelect.addEventListener('change', wbUpdateSelectedItemsSummary);
  }
  wbUpdateSelectedItemsSummary();

  function wbIsMobilePreviewMode() {
    if (!mobilePreviewEnabled) return false;
    return typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 1024px)').matches;
  }
  function wbIsDesktopPreviewMode() {
    if (!mobilePreviewEnabled) return false;
    return typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(min-width: 1025px)').matches;
  }

  function wbEnsureMobilePreviewEl() {
    var pop = document.getElementById('wb-mobile-preview');
    if (pop) return pop;
    pop = document.createElement('div');
    pop.id = 'wb-mobile-preview';
    pop.className = 'wb-mobile-preview';
    pop.innerHTML =
      '<div class="wb-mobile-preview__inner">' +
        '<div class="wb-mobile-preview__thumb"><img alt=""></div>' +
        '<div class="wb-mobile-preview__body">' +
          '<p class="wb-mobile-preview__title"></p>' +
          '<p class="wb-mobile-preview__subtitle"></p>' +
          '<p class="wb-mobile-preview__price"></p>' +
        '</div>' +
      '</div>';
    document.body.appendChild(pop);
    return pop;
  }

  function wbCloseMobilePreview() {
    var pop = document.getElementById('wb-mobile-preview');
    if (!pop) return;
    pop.classList.remove('is-open');
  }

  var wbPreviewImageCache = {};
  function wbPreloadPreviewImage(url) {
    var u = (url || '').trim();
    if (!u) return;
    if (wbPreviewImageCache[u] === 'loaded' || wbPreviewImageCache[u] === 'loading') return;
    wbPreviewImageCache[u] = 'loading';
    try {
      var im = new Image();
      im.decoding = 'async';
      im.onload = function () { wbPreviewImageCache[u] = 'loaded'; };
      im.onerror = function () { wbPreviewImageCache[u] = 'error'; };
      im.src = u;
    } catch (e) {
      wbPreviewImageCache[u] = 'error';
    }
  }

  function wbOpenMobilePreview(payload, anchorEl) {
    if (!mobilePreviewEnabled) return;
    var pop = wbEnsureMobilePreviewEl();
    if (!pop) return;
    var imgEl = pop.querySelector('.wb-mobile-preview__thumb img');
    var titleEl = pop.querySelector('.wb-mobile-preview__title');
    var subtitleEl = pop.querySelector('.wb-mobile-preview__subtitle');
    var priceEl = pop.querySelector('.wb-mobile-preview__price');
    if (titleEl) titleEl.textContent = payload.title || '';
    if (subtitleEl) {
      var sub = (payload.subtitle || '').trim();
      subtitleEl.textContent = sub;
      subtitleEl.style.display = sub ? '' : 'none';
    }
    if (priceEl) {
      if (payload.price) {
        priceEl.textContent = payload.price;
        priceEl.style.display = '';
      } else {
        priceEl.textContent = '';
        priceEl.style.display = 'none';
      }
    }
    if (imgEl) {
      if (payload.imageFast) {
        imgEl.src = payload.imageFast;
        imgEl.style.display = '';
      } else if (payload.image) {
        imgEl.src = payload.image;
        imgEl.style.display = '';
      } else {
        imgEl.src = 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%2296%22 height=%2296%22 viewBox=%220 0 96 96%22%3E%3Crect width=%2296%22 height=%2296%22 rx=%2212%22 fill=%22%23f1f3f5%22/%3E%3Cpath d=%22M30 60l12-14 8 10 10-12 16 16H20l10-12z%22 fill=%22%23d0d4d9%22/%3E%3Ccircle cx=%2240%22 cy=%2238%22 r=%226%22 fill=%22%23d0d4d9%22/%3E%3C/svg%3E';
        imgEl.style.display = '';
      }
      // Upgrade to high-res once ready (avoids 1-2s blank delay on first open).
      var hi = (payload.image || '').trim();
      var fast = (payload.imageFast || '').trim();
      if (hi && hi !== fast) {
        wbPreloadPreviewImage(hi);
        if (wbPreviewImageCache[hi] === 'loaded') {
          imgEl.src = hi;
        } else {
          var upgrade = new Image();
          upgrade.onload = function () {
            wbPreviewImageCache[hi] = 'loaded';
            var popNow = document.getElementById('wb-mobile-preview');
            if (!popNow || !popNow.classList.contains('is-open')) return;
            var live = popNow.querySelector('.wb-mobile-preview__thumb img');
            if (live) live.src = hi;
          };
          upgrade.onerror = function () { wbPreviewImageCache[hi] = 'error'; };
          upgrade.src = hi;
        }
      }
    }
    // Position preview in upper free area (not over Add to Cart / Book Now).
    try {
      var vw = window.innerWidth || document.documentElement.clientWidth || 0;
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var pad = 16;
      // Phone-only: smaller card (tablet 751px+ keeps 360 cap — matches theme workshop mobile breakpoint).
      var maxWCap = vw <= 750 ? 284 : 360;
      var maxW = Math.min(vw - pad * 2, maxWCap);
      pop.style.width = maxW + 'px';
      var left = pad;
      if (anchorEl && anchorEl.getBoundingClientRect) {
        var ar = anchorEl.getBoundingClientRect();
        left = Math.round(ar.left + (ar.width / 2) - (maxW / 2));
      }
      var minLeft = pad;
      var maxLeft = Math.max(pad, vw - maxW - pad);
      if (left < minLeft) left = minLeft;
      if (left > maxLeft) left = maxLeft;
      pop.style.left = left + 'px';

      // Pin preview in free space above the workshop sheet.
      // Use the real rendered popup height (not a fixed guess), otherwise
      // large preview cards open too low and cover variant tiles.
      var previewRect = pop.getBoundingClientRect ? pop.getBoundingClientRect() : null;
      var previewH = Math.round((previewRect && previewRect.height) || pop.offsetHeight || 0);
      if (!previewH || previewH < 120) previewH = 132;
      var targetTop = 84;
      try {
        var sheetEl = document.getElementById('wb-sheet');
        if (sheetEl && sheetEl.getBoundingClientRect) {
          var sh = sheetEl.getBoundingClientRect();
          if (sh && Number.isFinite(sh.top)) targetTop = Math.round(sh.top - previewH - mobilePreviewOffset - 12);
        }
      } catch (e2) {}
      var minTop = 40;
      var maxTop = Math.max(minTop, Math.round(vh * 0.42) - previewH);
      if (targetTop < minTop) targetTop = minTop;
      if (targetTop > maxTop) targetTop = maxTop;

      // Phone-only: pin magnify popup above variant tiles (tablet 751px+ keeps prior vertical rules).
      if (vw <= 750) {
        var phoneMinTop = 48;
        var tuckGap = 12;
        var tightTop = Math.max(phoneMinTop, Math.round(vh * 0.3) - previewH);
        targetTop = Math.min(targetTop, tightTop);
        var variantSlot =
          (booking && booking.querySelector && (
            booking.querySelector('[data-workshop-variant-wrap]') ||
            booking.querySelector('.wb-variant-cards')
          )) ||
          document.querySelector('.pdp-workshop-booking [data-workshop-variant-wrap]') ||
          document.querySelector('.pdp-workshop-booking .wb-variant-cards');
        try {
          if (variantSlot && variantSlot.getBoundingClientRect) {
            var vrr = variantSlot.getBoundingClientRect();
            var vTopRaw = Number(vrr.top);
            if (Number.isFinite(vTopRaw) && vrr.height > 2 && vTopRaw > phoneMinTop) {
              var aboveVariants = Math.round(vTopRaw - previewH - tuckGap);
              targetTop = Math.min(targetTop, aboveVariants);
            }
          }
        } catch (eVp) {}
        if (targetTop < phoneMinTop) targetTop = phoneMinTop;
      }

      pop.style.top = targetTop + 'px';
    } catch (e) {}
    pop.classList.add('is-open');
    window.__WB_LAST_PREVIEW_ANCHOR__ = anchorEl || null;
  }

  if (!window.__WB_MOBILE_PREVIEW_CLOSE_BOUND__) {
    window.__WB_MOBILE_PREVIEW_CLOSE_BOUND__ = true;
    document.addEventListener('pointerdown', function (e) {
      var pop = document.getElementById('wb-mobile-preview');
      if (!pop || !pop.classList.contains('is-open')) return;
      var anchor = window.__WB_LAST_PREVIEW_ANCHOR__;
      if (pop.contains(e.target)) return;
      if (anchor && anchor.contains && anchor.contains(e.target)) return;
      wbCloseMobilePreview();
    });
  }

  function wbBindMobilePreviewTarget(el, payloadBuilder) {
    if (!el || !payloadBuilder || el.dataset.wbPreviewBound === '1') return;
    el.dataset.wbPreviewBound = '1';
    var longPressTimer = null;
    var longPressOpened = false;
    var LONG_PRESS_MS = 420;
    function isStandaloneMobilePreviewContext() {
      return !!(
        booking &&
        booking.classList &&
        booking.classList.contains('wb-standalone') &&
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 750px)').matches
      );
    }
    function clearLongPress() {
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        longPressTimer = null;
      }
    }
    el.addEventListener('pointerdown', function (e) {
      if (isStandaloneMobilePreviewContext() && e) e.stopPropagation();
      if (!wbIsMobilePreviewMode()) return;
      longPressOpened = false;
      clearLongPress();
      longPressTimer = setTimeout(function () {
        longPressTimer = null;
        longPressOpened = true;
        wbOpenMobilePreview(payloadBuilder(el), el);
      }, LONG_PRESS_MS);
    });
    el.addEventListener('pointerup', clearLongPress);
    el.addEventListener('pointerleave', clearLongPress);
    el.addEventListener('pointercancel', clearLongPress);
    el.addEventListener('click', function (e) {
      if (isStandaloneMobilePreviewContext() && e) e.stopPropagation();
      if (!wbIsMobilePreviewMode()) return;
      if (longPressOpened) {
        longPressOpened = false;
        return;
      }
      wbOpenMobilePreview(payloadBuilder(el), el);
    });
    el.addEventListener('mouseenter', function () {
      if (!wbIsDesktopPreviewMode()) return;
      wbOpenMobilePreview(payloadBuilder(el), el);
    });
    el.addEventListener('mouseleave', function () {
      if (!wbIsDesktopPreviewMode()) return;
      wbCloseMobilePreview();
    });
  }

  booking.querySelectorAll('.wb-variant-btn').forEach(function (btn) {
    wbPreloadPreviewImage((btn.getAttribute('data-variant-preview-image') || '').trim());
    wbBindMobilePreviewTarget(btn, function (target) {
      var largePreview = (target.getAttribute('data-variant-preview-image') || '').trim();
      var img = target.querySelector('.wb-variant-img');
      var title = target.querySelector('.wb-variant-title');
      var price = target.querySelector('[data-variant-price]') || target.querySelector('.wb-variant-price');
      return {
        imageFast: img ? (img.currentSrc || img.src || img.getAttribute('src') || img.getAttribute('data-src') || '') : '',
        image: largePreview || (img ? (img.currentSrc || img.src || img.getAttribute('src') || img.getAttribute('data-src') || '') : ''),
        title: title ? title.textContent.trim() : 'Workshop variant',
        subtitle: '',
        price: price ? price.textContent.trim() : ''
      };
    });
  });
  addonCards.forEach(function (card) {
    if (!card) return;
    wbPreloadPreviewImage((card.getAttribute('data-addon-preview-image') || '').trim());
    wbBindMobilePreviewTarget(card, function (target) {
      var largePreview = (target.getAttribute('data-addon-preview-image') || '').trim();
      var img = target.querySelector('.wb-addon-turkish__thumb img');
      var title = target.querySelector('.wb-addon-turkish__title');
      var subtitle = target.querySelector('.wb-addon-turkish__sub');
      var addonPrice = (target.getAttribute('data-addon-price') || '').trim();
      return {
        imageFast: img ? (img.currentSrc || img.src || '') : '',
        image: largePreview || (img ? (img.currentSrc || img.src || '') : ''),
        title: title ? title.textContent.trim() : 'Turkish Coffee',
        subtitle: subtitle ? subtitle.textContent.trim() : 'Add-on',
        price: addonPrice
      };
    });
  });

  /**
   * JSON items format for cart/add.js (Shopify documented format).
   * Properties are reliably stored as line item properties -> Cart Transform attributes.
   */
  function buildCartAddPayload(variantId, quantity, properties, sections = [], extraItems = []) {
    var item = {
      id: parseInt(String(variantId), 10),
      quantity: Math.max(1, parseInt(String(quantity), 10) || 1),
      properties: {}
    };
    for (var k in (properties || {})) {
      if (properties[k] != null && properties[k] !== '') {
        item.properties[k] = String(properties[k]);
      }
    }
    var items = [item];
    if (Array.isArray(extraItems) && extraItems.length > 0) {
      extraItems.forEach(function (it) {
        if (!it || it.id == null) return;
        var addonId = parseInt(String(it.id), 10);
        if (!Number.isFinite(addonId) || addonId <= 0) return;
        items.push({
          id: addonId,
          quantity: Math.max(1, parseInt(String(it.quantity), 10) || 1),
          properties: it.properties && typeof it.properties === 'object' ? it.properties : {}
        });
      });
    }
    var payload = { items: items };
    if (Array.isArray(sections) && sections.length > 0) {
      payload.sections = sections.join(',');
      payload.sections_url = (typeof window !== 'undefined' && window.location && window.location.pathname) || '/';
    }
    return payload;
  }

  // Helper: Get total seats already in cart for a specific event from cart data.
  // Cart.js returns item.properties as object { key: value }; support that and array-of-pairs format.
  function getSeatsFromCart(eventId, cart) {
    if (!cart || !cart.items || !Array.isArray(cart.items)) return 0;
    const eventIdStr = String(eventId || '');
    let totalSeats = 0;

    function propsMatchEvent(props) {
      if (!props || typeof props !== 'object') return false;
      if (!Array.isArray(props)) {
        const gid = props._event_metaobject_gid;
        const eid = props._event_id ?? props.event_id;
        if (gid != null && String(gid) === eventIdStr) return true;
        if (eid != null && String(eid) === eventIdStr) return true;
        return false;
      }
      for (const entry of props) {
        if (!entry) continue;
        const k = entry[0] ?? entry.name;
        const v = entry[1] ?? entry.value;
        if ((k === '_event_metaobject_gid' || k === '_event_id' || k === 'event_id') && String(v) === eventIdStr) return true;
      }
      return false;
    }

    for (const item of cart.items) {
      if (propsMatchEvent(item.properties)) totalSeats += item.quantity || 0;
    }
    return totalSeats;
  }

  // Helper: Validate capacity before adding to cart
  // Simple toast notification function
  function showToast(message, type = 'error') {
    // Create toast element
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: ${type === 'error' ? '#f44336' : '#4caf50'};
      color: white;
      padding: 16px 24px;
      border-radius: 4px;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
      z-index: 10000;
      font-size: 14px;
      max-width: 400px;
      animation: slideIn 0.3s ease-out;
    `;
    toast.textContent = message;
    
    // Add animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from { transform: translateX(100%); opacity: 0; }
        to { transform: translateX(0); opacity: 1; }
      }
      @keyframes slideOut {
        from { transform: translateX(0); opacity: 1; }
        to { transform: translateX(100%); opacity: 0; }
      }
    `;
    if (!document.head.querySelector('#workshop-toast-styles')) {
      style.id = 'workshop-toast-styles';
      document.head.appendChild(style);
    }
    
    document.body.appendChild(toast);
    
    // Remove after 4 seconds
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        if (toast.parentNode) {
          toast.parentNode.removeChild(toast);
        }
      }, 300);
    }, 4000);
  }

  // Shared capacity message helper. Inputs: R, X (event-level seats in cart), Y (requested add qty).
  // Standard capacity messages: R<=0 sold-out; R>0 and over capacity = "You're trying to add more..." + Only R spot(s) remain + Reduce to R or fewer.
  function buildCapacityResult(remainingCapacity, seatsAlreadyInCart, requestedAddQty) {
    const R = remainingCapacity ?? 0;
    const X = seatsAlreadyInCart ?? 0;
    const Y = requestedAddQty ?? 0;
    const allowedAdditional = Math.max(0, R - X);
    const ok = R > 0 && Y <= allowedAdditional;
    let message = '';
    if (R <= 0) {
      message = 'This workshop is sold out. You already have seats for this workshop in your cart. Remove seats to continue.';
    } else if (Y > allowedAdditional) {
      message = `You're trying to add more seats than the remaining capacity. Only ${R} spot(s) remain. Reduce the total seats for this workshop in your cart to ${R} or fewer.`;
    }
    return { ok, message, inCart: X, requested: Y, remaining: R, allowed: allowedAdditional };
  }

  // Validates capacity. Fetches cart once if not provided; returns cartData for reuse in _workshop_prices update.
  async function validateCapacityBeforeAdd(eventId, requestedAddQty, cartData) {
    const root = (window.Shopify?.routes?.root || '/');
    let cart = cartData;
    if (!cart) {
      try {
        const res = await fetch(root + 'cart.js');
        cart = res.ok ? await res.json().catch(function() { return null; }) : null;
      } catch (e) {
        if (window.__WB_DIAG__) console.warn('Workshop: Failed to fetch cart', e);
        cart = null;
      }
    }

    let selectedCard = document.querySelector('.workshop-event-card.is-selected');
    if (!selectedCard && document.getElementById('wb-event-list')) {
      selectedCard =
        document.querySelector('#wb-event-list .wb-all__event-card.is-selected') ||
        document.querySelector('.wb-all__event-card.is-selected');
    }
    if (!selectedCard) {
      return { allowed: true, remainingCapacity: null, adjustedQuantity: requestedAddQty, message: '', cartData: cart };
    }
    const remainingCapacity = getRemainingCapacityFromCard(selectedCard);
    if (remainingCapacity == null && selectedCard.classList && selectedCard.classList.contains('wb-all__event-card')) {
      return { allowed: true, remainingCapacity: null, adjustedQuantity: requestedAddQty, message: '', cartData: cart };
    }
    const seatsAlreadyInCart = getSeatsFromCart(eventId, cart);
    const res = buildCapacityResult(remainingCapacity, seatsAlreadyInCart, requestedAddQty);

    if (res.remaining <= 0) {
      showToast(res.message);
      return { allowed: false, remainingCapacity: 0, adjustedQuantity: 0, abort: true, message: res.message, inCart: res.inCart, requested: res.requested, remaining: res.remaining, allowed: res.allowed, cartData: cart };
    }
    if (!res.ok) {
      showToast(res.message);
      return {
        allowed: false,
        remainingCapacity: res.remaining,
        adjustedQuantity: res.allowed <= 0 ? 0 : res.allowed,
        abort: res.allowed <= 0,
        message: res.message,
        inCart: res.inCart,
        requested: res.requested,
        remaining: res.remaining,
        allowed: res.allowed,
        cartData: cart
      };
    }
    return { allowed: true, remainingCapacity: res.remaining, adjustedQuantity: requestedAddQty, message: '', cartData: cart };
  }

  /**
   * Global __WORKSHOP_SELECTION__ can be cleared or out of sync (multi-init, filters, other scripts).
   * If user has a visible selected event card, rebuild selection from DOM so Add to Cart / Book Now still work.
   */
  function repairWorkshopSelectionFromDomIfNeeded() {
    var sel = window.__WORKSHOP_SELECTION__;
    var existing = sel && sel.eventId != null ? String(sel.eventId).trim() : '';
    if (existing) return;

    var card =
      document.querySelector('#wb-event-list .wb-all__event-card.is-selected') ||
      document.querySelector('.wb-all__event-card.is-selected') ||
      document.querySelector('.workshop-event-card.is-selected');
    if (!card) return;

    var eventId =
      (card.dataset.eventMetaobjectGid || card.dataset.eventId || (card.getAttribute && card.getAttribute('data-event-id')) || '')
        .toString()
        .trim();
    if (!eventId) return;

    window.__WORKSHOP_SELECTION__ = {
      eventId: eventId,
      eventHandle: ((card.dataset.eventHandle || (card.getAttribute && card.getAttribute('data-event-handle')) || '').toString()).trim(),
      city: ((card.dataset.city || (card.getAttribute && card.getAttribute('data-city')) || '').toString()).trim(),
      state: ((card.dataset.state || (card.getAttribute && card.getAttribute('data-state')) || '').toString()).trim(),
      venue: ((card.dataset.venueName || (card.getAttribute && card.getAttribute('data-venue-name')) || '').toString()).trim(),
      datetime: ((card.getAttribute && card.getAttribute('data-datetime')) || '').toString().trim()
    };
    if (selectedEventIdInput) selectedEventIdInput.value = eventId;
  }

  // Shared function to add workshop item to cart (plain-cart: add only, no merge/change/update)
  async function addWorkshopToCart(redirectToCheckout = false) {
    if (window.__WORKSHOP_ADDING_TO_CART__) return false;

    repairWorkshopSelectionFromDomIfNeeded();

    // Read selection from global state (possibly repaired from .is-selected card above)
    const selection = window.__WORKSHOP_SELECTION__;
    const eventIdStr = selection && selection.eventId != null ? String(selection.eventId).trim() : '';
    if (!selection || !eventIdStr) {
      alert('Please select an event before adding to cart.');
      return false;
    }

    const eventId = eventIdStr;
    const variantId = variantSelect ? variantSelect.value : '';
    if (!variantId) {
      alert('Please select a variant before adding to cart.');
      return false;
    }

    var eidMatch = String(eventId);
    let selectedCard = null;
    if (eventsRoot) {
      if (typeof CSS !== 'undefined' && CSS.escape) {
        try {
          selectedCard = eventsRoot.querySelector('.workshop-event-card[data-event-id="' + CSS.escape(eidMatch) + '"]');
        } catch (e) {}
      }
      if (!selectedCard) {
        var slotEls = eventsRoot.querySelectorAll('.workshop-event-card[data-event-id]');
        for (var si = 0; si < slotEls.length; si++) {
          if ((slotEls[si].getAttribute('data-event-id') || '').trim() === eidMatch) {
            selectedCard = slotEls[si];
            break;
          }
        }
      }
    }
    if (!selectedCard && document.getElementById('wb-event-list')) {
      var listWa = document.querySelectorAll('#wb-event-list .wb-all__event-card[data-event-id]');
      for (var wi = 0; wi < listWa.length; wi++) {
        if ((listWa[wi].getAttribute('data-event-id') || '').trim() === eidMatch) {
          selectedCard = listWa[wi];
          break;
        }
      }
    }
    if (!selectedCard && selection.eventHandle) {
      // Selection from workshop-events-ui (App Proxy flow); no card in eventsRoot
      selectedCard = null;
    }
    if (!selectedCard && !selection.eventHandle) {
      alert('Selected event not found. Please try again.');
      return false;
    }

    if (selectedCard && wbIsEventSoldOut(selectedCard)) {
      showSoldOutCapacityModal();
      return false;
    }
    if (selectedCard && !wbIsEventBookable(selectedCard)) {
      alert('This session is not available. Please choose another time.');
      return false;
    }

    // CRITICAL FIX: Resolve price FRESH on every add-to-cart to prevent price leakage
    // Do NOT read from dataset.resolvedPriceCents which may be stale
    const basePriceCents = getSelectedVariantPriceCents();
    if (!basePriceCents || Number.isNaN(basePriceCents)) {
      alert('Unable to determine base price. Please refresh and try again.');
      return false;
    }

    // Use selection state (from dataset attributes or workshop:event-selected detail)
    const state = selection.state || (selectedCard && selectedCard.dataset.state) || '';
    const city = selection.city || (selectedCard && selectedCard.dataset.city) || '';
    const venueName = selection.venue || (selectedCard && selectedCard.dataset.venueName) || '';

    // CRITICAL: Resolve price ONLY on Add to Cart (fresh resolution)
    const cityKey = (selectedCard && selectedCard.dataset.cityKey) ? String(selectedCard.dataset.cityKey).trim() : '';
    const venueHandle = (selectedCard && selectedCard.dataset.venueHandle) ? String(selectedCard.dataset.venueHandle).trim() : '';
    const resolvedCents = resolvePriceCents({
      basePriceCents: basePriceCents,
      eventId: eventId,
      state: state,
      city: city,
      cityKey: cityKey,
      venueName: venueName,
      venueHandle: venueHandle,
      variantId: variantId,
      workshopId: booking?.dataset?.productId,
    });

    if (!Number.isFinite(resolvedCents) || resolvedCents <= 0) {
      alert('Unable to determine price. Please refresh and try again.');
      return false;
    }

    // Get event handle from card or selection (must be metaobject handle, never numeric event_id)
    // CRITICAL: Block add-to-cart if handle is missing or numeric
    const datasetEventHandle = (selectedCard && selectedCard.dataset.eventHandle) || selection.eventHandle || '';
    const eventHandle = datasetEventHandle;
    const looksNumeric = /^\d+$/.test(eventHandle);
    
    if (!eventHandle || looksNumeric || eventHandle === String(eventId)) {
      const errorMsg = 'This workshop event is not available for booking. Please select a different event.';
      alert(errorMsg);
      if (window.__WB_DIAG__) {
        console.error('[Workshop] Add to cart blocked - invalid event_handle:', {
          eventHandle: eventHandle,
          eventId: eventId,
          looksNumeric: looksNumeric,
          reason: !eventHandle ? 'missing' : (looksNumeric ? 'numeric' : 'equals_event_id')
        });
      }
      return false;
    }

    if (window.__WB_DIAG__) {
      console.log('[Workshop] Add to cart - event handle validation:', {
        eventId: eventId,
        eventHandle: eventHandle,
        datasetEventHandle: datasetEventHandle
      });
    }

    // Build display properties (for cart/checkout UI) from selection state
    const displayCity = state ? `${city}, ${state}` : city;
    const displayVenue = venueName;
    const displayDatetime = selection.datetime || '';
    const bookingDetailsVisible = `${displayCity || ''}${displayVenue ? ` — ${displayVenue}` : ''}${displayDatetime ? ` · ${displayDatetime}` : ''}`.trim();

    // Get quantity from input (default 1)
    const quantityInput = bookingBox ? bookingBox.querySelector('[data-workshop-quantity]') : null;
    let quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value, 10) || 1) : 1;

    const capacityCheck = await validateCapacityBeforeAdd(eventId, quantity);
    
    // If capacity check failed and we should abort, return early
    if (capacityCheck.abort === true) {
      return false;
    }
    
    // If capacity check failed but we can adjust quantity, use adjusted quantity
    if (!capacityCheck.allowed && capacityCheck.adjustedQuantity > 0) {
      quantity = capacityCheck.adjustedQuantity;
      // Update quantity input to reflect adjusted value
      if (quantityInput) {
        quantityInput.value = quantity;
      }
    } else if (!capacityCheck.allowed) {
      // Should not happen if logic is correct, but fail-safe
      return false;
    }

    // Line item properties — Cart Transform only uses _workshop and _resolved_price*
    // _unique: merge by variantId:eventId; same event+variant = one line
    let capacityRemaining = null;
    const capRaw =
      selectedCard?.dataset.capacityRemaining ||
      selectedCard?.getAttribute('data-capacity-remaining') ||
      null;
    if (capRaw != null) {
      const n = Number(capRaw);
      if (Number.isFinite(n) && n >= 0) {
        capacityRemaining = String(n);
      }
    }

    const uniqueId = `${variantId}:${eventId}`;
    const centsStr = String(resolvedCents ?? '').trim();
    const validCents = /^[0-9]+$/.test(centsStr) && parseInt(centsStr, 10) > 0;
    // Line item properties: Shopify maps these to cart line attributes for Cart Transform
    const properties = {
      _workshop: "true",
      _event_id: eventId,
      _event_metaobject_gid: selectedCard && selectedCard.dataset.eventMetaobjectGid ? String(selectedCard.dataset.eventMetaobjectGid) : eventId,
      _event_handle: eventHandle,
      _display_city: displayCity,
      _display_venue: displayVenue,
      _display_datetime: displayDatetime,
      _unique: uniqueId,
      _is_workshop: "1",
      "Booking Details": bookingDetailsVisible
    };
    if (validCents) {
      properties._resolved_price_cents = centsStr;
      properties._resolved_price = centsStr;
    } else {
      if (window.__WB_DIAG__) console.warn('[Workshop] Missing/invalid resolved cents', { resolvedCents, variantId, eventId });
    }
    if (capacityRemaining !== null) {
      properties._capacity_remaining = capacityRemaining;
      properties._capacity_source = 'metaobject_dom';
    }

    // Hard guard: prevent duplicate add within 1s (same event + variant + quantity)
    var addLockAddon = addonSelection();
    var addLockAddonQty = (addLockAddon && addLockAddon.selected) ? Math.max(0, parseInt(String(addLockAddon.quantity || 0), 10) || 0) : 0;
    var addLockKey = eventId + '|' + variantId + '|' + quantity + '|' + addLockAddonQty;
    var lock = window.__WB_ADD_LOCK__;
    if (lock && (Date.now() - lock.ts) < 1000 && lock.key === addLockKey) {
      if (window.__WB_DIAG__) console.warn('[Workshop] Duplicate add blocked (lock)', { key: addLockKey });
      return false;
    }
    window.__WB_ADD_LOCK__ = { key: addLockKey, ts: Date.now() };

    window.__WORKSHOP_ADDING_TO_CART__ = true;
    let responseData;
    try {
      if (window.__WB_DIAG__) console.log('[Workshop] REQUEST START add to cart', { eventId: eventId, variantId: variantId, quantity: quantity });
      const sectionsToRender = window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper')
        ? window.Alpine.store('xCartHelper').getSectionsToRender().map((section) => section.id)
        : [];
      const root = (window.Shopify?.routes?.root || '/');
      var addonItems = [];
      var addon = addonSelection();
      if (addon.selected && addon.variantId && Number(addon.quantity) > 0) {
        var addonQty = Math.max(0, parseInt(String(addon.quantity || 0), 10) || 0);
        addonItems.push({
          id: addon.variantId,
          quantity: addonQty,
          properties: {
            _workshop_addon: 'true',
            _addon_for_event_id: eventId,
            _addon_for_variant_id: variantId,
            _addon_title: addon.title
          }
        });
      }
      const payload = buildCartAddPayload(variantId, quantity, properties, sectionsToRender, addonItems);
      const endpoint = root + 'cart/add.js';
      if (window.__WB_DIAG__) console.log('[Workshop Cart] Add payload:', JSON.stringify({ items: payload.items }));

      const addRes = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      responseData = await addRes.json().catch(() => ({}));
      if (!addRes.ok || responseData?.status === 422) {
        const desc = (responseData && responseData.description) ? String(responseData.description).toLowerCase() : '';
        const capacityRelated = /capacity|sold out|spots|seats|full|remaining|maximum|booked/.test(desc);
        const msg = capacityRelated && capacityCheck && capacityCheck.message
          ? capacityCheck.message
          : (responseData?.description || 'Unable to add to cart. Please try again.');
        alert(msg);
        return false;
      }

      if (validCents && capacityCheck.cartData) {
        try {
          var cartData = capacityCheck.cartData;
          var prices = {};
          try {
            if (cartData.attributes && cartData.attributes._workshop_prices) {
              prices = JSON.parse(cartData.attributes._workshop_prices);
            }
          } catch (_) {}
          prices[uniqueId] = centsStr;
          var keep = {};
          (cartData.items || []).forEach(function(it) {
            var u = it.properties && it.properties._unique;
            if (!u && it.properties && it.properties._event_metaobject_gid) u = (it.variant_id || it.id) + ':' + it.properties._event_metaobject_gid;
            if (u && prices[u] != null) keep[u] = prices[u];
          });
          if (keep[uniqueId] == null) keep[uniqueId] = centsStr;
          await fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _workshop_prices: JSON.stringify(keep) } })
          });
        } catch (attrErr) {
          if (window.__WB_DIAG__) console.warn('[Workshop] cart.attributes update failed', attrErr);
        }
      }

      if (responseData.sections && window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper')) {
        window.Alpine.store('xCartHelper').reRenderSections(responseData.sections);
      }
      if (window.Alpine && window.Alpine.store && window.Alpine.store('xCartHelper')) {
        window.Alpine.store('xCartHelper').currentItemCount = parseInt(document.querySelector('#cart-icon-bubble span')?.innerHTML || '0');
      }
      document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));

      if (typeof window.__WB_closeWorkshopSheet === 'function') {
        try {
          window.__WB_closeWorkshopSheet();
          wbCloseMobilePreview();
        } catch (closeErr) {}
      }

      if (redirectToCheckout) return true;

      // Workshop product mobile: after successful add-to-cart, close popup panel.
      wbCloseStandaloneMobilePanel();

      // Open theme cart drawer: dispatch ajaxProduct:added (theme.CartDrawer listens for this)
      document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: responseData } }));
      // Fallback: direct cart:open event
      document.dispatchEvent(new CustomEvent('cart:open'));
      // Some themes repaint after cart events; close once more to ensure sheet stays closed.
      setTimeout(wbCloseStandaloneMobilePanel, 60);
      // Alpine mini-cart fallback
      if (window.Alpine && window.Alpine.store && window.Alpine.store('xMiniCart')) {
        if (!window.Alpine.store('xCartNoti') || !window.Alpine.store('xCartNoti').enable) {
          window.Alpine.store('xMiniCart').openCart();
        }
      }
      return true;
    } catch (err) {
      if (window.__WB_DIAG__) console.error('Workshop booking error:', err);
      const desc = (err && err.message) ? String(err.message).toLowerCase() : '';
      const capacityRelated = /capacity|sold out|spots|seats|full|remaining|maximum|booked/.test(desc);
      const msg = capacityRelated && capacityCheck && capacityCheck.message
        ? capacityCheck.message
        : 'Unable to add to cart. Please try again.';
      alert(msg);
      return false;
    } finally {
      window.__WORKSHOP_ADDING_TO_CART__ = false;
      window.__WB_ADD_LOCK__ = null;
    }
  }

  function restoreButtonState(btn, text, clearStyles) {
    if (!btn) return;
    btn.disabled = false;
    btn.textContent = text;
    if (clearStyles) {
      btn.style.backgroundColor = '';
      btn.style.color = '';
      btn.style.cursor = '';
    }
  }

  // Book Now action: add to cart and redirect to checkout/cart (bind once per button)
  if (bookNowBtn && !bookNowBtn.dataset.wbBound) {
    bookNowBtn.dataset.wbBound = '1';
    bookNowBtn.addEventListener('click', async (e) => {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      var factsOnBookNow = wstGetCurrentBookingFacts();
      if (factsOnBookNow && factsOnBookNow.venueName && factsOnBookNow.dateLabel && factsOnBookNow.timeLabel && factsOnBookNow.variantName) {
        wstUpdate(
          7,
          wstValuesWithQty(
            factsOnBookNow.venueName,
            factsOnBookNow.dateLabel,
            factsOnBookNow.timeLabel,
            factsOnBookNow.variantName,
            factsOnBookNow.qtyLabel || '',
            factsOnBookNow.extrasName || 'None'
          )
        );
      }
      const originalText = bookNowBtn.textContent;
      bookNowBtn.disabled = true;
      bookNowBtn.textContent = 'Booking...';

      try {
        const success = await addWorkshopToCart(true);
        if (success) {
          window.location.href = '/cart';
          return;
        }
      } catch (_) {}
      restoreButtonState(bookNowBtn, originalText, true);
    });
  }

  if (addToCartBtn && !addToCartBtn.dataset.wbBound) {
    addToCartBtn.dataset.wbBound = '1';
    addToCartBtn.addEventListener('click', async (e) => {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      var factsOnAdd = wstGetCurrentBookingFacts();
      if (factsOnAdd && factsOnAdd.venueName && factsOnAdd.dateLabel && factsOnAdd.timeLabel && factsOnAdd.variantName) {
        wstUpdate(
          7,
          wstValuesWithQty(
            factsOnAdd.venueName,
            factsOnAdd.dateLabel,
            factsOnAdd.timeLabel,
            factsOnAdd.variantName,
            factsOnAdd.qtyLabel || '',
            factsOnAdd.extrasName || 'None'
          )
        );
      }
      const originalText = addToCartBtn.textContent;
      addToCartBtn.disabled = true;
      addToCartBtn.textContent = 'Adding...';

      try {
        const success = await addWorkshopToCart(false);
        if (success) {
          addToCartBtn.textContent = 'Added';
          setTimeout(() => restoreButtonState(addToCartBtn, originalText, true), 1500);
          return;
        }
      } catch (_) {}
      restoreButtonState(addToCartBtn, originalText, true);
    });
  }

  /**
   * "Book a workshop" list page: event cards live in #wb-event-list; the booking UI is cloned into #wb-sheet-body.
   * Mirror venue-card selection into the sheet (summary, capacity ring, variant, resolved price, CTA state).
   */
  function tryHydrateWbAllListSelection() {
    if (!booking || !booking.closest || !booking.closest('#wb-sheet-body')) return;
    var listCard =
      document.querySelector('#wb-event-list .wb-all__event-card.is-selected') ||
      document.querySelector('.wb-all__event-card.is-selected');
    if (!listCard) return;
    try {
      setEventSelectionFromCard(listCard);
    } catch (err) {
      if (DEV_MODE) console.warn('[WB] tryHydrateWbAllListSelection', err);
    }
  }

  window.__WB_onListCardUpdated = function (card) {
    try {
      if (!booking || !booking.closest || !booking.closest('#wb-sheet-body')) return;
      if (!card || !card.classList || !card.classList.contains('wb-all__event-card') || !card.classList.contains('is-selected')) return;
      setEventSelectionFromCard(card);
    } catch (e) {}
  };

  applyFilters();

  tryHydrateWbAllListSelection();
  if (booking.closest && booking.closest('#wb-sheet-body')) {
    if (useLocationFirst && typeof setWbState === 'function') {
      setWbState(2);
    }
    validateSeatSelection();
  }

  var placeEl = document.querySelector('#workshop-location-place-autocomplete,[data-workshop-place-autocomplete],gmp-place-autocomplete');
  if (placeEl) {
    ensurePlaceListeners(placeEl);
    startPlaceInputWatcher(placeEl);
  }
  ensureDocPlaceListeners();
}

document.addEventListener('DOMContentLoaded', initWorkshopBooking);
document.addEventListener('shopify:section:load', initWorkshopBooking);
document.addEventListener('shopify:section:reorder', initWorkshopBooking);
document.addEventListener('shopify:section:select', initWorkshopBooking);